/**
 * ETL Node Registry - Data pipeline nodes for extract, transform, load workflows
 * Follows the same structure as nodeRegistry.js but for ETL/data engineering use cases
 */

// Category colors — subdued palette, no pink, no purple
export const CATEGORY_COLORS = {
  templates: '#58a6ff',   // Blue - pre-built ETL templates
  sources: '#6cb6ff',     // Sky blue - data extraction sources
  transforms: '#39d2c0',  // Teal - core data transforms
  schema: '#db6d28',      // Orange - schema / column operations
  data_prep: '#768390',   // Slate - data cleaning & preparation
  stats: '#e0a4ff',       // Lavender - statistics & data science
  ml_prep: '#57ab5a',     // Sage - ML preprocessing
  targets: '#3fb950',     // Green - data load targets
  quality: '#d29922',     // Amber - data quality and validation
  utility: '#8b949e',     // Gray - utility and control nodes
}

// =============================================================================
// NODE DEFINITIONS
// =============================================================================

export const ETL_NODE_REGISTRY = {
  // ===========================================================================
  // TEMPLATES - Pre-built ETL workflow templates (expand into multiple nodes)
  // ===========================================================================
  'etl-template-db-to-db': {
    type: 'etl-template-db-to-db',
    category: 'templates',
    label: 'Database to Database Migration',
    description: 'Pre-built pipeline for migrating data between relational databases with column mapping and quality checks',
    icon: 'LayoutTemplate',
    color: CATEGORY_COLORS.templates,
    isTemplate: true,
    templateNodes: [
      'etl-source-database',
      'etl-transform-column-mapper',
      'etl-quality-check',
      'etl-target-database',
    ],
    inputs: [],
    outputs: [],
    configSchema: [],
    defaultConfig: {},
  },

  'etl-template-cobol-to-db': {
    type: 'etl-template-cobol-to-db',
    category: 'templates',
    label: 'COBOL to Database Migration',
    description: 'Pre-built pipeline for migrating COBOL copybook data into a relational database with expression transforms and quality validation',
    icon: 'LayoutTemplate',
    color: CATEGORY_COLORS.templates,
    isTemplate: true,
    templateNodes: [
      'etl-source-cobol-copybook',
      'etl-transform-expression',
      'etl-quality-check',
      'etl-target-database',
    ],
    inputs: [],
    outputs: [],
    configSchema: [],
    defaultConfig: {},
  },

  'etl-template-idms-to-db2': {
    type: 'etl-template-idms-to-db2',
    category: 'templates',
    label: 'IDMS to DB2 Migration',
    description: 'Pre-built pipeline for migrating IDMS network database records to DB2 relational tables with column mapping, expression transforms, and quality checks',
    icon: 'LayoutTemplate',
    color: CATEGORY_COLORS.templates,
    isTemplate: true,
    templateNodes: [
      'etl-source-idms',
      'etl-transform-column-mapper',
      'etl-transform-expression',
      'etl-quality-check',
      'etl-target-database',
    ],
    inputs: [],
    outputs: [],
    configSchema: [],
    defaultConfig: {},
  },

  'etl-template-s3-etl': {
    type: 'etl-template-s3-etl',
    category: 'templates',
    label: 'S3 Extract-Transform-Load',
    description: 'Pre-built pipeline for extracting data from S3, applying filter and aggregation transforms, and writing results back to S3',
    icon: 'LayoutTemplate',
    color: CATEGORY_COLORS.templates,
    isTemplate: true,
    templateNodes: [
      'etl-source-s3',
      'etl-transform-filter',
      'etl-transform-aggregate',
      'etl-target-s3',
    ],
    inputs: [],
    outputs: [],
    configSchema: [],
    defaultConfig: {},
  },

  // ===========================================================================
  // SOURCES - Data extraction from various systems
  // ===========================================================================
  'etl-source-database': {
    type: 'etl-source-database',
    category: 'sources',
    label: 'Database Source',
    description: 'Extract data from a relational database via JDBC. Supports PostgreSQL, MySQL, SQL Server, Oracle, DB2, and other JDBC-compatible databases.',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [
      { id: 'data', name: 'Data', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'connection_type', label: 'Database Type', type: 'select', options: [
        { value: 'postgresql', label: 'PostgreSQL' },
        { value: 'mysql', label: 'MySQL' },
        { value: 'sqlserver', label: 'SQL Server' },
        { value: 'oracle', label: 'Oracle' },
        { value: 'db2', label: 'DB2' },
        { value: 'other', label: 'Other (Custom JDBC)' },
      ], default: 'postgresql', section: 'Connection', description: 'The relational database engine to connect to' },
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', section: 'Connection', description: 'Database server hostname or IP address' },
      { key: 'port', label: 'Port', type: 'number', default: 5432, section: 'Connection', description: 'Database server port number' },
      { key: 'database', label: 'Database Name', type: 'text', default: '', section: 'Connection', description: 'Name of the database to connect to' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Connection', description: 'Database login username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Connection', description: 'Database login password' },
      { key: 'schema', label: 'Schema', type: 'text', default: 'public', section: 'Connection', description: 'Database schema (e.g., public, dbo)' },
      { key: 'table_or_query', label: 'Source Mode', type: 'select', options: [
        { value: 'table', label: 'Table' },
        { value: 'query', label: 'Custom Query' },
      ], default: 'table', section: 'Source', description: 'Read from a table directly or use a custom SQL query' },
      { key: 'table_name', label: 'Table Name', type: 'text', default: '', section: 'Source', description: 'Name of the table to read (when Source Mode is Table)' },
      { key: 'custom_query', label: 'Custom Query', type: 'textarea', default: '', section: 'Source', description: 'SQL SELECT query to execute (when Source Mode is Custom Query)' },
      { key: 'custom_jdbc_url', label: 'Custom JDBC URL', type: 'text', default: '', section: 'Connection', description: 'Full JDBC URL override (e.g., jdbc:postgresql://host:5432/db?ssl=true). When set, overrides host/port/database fields.' },
      { key: 'fetch_size', label: 'Fetch Size', type: 'number', default: 10000, section: 'Performance', description: 'Number of rows to fetch per JDBC round-trip. Higher values use more memory but reduce network calls.' },
      { key: 'partition_column', label: 'Partition Column', type: 'text', default: '', section: 'Parallel Reads', description: 'Numeric or date column to split reads across multiple Spark tasks. Enables parallel JDBC reads.' },
      { key: 'lower_bound', label: 'Lower Bound', type: 'text', default: '', section: 'Parallel Reads', description: 'Minimum value of the partition column (used for stride calculation, not filtering)' },
      { key: 'upper_bound', label: 'Upper Bound', type: 'text', default: '', section: 'Parallel Reads', description: 'Maximum value of the partition column (used for stride calculation, not filtering)' },
      { key: 'num_partitions', label: 'Number of Partitions', type: 'number', default: 1, section: 'Parallel Reads', description: 'Number of parallel JDBC connections. Each partition reads a range of the partition column.' },
      { key: 'pushdown_predicate', label: 'Predicate Pushdown', type: 'checkbox', default: true, section: 'Performance', description: 'Push WHERE filters down to the database for server-side filtering' },
      { key: 'session_init_statement', label: 'Session Init SQL', type: 'textarea', default: '', section: 'Advanced', description: 'SQL statement executed on each JDBC connection before reading (e.g., SET search_path TO myschema)' },
    ],
    defaultConfig: {
      connection_type: 'postgresql',
      host: 'localhost',
      port: 5432,
      database: '',
      username: '',
      password: '',
      schema: 'public',
      table_or_query: 'table',
      table_name: '',
      custom_query: '',
      custom_jdbc_url: '',
      fetch_size: 10000,
      partition_column: '',
      lower_bound: '',
      upper_bound: '',
      num_partitions: 1,
      pushdown_predicate: true,
      session_init_statement: '',
    },
  },

  'etl-source-s3': {
    type: 'etl-source-s3',
    category: 'sources',
    label: 'S3 Source',
    description: 'Extract data from files stored in Amazon S3. Supports CSV, JSON, Parquet, Avro, ORC, and XML formats with optional recursive directory scanning.',
    icon: 'Cloud',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [
      { id: 'data', name: 'Data', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'bucket', label: 'S3 Bucket', type: 'text', default: '', section: 'Location', description: 'S3 bucket name (without s3:// prefix)' },
      { key: 'prefix', label: 'Prefix / Path', type: 'text', default: '', section: 'Location', description: 'S3 key prefix to filter objects (e.g., data/input/2026/)' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'csv', label: 'CSV' },
        { value: 'json', label: 'JSON' },
        { value: 'parquet', label: 'Parquet' },
        { value: 'avro', label: 'Avro' },
        { value: 'orc', label: 'ORC' },
        { value: 'xml', label: 'XML' },
      ], default: 'csv', section: 'Format', description: 'File format of the source data' },
      { key: 'delimiter', label: 'Delimiter', type: 'text', default: ',', section: 'Format', description: 'Column delimiter for CSV files (e.g., comma, tab, pipe)' },
      { key: 'header', label: 'Has Header Row', type: 'checkbox', default: true, section: 'Format', description: 'Whether the first row contains column names (CSV)' },
      { key: 'infer_schema', label: 'Infer Schema', type: 'checkbox', default: true, section: 'Format', description: 'Automatically detect column types from data (CSV/JSON). If disabled, all columns are read as strings.' },
      { key: 'quote', label: 'Quote Character', type: 'text', default: '"', section: 'CSV Options', description: 'Character used to quote fields containing special characters (CSV)' },
      { key: 'escape', label: 'Escape Character', type: 'text', default: '\\', section: 'CSV Options', description: 'Character used to escape the quote character within quoted fields (CSV)' },
      { key: 'null_value', label: 'Null Value', type: 'text', default: '', section: 'CSV Options', description: 'String representation of null value (e.g., NULL, N/A, \\N). Empty = default null handling.' },
      { key: 'date_format', label: 'Date Format', type: 'text', default: 'yyyy-MM-dd', section: 'CSV Options', description: 'Java SimpleDateFormat pattern for parsing date columns (CSV/JSON)' },
      { key: 'timestamp_format', label: 'Timestamp Format', type: 'text', default: "yyyy-MM-dd'T'HH:mm:ss", section: 'CSV Options', description: 'Java SimpleDateFormat pattern for parsing timestamp columns (CSV/JSON)' },
      { key: 'multiline', label: 'Multiline Records', type: 'checkbox', default: false, section: 'Format', description: 'Enable multiline mode for JSON records or CSV fields spanning multiple lines' },
      { key: 'compression', label: 'Input Compression', type: 'select', options: [
        { value: 'none', label: 'None (Uncompressed)' },
        { value: 'auto', label: 'Auto-Detect' },
        { value: 'gzip', label: 'Gzip (.gz)' },
        { value: 'bzip2', label: 'Bzip2 (.bz2)' },
        { value: 'deflate', label: 'Deflate' },
        { value: 'lz4', label: 'LZ4' },
        { value: 'snappy', label: 'Snappy' },
      ], default: 'auto', section: 'Format', description: 'Compression codec of the source files' },
      { key: 'merge_schema', label: 'Merge Schema', type: 'checkbox', default: false, section: 'Format', description: 'Merge schemas from all files (Parquet/ORC). Handles schema evolution across files.' },
      { key: 'recursive', label: 'Recursive Scan', type: 'checkbox', default: false, section: 'Options', description: 'Recursively scan all subdirectories under the prefix' },
      { key: 'path_glob_filter', label: 'Path Glob Filter', type: 'text', default: '', section: 'Options', description: 'Glob pattern to filter files (e.g., *.csv, transactions_*.json)' },
      { key: 'aws_access_key', label: 'AWS Access Key ID', type: 'text', default: '', section: 'AWS Auth', description: 'AWS access key (leave empty to use IAM role, instance profile, or env vars)' },
      { key: 'aws_secret_key', label: 'AWS Secret Access Key', type: 'password', default: '', section: 'AWS Auth', description: 'AWS secret key' },
      { key: 'aws_session_token', label: 'Session Token', type: 'password', default: '', section: 'AWS Auth', description: 'AWS session token for temporary credentials (STS)' },
      { key: 'aws_region', label: 'AWS Region', type: 'text', default: 'us-east-1', section: 'AWS Auth', description: 'AWS region for the S3 bucket' },
    ],
    defaultConfig: {
      bucket: '',
      prefix: '',
      file_format: 'csv',
      delimiter: ',',
      header: true,
      infer_schema: true,
      quote: '"',
      escape: '\\',
      null_value: '',
      date_format: 'yyyy-MM-dd',
      timestamp_format: "yyyy-MM-dd'T'HH:mm:ss",
      multiline: false,
      compression: 'auto',
      merge_schema: false,
      recursive: false,
      path_glob_filter: '',
      aws_access_key: '',
      aws_secret_key: '',
      aws_session_token: '',
      aws_region: 'us-east-1',
    },
  },

  'etl-source-file': {
    type: 'etl-source-file',
    category: 'sources',
    label: 'File Source',
    description: 'Extract data from local filesystem or HDFS. Supports CSV, JSON, Parquet, Avro, ORC, XML, and plain text formats with configurable encoding.',
    icon: 'FileInput',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [
      { id: 'data', name: 'Data', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'file_path', label: 'File Path', type: 'text', default: '', section: 'Location', description: 'Absolute path to the file or directory (local or HDFS)' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'csv', label: 'CSV' },
        { value: 'json', label: 'JSON' },
        { value: 'parquet', label: 'Parquet' },
        { value: 'avro', label: 'Avro' },
        { value: 'orc', label: 'ORC' },
        { value: 'xml', label: 'XML' },
        { value: 'text', label: 'Plain Text' },
      ], default: 'csv', section: 'Format', description: 'File format of the source data' },
      { key: 'delimiter', label: 'Delimiter', type: 'text', default: ',', section: 'Format', description: 'Column delimiter for CSV files' },
      { key: 'header', label: 'Has Header Row', type: 'checkbox', default: true, section: 'Format', description: 'Whether the first row contains column names (CSV)' },
      { key: 'encoding', label: 'File Encoding', type: 'select', options: [
        { value: 'utf-8', label: 'UTF-8' },
        { value: 'latin-1', label: 'Latin-1 (ISO-8859-1)' },
        { value: 'ascii', label: 'ASCII' },
        { value: 'ebcdic', label: 'EBCDIC' },
      ], default: 'utf-8', section: 'Format', description: 'Character encoding of the source file' },
      { key: 'infer_schema', label: 'Infer Schema', type: 'checkbox', default: true, section: 'Format', description: 'Automatically detect column types (CSV/JSON). If disabled, all columns read as strings.' },
      { key: 'quote', label: 'Quote Character', type: 'text', default: '"', section: 'CSV Options', description: 'Character used to quote fields containing delimiters or newlines' },
      { key: 'escape', label: 'Escape Character', type: 'text', default: '\\', section: 'CSV Options', description: 'Character used to escape quote characters within quoted fields' },
      { key: 'null_value', label: 'Null Value', type: 'text', default: '', section: 'CSV Options', description: 'String representation of null (e.g., NULL, N/A). Empty = default null handling.' },
      { key: 'date_format', label: 'Date Format', type: 'text', default: 'yyyy-MM-dd', section: 'CSV Options', description: 'Java SimpleDateFormat pattern for date columns' },
      { key: 'timestamp_format', label: 'Timestamp Format', type: 'text', default: "yyyy-MM-dd'T'HH:mm:ss", section: 'CSV Options', description: 'Java SimpleDateFormat pattern for timestamp columns' },
      { key: 'multiline', label: 'Multiline Records', type: 'checkbox', default: false, section: 'Options', description: 'Enable multiline mode for JSON files where records span multiple lines' },
      { key: 'compression', label: 'Input Compression', type: 'select', options: [
        { value: 'none', label: 'None' }, { value: 'auto', label: 'Auto-Detect' },
        { value: 'gzip', label: 'Gzip' }, { value: 'bzip2', label: 'Bzip2' }, { value: 'snappy', label: 'Snappy' },
      ], default: 'auto', section: 'Options', description: 'Compression codec of the source file' },
      { key: 'merge_schema', label: 'Merge Schema', type: 'checkbox', default: false, section: 'Options', description: 'Merge schemas from multiple Parquet/ORC files (handles schema evolution)' },
      { key: 'recursive', label: 'Recursive Scan', type: 'checkbox', default: false, section: 'Options', description: 'Recursively read files from subdirectories' },
    ],
    defaultConfig: {
      file_path: '',
      file_format: 'csv',
      delimiter: ',',
      header: true,
      encoding: 'utf-8',
      infer_schema: true,
      quote: '"',
      escape: '\\',
      null_value: '',
      date_format: 'yyyy-MM-dd',
      timestamp_format: "yyyy-MM-dd'T'HH:mm:ss",
      multiline: false,
      compression: 'auto',
      merge_schema: false,
      recursive: false,
    },
  },

  'etl-source-cobol-copybook': {
    type: 'etl-source-cobol-copybook',
    category: 'sources',
    label: 'COBOL Copybook Source',
    description: 'Extract data from mainframe flat files using a COBOL copybook as the schema definition. Handles COMP-3 (packed decimal), REDEFINES, and OCCURS clauses.',
    icon: 'FileCode',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [
      { id: 'data', name: 'Data', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'copybook_path', label: 'Copybook Path', type: 'text', default: '', section: 'Files', description: 'Path to the COBOL copybook (.cpy) that defines the record layout' },
      { key: 'data_file_path', label: 'Data File Path', type: 'text', default: '', section: 'Files', description: 'Path to the flat data file to be parsed using the copybook layout' },
      { key: 'encoding', label: 'Data Encoding', type: 'select', options: [
        { value: 'ebcdic', label: 'EBCDIC (Mainframe)' },
        { value: 'ascii', label: 'ASCII' },
      ], default: 'ebcdic', section: 'Format', description: 'Character encoding of the data file. Mainframe files are typically EBCDIC.' },
      { key: 'record_format', label: 'Record Format', type: 'select', options: [
        { value: 'fixed', label: 'Fixed Length' },
        { value: 'variable', label: 'Variable Length (RDW)' },
      ], default: 'fixed', section: 'Format', description: 'Whether records are fixed-length or variable-length with Record Descriptor Word' },
      { key: 'record_length', label: 'Record Length', type: 'number', default: 0, section: 'Format', description: 'Fixed record length in bytes (0 = auto-detect from copybook)' },
      { key: 'handle_comp3', label: 'Handle COMP-3 (Packed Decimal)', type: 'checkbox', default: true, section: 'COBOL Features', description: 'Decode COMP-3 packed decimal fields into numeric values' },
      { key: 'handle_redefines', label: 'Handle REDEFINES', type: 'checkbox', default: true, section: 'COBOL Features', description: 'Process REDEFINES clauses that overlay fields on the same memory' },
      { key: 'handle_occurs', label: 'Handle OCCURS (Arrays)', type: 'checkbox', default: true, section: 'COBOL Features', description: 'Expand OCCURS clauses into repeated field groups' },
    ],
    defaultConfig: {
      copybook_path: '',
      data_file_path: '',
      encoding: 'ebcdic',
      record_format: 'fixed',
      record_length: 0,
      handle_comp3: true,
      handle_redefines: true,
      handle_occurs: true,
    },
  },

  'etl-source-idms': {
    type: 'etl-source-idms',
    category: 'sources',
    label: 'IDMS Source',
    description: 'Extract data from CA IDMS network database. Navigate sets, retrieve records by CALC key, and traverse owner/member relationships.',
    icon: 'Network',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [
      { id: 'data', name: 'Data', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'host', label: 'Host', type: 'text', default: '', section: 'Connection', description: 'IDMS Central Version hostname or IP address' },
      { key: 'port', label: 'Port', type: 'number', default: 3709, section: 'Connection', description: 'IDMS listener port number' },
      { key: 'subschema', label: 'Subschema', type: 'text', default: '', section: 'Schema', description: 'IDMS subschema name that defines the accessible record types and sets' },
      { key: 'schema_name', label: 'Schema Name', type: 'text', default: '', section: 'Schema', description: 'IDMS schema name (logical database definition)' },
      { key: 'record_types', label: 'Record Types', type: 'tags', default: [], section: 'Extraction', description: 'List of IDMS record type names to extract (e.g., EMPLOYEE, DEPARTMENT)' },
      { key: 'area_name', label: 'Area Name', type: 'text', default: '', section: 'Extraction', description: 'IDMS area (physical storage area) to scan for records' },
      { key: 'calc_key', label: 'CALC Key', type: 'text', default: '', section: 'Navigation', description: 'CALC key value for direct record access (leave empty for area sweep)' },
      { key: 'set_navigation', label: 'Set Navigation', type: 'select', options: [
        { value: 'owner', label: 'Owner (OBTAIN OWNER)' },
        { value: 'next', label: 'Next (OBTAIN NEXT WITHIN SET)' },
        { value: 'prior', label: 'Prior (OBTAIN PRIOR WITHIN SET)' },
      ], default: 'next', section: 'Navigation', description: 'How to traverse IDMS set relationships when walking the network' },
      { key: 'max_records', label: 'Max Records', type: 'number', default: 0, section: 'Limits', description: 'Maximum number of records to extract (0 = unlimited)' },
    ],
    defaultConfig: {
      host: '',
      port: 3709,
      subschema: '',
      schema_name: '',
      record_types: [],
      area_name: '',
      calc_key: '',
      set_navigation: 'next',
      max_records: 0,
    },
  },

  'etl-source-vsam': {
    type: 'etl-source-vsam',
    category: 'sources',
    label: 'VSAM Source',
    description: 'Extract data from VSAM datasets (KSDS, ESDS, RRDS). Uses a COBOL copybook to interpret the record layout.',
    icon: 'HardDrive',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [
      { id: 'data', name: 'Data', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'dataset_name', label: 'Dataset Name', type: 'text', default: '', section: 'Source', description: 'Fully qualified VSAM dataset name (e.g., PROD.ACCT.MASTER)' },
      { key: 'file_type', label: 'VSAM Type', type: 'select', options: [
        { value: 'ksds', label: 'KSDS (Key-Sequenced)' },
        { value: 'esds', label: 'ESDS (Entry-Sequenced)' },
        { value: 'rrds', label: 'RRDS (Relative Record)' },
      ], default: 'ksds', section: 'Source', description: 'VSAM dataset organization type' },
      { key: 'key_offset', label: 'Key Offset', type: 'number', default: 0, section: 'Key', description: 'Byte offset of the primary key within the record (KSDS only)' },
      { key: 'key_length', label: 'Key Length', type: 'number', default: 0, section: 'Key', description: 'Length of the primary key in bytes (KSDS only)' },
      { key: 'record_length', label: 'Record Length', type: 'number', default: 0, section: 'Format', description: 'Fixed record length in bytes (0 = variable length)' },
      { key: 'copybook_path', label: 'Copybook Path', type: 'text', default: '', section: 'Format', description: 'Path to the COBOL copybook that defines the VSAM record layout' },
      { key: 'encoding', label: 'Data Encoding', type: 'select', options: [
        { value: 'ebcdic', label: 'EBCDIC (Mainframe)' },
        { value: 'ascii', label: 'ASCII' },
      ], default: 'ebcdic', section: 'Format', description: 'Character encoding of the VSAM data' },
    ],
    defaultConfig: {
      dataset_name: '',
      file_type: 'ksds',
      key_offset: 0,
      key_length: 0,
      record_length: 0,
      copybook_path: '',
      encoding: 'ebcdic',
    },
  },

  'etl-source-kafka': {
    type: 'etl-source-kafka',
    category: 'sources',
    label: 'Kafka Source',
    description: 'Consume messages from an Apache Kafka topic. Supports JSON, Avro, Protobuf, and plain string deserialization with optional Schema Registry integration.',
    icon: 'Radio',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [
      { id: 'data', name: 'Data', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'bootstrap_servers', label: 'Bootstrap Servers', type: 'text', default: 'localhost:9092', section: 'Connection', description: 'Comma-separated list of Kafka broker addresses (host:port)' },
      { key: 'topic', label: 'Topic', type: 'text', default: '', section: 'Source', description: 'Kafka topic name to consume from' },
      { key: 'group_id', label: 'Consumer Group ID', type: 'text', default: '', section: 'Source', description: 'Kafka consumer group ID for offset tracking' },
      { key: 'starting_offsets', label: 'Starting Offsets', type: 'select', options: [
        { value: 'earliest', label: 'Earliest (Beginning)' },
        { value: 'latest', label: 'Latest (New Messages Only)' },
        { value: 'specific', label: 'Specific Offset' },
      ], default: 'earliest', section: 'Source', description: 'Where to start reading from in the topic' },
      { key: 'value_format', label: 'Value Format', type: 'select', options: [
        { value: 'json', label: 'JSON' },
        { value: 'avro', label: 'Avro' },
        { value: 'protobuf', label: 'Protobuf' },
        { value: 'string', label: 'Plain String' },
      ], default: 'json', section: 'Format', description: 'Deserialization format for message values' },
      { key: 'schema_registry_url', label: 'Schema Registry URL', type: 'text', default: '', section: 'Format', description: 'Confluent Schema Registry URL (required for Avro/Protobuf)' },
      { key: 'key_format', label: 'Key Format', type: 'select', options: [
        { value: 'string', label: 'String' }, { value: 'json', label: 'JSON' }, { value: 'long', label: 'Long' }, { value: 'none', label: 'Ignore Key' },
      ], default: 'string', section: 'Format', description: 'Deserialization format for message keys' },
      { key: 'include_headers', label: 'Include Headers', type: 'checkbox', default: false, section: 'Options', description: 'Include Kafka message headers as a column in the DataFrame' },
      { key: 'include_metadata', label: 'Include Metadata', type: 'checkbox', default: false, section: 'Options', description: 'Include topic, partition, offset, and timestamp as columns' },
      { key: 'max_offsets_per_trigger', label: 'Max Offsets Per Trigger', type: 'number', default: 0, section: 'Streaming', description: 'Rate limit: max records per micro-batch (0 = unlimited). Only for Structured Streaming.' },
      { key: 'fail_on_data_loss', label: 'Fail On Data Loss', type: 'checkbox', default: false, section: 'Streaming', description: 'Fail the query when data may be lost (e.g., topic deleted, offsets out of range)' },
      { key: 'security_protocol', label: 'Security Protocol', type: 'select', options: [
        { value: 'PLAINTEXT', label: 'PLAINTEXT (No Auth)' },
        { value: 'SSL', label: 'SSL' },
        { value: 'SASL_PLAINTEXT', label: 'SASL_PLAINTEXT' },
        { value: 'SASL_SSL', label: 'SASL_SSL' },
      ], default: 'PLAINTEXT', section: 'Security', description: 'Kafka security protocol' },
      { key: 'sasl_mechanism', label: 'SASL Mechanism', type: 'select', options: [
        { value: 'PLAIN', label: 'PLAIN' }, { value: 'SCRAM-SHA-256', label: 'SCRAM-SHA-256' },
        { value: 'SCRAM-SHA-512', label: 'SCRAM-SHA-512' }, { value: 'GSSAPI', label: 'GSSAPI (Kerberos)' },
      ], default: 'PLAIN', section: 'Security', description: 'SASL authentication mechanism (when using SASL protocol)' },
      { key: 'sasl_username', label: 'SASL Username', type: 'text', default: '', section: 'Security', description: 'SASL username (PLAIN/SCRAM)' },
      { key: 'sasl_password', label: 'SASL Password', type: 'password', default: '', section: 'Security', description: 'SASL password (PLAIN/SCRAM)' },
      { key: 'ssl_truststore_location', label: 'SSL Truststore Path', type: 'text', default: '', section: 'Security', description: 'Path to SSL truststore file (JKS format)' },
      { key: 'ssl_truststore_password', label: 'SSL Truststore Password', type: 'password', default: '', section: 'Security', description: 'Truststore password' },
    ],
    defaultConfig: {
      bootstrap_servers: 'localhost:9092',
      topic: '',
      group_id: '',
      starting_offsets: 'earliest',
      value_format: 'json',
      schema_registry_url: '',
      key_format: 'string',
      include_headers: false,
      include_metadata: false,
      max_offsets_per_trigger: 0,
      fail_on_data_loss: false,
      security_protocol: 'PLAINTEXT',
      sasl_mechanism: 'PLAIN',
      sasl_username: '',
      sasl_password: '',
      ssl_truststore_location: '',
      ssl_truststore_password: '',
    },
  },

  // --- Cloud Storage Sources ---

  'etl-source-azure-blob': {
    type: 'etl-source-azure-blob',
    category: 'sources',
    label: 'Azure Blob / ADLS',
    description: 'Read data from Azure Blob Storage or Azure Data Lake Storage Gen2. Supports CSV, JSON, Parquet, Avro, ORC, and Delta Lake formats.',
    icon: 'Cloud',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'storage_account', label: 'Storage Account', type: 'text', default: '', section: 'Connection', description: 'Azure storage account name' },
      { key: 'container', label: 'Container', type: 'text', default: '', section: 'Connection', description: 'Blob container or ADLS filesystem name' },
      { key: 'path', label: 'Path / Prefix', type: 'text', default: '', section: 'Connection', description: 'Path to file or folder within container' },
      { key: 'auth_method', label: 'Authentication', type: 'select', options: [
        { value: 'access_key', label: 'Access Key' },
        { value: 'sas_token', label: 'SAS Token' },
        { value: 'service_principal', label: 'Service Principal' },
        { value: 'managed_identity', label: 'Managed Identity' },
      ], default: 'access_key', section: 'Connection', description: 'Authentication method for Azure storage' },
      { key: 'access_key', label: 'Access Key / SAS Token', type: 'password', default: '', section: 'Connection', description: 'Storage access key or SAS token (depending on auth method)' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'csv', label: 'CSV' }, { value: 'json', label: 'JSON' }, { value: 'parquet', label: 'Parquet' },
        { value: 'avro', label: 'Avro' }, { value: 'orc', label: 'ORC' }, { value: 'delta', label: 'Delta Lake' },
      ], default: 'parquet', section: 'Format', description: 'Format of the source files' },
      { key: 'recursive', label: 'Recursive', type: 'checkbox', default: true, section: 'Options', description: 'Read files recursively from subdirectories' },
    ],
    defaultConfig: { storage_account: '', container: '', path: '', auth_method: 'access_key', access_key: '', file_format: 'parquet', recursive: true },
  },

  'etl-source-gcs': {
    type: 'etl-source-gcs',
    category: 'sources',
    label: 'Google Cloud Storage',
    description: 'Read data from Google Cloud Storage buckets. Supports CSV, JSON, Parquet, Avro, and ORC formats.',
    icon: 'Cloud',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'bucket', label: 'Bucket', type: 'text', default: '', section: 'Connection', description: 'GCS bucket name' },
      { key: 'path', label: 'Path / Prefix', type: 'text', default: '', section: 'Connection', description: 'Object path or prefix within the bucket' },
      { key: 'project_id', label: 'Project ID', type: 'text', default: '', section: 'Connection', description: 'Google Cloud project ID' },
      { key: 'credentials_json', label: 'Service Account Key (JSON)', type: 'textarea', default: '', section: 'Connection', description: 'Service account credentials JSON (leave empty for default credentials)' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'csv', label: 'CSV' }, { value: 'json', label: 'JSON' }, { value: 'parquet', label: 'Parquet' },
        { value: 'avro', label: 'Avro' }, { value: 'orc', label: 'ORC' },
      ], default: 'parquet', section: 'Format', description: 'Format of the source files' },
      { key: 'recursive', label: 'Recursive', type: 'checkbox', default: true, section: 'Options', description: 'Read files recursively from subdirectories' },
    ],
    defaultConfig: { bucket: '', path: '', project_id: '', credentials_json: '', file_format: 'parquet', recursive: true },
  },

  'etl-source-hdfs': {
    type: 'etl-source-hdfs',
    category: 'sources',
    label: 'HDFS',
    description: 'Read data from Hadoop Distributed File System. Supports all Spark file formats including CSV, JSON, Parquet, Avro, ORC, and text.',
    icon: 'HardDrive',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'namenode_url', label: 'NameNode URL', type: 'text', default: 'hdfs://localhost:9000', section: 'Connection', description: 'HDFS NameNode URL (e.g., hdfs://namenode:9000)' },
      { key: 'path', label: 'Path', type: 'text', default: '', section: 'Connection', description: 'HDFS path to file or directory' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'csv', label: 'CSV' }, { value: 'json', label: 'JSON' }, { value: 'parquet', label: 'Parquet' },
        { value: 'avro', label: 'Avro' }, { value: 'orc', label: 'ORC' }, { value: 'text', label: 'Text' },
      ], default: 'parquet', section: 'Format', description: 'Format of the source files' },
      { key: 'delimiter', label: 'Delimiter', type: 'text', default: ',', section: 'Format', description: 'Field delimiter (CSV only)' },
      { key: 'header', label: 'Header Row', type: 'checkbox', default: true, section: 'Format', description: 'First row contains column names (CSV only)' },
    ],
    defaultConfig: { namenode_url: 'hdfs://localhost:9000', path: '', file_format: 'parquet', delimiter: ',', header: true },
  },

  // --- NoSQL / Document Sources ---

  'etl-source-mongodb': {
    type: 'etl-source-mongodb',
    category: 'sources',
    label: 'MongoDB',
    description: 'Read data from MongoDB collections using the official Spark MongoDB connector. Supports aggregation pipeline pushdown and schema inference.',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'connection_uri', label: 'Connection URI', type: 'text', default: 'mongodb://localhost:27017', section: 'Connection', description: 'MongoDB connection string (e.g., mongodb://host:27017)' },
      { key: 'database', label: 'Database', type: 'text', default: '', section: 'Connection', description: 'MongoDB database name' },
      { key: 'collection', label: 'Collection', type: 'text', default: '', section: 'Connection', description: 'MongoDB collection name' },
      { key: 'pipeline', label: 'Aggregation Pipeline', type: 'textarea', default: '[]', section: 'Query', description: 'MongoDB aggregation pipeline as JSON array (e.g., [{\"$match\": {\"status\": \"active\"}}]). Leave as [] to read all documents.' },
      { key: 'schema_inference', label: 'Schema Inference', type: 'select', options: [
        { value: 'auto', label: 'Automatic (Sample Documents)' },
        { value: 'full', label: 'Full Scan (Slower, More Accurate)' },
        { value: 'manual', label: 'Manual (Provide Schema)' },
      ], default: 'auto', section: 'Schema', description: 'How to determine the schema of the collection' },
    ],
    defaultConfig: { connection_uri: 'mongodb://localhost:27017', database: '', collection: '', pipeline: '[]', schema_inference: 'auto' },
  },

  'etl-source-cassandra': {
    type: 'etl-source-cassandra',
    category: 'sources',
    label: 'Cassandra',
    description: 'Read data from Apache Cassandra tables using the Spark Cassandra connector. Supports predicate pushdown and partition-aware reads.',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'host', label: 'Contact Points', type: 'text', default: 'localhost', section: 'Connection', description: 'Comma-separated Cassandra contact points (hosts)' },
      { key: 'port', label: 'Port', type: 'number', default: 9042, section: 'Connection', description: 'Cassandra native transport port' },
      { key: 'keyspace', label: 'Keyspace', type: 'text', default: '', section: 'Connection', description: 'Cassandra keyspace name' },
      { key: 'table', label: 'Table', type: 'text', default: '', section: 'Connection', description: 'Cassandra table name' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Auth', description: 'Cassandra username (leave empty for no auth)' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'Cassandra password' },
      { key: 'consistency_level', label: 'Consistency Level', type: 'select', options: [
        { value: 'LOCAL_ONE', label: 'LOCAL_ONE' }, { value: 'LOCAL_QUORUM', label: 'LOCAL_QUORUM' },
        { value: 'ONE', label: 'ONE' }, { value: 'QUORUM', label: 'QUORUM' }, { value: 'ALL', label: 'ALL' },
      ], default: 'LOCAL_ONE', section: 'Options', description: 'Read consistency level' },
    ],
    defaultConfig: { host: 'localhost', port: 9042, keyspace: '', table: '', username: '', password: '', consistency_level: 'LOCAL_ONE' },
  },

  'etl-source-dynamodb': {
    type: 'etl-source-dynamodb',
    category: 'sources',
    label: 'DynamoDB',
    description: 'Read data from Amazon DynamoDB tables. Supports parallel scans and filter expression pushdown.',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'table_name', label: 'Table Name', type: 'text', default: '', section: 'Connection', description: 'DynamoDB table name' },
      { key: 'region', label: 'AWS Region', type: 'text', default: 'us-east-1', section: 'Connection', description: 'AWS region where the table resides' },
      { key: 'read_throughput', label: 'Read Throughput', type: 'text', default: '1', section: 'Performance', description: 'Target fraction of provisioned read throughput to consume (0.1 to 1.0)' },
      { key: 'filter_expression', label: 'Filter Expression', type: 'textarea', default: '', section: 'Query', description: 'DynamoDB filter expression (e.g., status = :val). Reduces data transferred.' },
      { key: 'splits', label: 'Parallel Scans', type: 'number', default: 0, section: 'Performance', description: 'Number of parallel scan segments (0 = auto based on table size)' },
    ],
    defaultConfig: { table_name: '', region: 'us-east-1', read_throughput: '1', filter_expression: '', splits: 0 },
  },

  'etl-source-elasticsearch': {
    type: 'etl-source-elasticsearch',
    category: 'sources',
    label: 'Elasticsearch',
    description: 'Read data from Elasticsearch indices using the Elasticsearch-Hadoop connector. Supports query pushdown.',
    icon: 'Search',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'nodes', label: 'Nodes', type: 'text', default: 'localhost', section: 'Connection', description: 'Comma-separated Elasticsearch node addresses' },
      { key: 'port', label: 'Port', type: 'number', default: 9200, section: 'Connection', description: 'Elasticsearch HTTP port' },
      { key: 'index', label: 'Index', type: 'text', default: '', section: 'Connection', description: 'Elasticsearch index name or pattern (e.g., logs-2024-*)' },
      { key: 'query', label: 'Query DSL', type: 'textarea', default: '{"match_all": {}}', section: 'Query', description: 'Elasticsearch query DSL as JSON' },
      { key: 'scroll_size', label: 'Scroll Size', type: 'number', default: 1000, section: 'Performance', description: 'Number of documents per scroll batch' },
      { key: 'ssl_enabled', label: 'SSL Enabled', type: 'checkbox', default: false, section: 'Security', description: 'Enable SSL/TLS for connections' },
    ],
    defaultConfig: { nodes: 'localhost', port: 9200, index: '', query: '{"match_all": {}}', scroll_size: 1000, ssl_enabled: false },
  },

  'etl-source-redis': {
    type: 'etl-source-redis',
    category: 'sources',
    label: 'Redis',
    description: 'Read data from Redis using the Spark Redis connector. Supports hashes, strings, lists, sets, sorted sets, and streams.',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', section: 'Connection', description: 'Redis server host' },
      { key: 'port', label: 'Port', type: 'number', default: 6379, section: 'Connection', description: 'Redis server port' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Connection', description: 'Redis password (leave empty for no auth)' },
      { key: 'db_number', label: 'Database Number', type: 'number', default: 0, section: 'Connection', description: 'Redis database number (0-15)' },
      { key: 'data_type', label: 'Data Type', type: 'select', options: [
        { value: 'hash', label: 'Hash' }, { value: 'string', label: 'String' }, { value: 'list', label: 'List' },
        { value: 'set', label: 'Set' }, { value: 'zset', label: 'Sorted Set' }, { value: 'stream', label: 'Stream' },
      ], default: 'hash', section: 'Data', description: 'Redis data type to read' },
      { key: 'key_pattern', label: 'Key Pattern', type: 'text', default: '*', section: 'Data', description: 'Key pattern to match (glob-style, e.g., user:*)' },
    ],
    defaultConfig: { host: 'localhost', port: 6379, password: '', db_number: 0, data_type: 'hash', key_pattern: '*' },
  },

  'etl-source-hbase': {
    type: 'etl-source-hbase',
    category: 'sources',
    label: 'HBase',
    description: 'Read data from Apache HBase tables using the Spark HBase connector. Supports row key range scans and column family selection.',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'zookeeper_quorum', label: 'ZooKeeper Quorum', type: 'text', default: 'localhost', section: 'Connection', description: 'Comma-separated ZooKeeper hosts' },
      { key: 'zookeeper_port', label: 'ZooKeeper Port', type: 'number', default: 2181, section: 'Connection', description: 'ZooKeeper client port' },
      { key: 'table', label: 'Table', type: 'text', default: '', section: 'Connection', description: 'HBase table name (including namespace, e.g., ns:table)' },
      { key: 'column_families', label: 'Column Families', type: 'tags', default: [], section: 'Data', description: 'Column families to read (empty = all)' },
      { key: 'start_row', label: 'Start Row Key', type: 'text', default: '', section: 'Scan', description: 'Start row key for range scan (inclusive, leave empty for full scan)' },
      { key: 'end_row', label: 'End Row Key', type: 'text', default: '', section: 'Scan', description: 'End row key for range scan (exclusive)' },
    ],
    defaultConfig: { zookeeper_quorum: 'localhost', zookeeper_port: 2181, table: '', column_families: [], start_row: '', end_row: '' },
  },

  // --- Data Warehouse Sources ---

  'etl-source-snowflake': {
    type: 'etl-source-snowflake',
    category: 'sources',
    label: 'Snowflake',
    description: 'Read data from Snowflake using the Spark Snowflake connector. Supports query pushdown for filters, projections, and aggregations.',
    icon: 'Snowflake',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'account', label: 'Account', type: 'text', default: '', section: 'Connection', description: 'Snowflake account identifier (e.g., xy12345.us-east-1)' },
      { key: 'warehouse', label: 'Warehouse', type: 'text', default: '', section: 'Connection', description: 'Snowflake virtual warehouse name' },
      { key: 'database', label: 'Database', type: 'text', default: '', section: 'Connection', description: 'Snowflake database name' },
      { key: 'schema', label: 'Schema', type: 'text', default: 'PUBLIC', section: 'Connection', description: 'Snowflake schema name' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Auth', description: 'Snowflake username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'Snowflake password' },
      { key: 'source_type', label: 'Source Type', type: 'select', options: [
        { value: 'table', label: 'Table' }, { value: 'query', label: 'Custom Query' },
      ], default: 'table', section: 'Data', description: 'Read from a table or a custom SQL query' },
      { key: 'table_name', label: 'Table Name', type: 'text', default: '', section: 'Data', description: 'Table name (when Source Type is Table)' },
      { key: 'query', label: 'SQL Query', type: 'textarea', default: '', section: 'Data', description: 'Custom SQL query (when Source Type is Query)' },
    ],
    defaultConfig: { account: '', warehouse: '', database: '', schema: 'PUBLIC', username: '', password: '', source_type: 'table', table_name: '', query: '' },
  },

  'etl-source-redshift': {
    type: 'etl-source-redshift',
    category: 'sources',
    label: 'Redshift',
    description: 'Read data from Amazon Redshift using the Spark Redshift connector. Uses S3 as an intermediary for data transfer.',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'jdbc_url', label: 'JDBC URL', type: 'text', default: '', section: 'Connection', description: 'Redshift JDBC connection URL (jdbc:redshift://host:5439/database)' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Auth', description: 'Redshift username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'Redshift password' },
      { key: 'temp_dir', label: 'S3 Temp Directory', type: 'text', default: '', section: 'Connection', description: 'S3 path for temporary data during UNLOAD (e.g., s3://bucket/temp/)' },
      { key: 'source_type', label: 'Source Type', type: 'select', options: [
        { value: 'table', label: 'Table' }, { value: 'query', label: 'Custom Query' },
      ], default: 'table', section: 'Data', description: 'Read from a table or a custom SQL query' },
      { key: 'table_name', label: 'Table Name', type: 'text', default: '', section: 'Data', description: 'Redshift table name' },
      { key: 'query', label: 'SQL Query', type: 'textarea', default: '', section: 'Data', description: 'Custom SQL query' },
    ],
    defaultConfig: { jdbc_url: '', username: '', password: '', temp_dir: '', source_type: 'table', table_name: '', query: '' },
  },

  'etl-source-bigquery': {
    type: 'etl-source-bigquery',
    category: 'sources',
    label: 'BigQuery',
    description: 'Read data from Google BigQuery using the Spark BigQuery connector. Supports column pruning and filter pushdown.',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'project', label: 'GCP Project', type: 'text', default: '', section: 'Connection', description: 'Google Cloud project ID' },
      { key: 'dataset', label: 'Dataset', type: 'text', default: '', section: 'Connection', description: 'BigQuery dataset name' },
      { key: 'table', label: 'Table', type: 'text', default: '', section: 'Connection', description: 'BigQuery table name' },
      { key: 'credentials_json', label: 'Service Account Key', type: 'textarea', default: '', section: 'Auth', description: 'Service account credentials JSON (leave empty for default credentials)' },
      { key: 'filter', label: 'Filter', type: 'textarea', default: '', section: 'Query', description: 'SQL WHERE clause for predicate pushdown (e.g., date >= \"2024-01-01\")' },
      { key: 'selected_fields', label: 'Selected Columns', type: 'tags', default: [], section: 'Query', description: 'Columns to read (empty = all columns)' },
    ],
    defaultConfig: { project: '', dataset: '', table: '', credentials_json: '', filter: '', selected_fields: [] },
  },

  // --- Lakehouse Format Sources ---

  'etl-source-delta': {
    type: 'etl-source-delta',
    category: 'sources',
    label: 'Delta Lake',
    description: 'Read data from Delta Lake tables. Supports time travel, schema evolution, and ACID transactions.',
    icon: 'Triangle',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'path', label: 'Delta Table Path', type: 'text', default: '', section: 'Connection', description: 'Path to Delta table (local, S3, HDFS, or ADLS)' },
      { key: 'time_travel', label: 'Time Travel', type: 'select', options: [
        { value: 'latest', label: 'Latest Version' }, { value: 'version', label: 'Specific Version' }, { value: 'timestamp', label: 'Specific Timestamp' },
      ], default: 'latest', section: 'Version', description: 'Read a specific version of the Delta table' },
      { key: 'version_number', label: 'Version Number', type: 'number', default: 0, section: 'Version', description: 'Delta table version number (when Time Travel is Specific Version)' },
      { key: 'timestamp_str', label: 'Timestamp', type: 'text', default: '', section: 'Version', description: 'Timestamp for time travel (e.g., 2024-01-15T10:00:00Z)' },
    ],
    defaultConfig: { path: '', time_travel: 'latest', version_number: 0, timestamp_str: '' },
  },

  'etl-source-iceberg': {
    type: 'etl-source-iceberg',
    category: 'sources',
    label: 'Apache Iceberg',
    description: 'Read data from Apache Iceberg tables. Supports snapshot isolation, schema evolution, and hidden partitioning.',
    icon: 'Hexagon',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'catalog', label: 'Catalog Name', type: 'text', default: '', section: 'Connection', description: 'Iceberg catalog name (configured in Spark session)' },
      { key: 'database', label: 'Database / Namespace', type: 'text', default: 'default', section: 'Connection', description: 'Iceberg database or namespace' },
      { key: 'table', label: 'Table', type: 'text', default: '', section: 'Connection', description: 'Iceberg table name' },
      { key: 'snapshot_id', label: 'Snapshot ID', type: 'text', default: '', section: 'Version', description: 'Read a specific snapshot (leave empty for latest)' },
      { key: 'as_of_timestamp', label: 'As Of Timestamp', type: 'text', default: '', section: 'Version', description: 'Time travel to a specific timestamp (e.g., 2024-01-15T10:00:00Z)' },
    ],
    defaultConfig: { catalog: '', database: 'default', table: '', snapshot_id: '', as_of_timestamp: '' },
  },

  // --- Streaming Sources ---

  'etl-source-kinesis': {
    type: 'etl-source-kinesis',
    category: 'sources',
    label: 'AWS Kinesis',
    description: 'Read streaming data from Amazon Kinesis Data Streams using Spark Structured Streaming.',
    icon: 'Waves',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Stream', type: 'dataframe' }],
    configSchema: [
      { key: 'stream_name', label: 'Stream Name', type: 'text', default: '', section: 'Connection', description: 'Kinesis stream name' },
      { key: 'region', label: 'AWS Region', type: 'text', default: 'us-east-1', section: 'Connection', description: 'AWS region of the Kinesis stream' },
      { key: 'starting_position', label: 'Starting Position', type: 'select', options: [
        { value: 'latest', label: 'Latest' }, { value: 'trim_horizon', label: 'Trim Horizon (Earliest)' }, { value: 'at_timestamp', label: 'At Timestamp' },
      ], default: 'latest', section: 'Options', description: 'Where to start reading from the stream' },
      { key: 'format', label: 'Record Format', type: 'select', options: [
        { value: 'json', label: 'JSON' }, { value: 'csv', label: 'CSV' }, { value: 'avro', label: 'Avro' }, { value: 'string', label: 'Raw String' },
      ], default: 'json', section: 'Format', description: 'Format of the Kinesis records' },
    ],
    defaultConfig: { stream_name: '', region: 'us-east-1', starting_position: 'latest', format: 'json' },
  },

  // --- API / Transfer Sources ---

  'etl-source-rest-api': {
    type: 'etl-source-rest-api',
    category: 'sources',
    label: 'REST API',
    description: 'Read data from a REST API endpoint. Supports pagination, authentication, and JSON response parsing.',
    icon: 'Globe',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'url', label: 'Base URL', type: 'text', default: '', section: 'Connection', description: 'API base URL (e.g., https://api.example.com/v1/data)' },
      { key: 'method', label: 'HTTP Method', type: 'select', options: [
        { value: 'GET', label: 'GET' }, { value: 'POST', label: 'POST' },
      ], default: 'GET', section: 'Connection', description: 'HTTP method for the API request' },
      { key: 'headers', label: 'Headers', type: 'textarea', default: '{}', section: 'Connection', description: 'HTTP headers as JSON (e.g., {\"Authorization\": \"Bearer token\"})' },
      { key: 'body', label: 'Request Body', type: 'textarea', default: '', section: 'Connection', description: 'Request body for POST requests (JSON)' },
      { key: 'response_path', label: 'Data Path', type: 'text', default: '', section: 'Parsing', description: 'JSON path to the data array in the response (e.g., data.results). Leave empty if response is an array.' },
      { key: 'pagination_type', label: 'Pagination', type: 'select', options: [
        { value: 'none', label: 'None' }, { value: 'offset', label: 'Offset / Limit' },
        { value: 'cursor', label: 'Cursor-Based' }, { value: 'next_url', label: 'Next URL Link' },
      ], default: 'none', section: 'Pagination', description: 'API pagination strategy' },
      { key: 'page_size', label: 'Page Size', type: 'number', default: 100, section: 'Pagination', description: 'Number of records per page' },
      { key: 'max_pages', label: 'Max Pages', type: 'number', default: 0, section: 'Pagination', description: 'Maximum pages to fetch (0 = unlimited)' },
    ],
    defaultConfig: { url: '', method: 'GET', headers: '{}', body: '', response_path: '', pagination_type: 'none', page_size: 100, max_pages: 0 },
  },

  'etl-source-ftp': {
    type: 'etl-source-ftp',
    category: 'sources',
    label: 'FTP / SFTP',
    description: 'Read files from FTP or SFTP servers. Downloads files to Spark for processing. Supports CSV, JSON, Parquet, and fixed-width formats.',
    icon: 'FolderSync',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'protocol', label: 'Protocol', type: 'select', options: [
        { value: 'ftp', label: 'FTP' }, { value: 'sftp', label: 'SFTP' },
      ], default: 'sftp', section: 'Connection', description: 'File transfer protocol' },
      { key: 'host', label: 'Host', type: 'text', default: '', section: 'Connection', description: 'FTP/SFTP server hostname' },
      { key: 'port', label: 'Port', type: 'number', default: 22, section: 'Connection', description: 'Server port (22 for SFTP, 21 for FTP)' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Auth', description: 'Login username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'Login password (or use key-based auth for SFTP)' },
      { key: 'remote_path', label: 'Remote Path', type: 'text', default: '', section: 'Data', description: 'Path to file or directory on the remote server' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'csv', label: 'CSV' }, { value: 'json', label: 'JSON' }, { value: 'parquet', label: 'Parquet' },
        { value: 'text', label: 'Text / Fixed-Width' }, { value: 'xml', label: 'XML' },
      ], default: 'csv', section: 'Format', description: 'Format of the remote files' },
    ],
    defaultConfig: { protocol: 'sftp', host: '', port: 22, username: '', password: '', remote_path: '', file_format: 'csv' },
  },

  // --- Mainframe Sources ---

  'etl-source-ims': {
    type: 'etl-source-ims',
    category: 'sources',
    label: 'IMS DB',
    description: 'Read data from IBM IMS hierarchical databases. Supports segment extraction, SSA-based selection, and PCB-based navigation of the IMS hierarchy.',
    icon: 'Network',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'host', label: 'Host', type: 'text', default: '', section: 'Connection', description: 'IMS Connect hostname or IP' },
      { key: 'port', label: 'Port', type: 'number', default: 9999, section: 'Connection', description: 'IMS Connect port' },
      { key: 'datastore', label: 'Datastore', type: 'text', default: '', section: 'Connection', description: 'IMS datastore name (IMS subsystem ID)' },
      { key: 'psb_name', label: 'PSB Name', type: 'text', default: '', section: 'Database', description: 'Program Specification Block name' },
      { key: 'pcb_name', label: 'PCB Name', type: 'text', default: '', section: 'Database', description: 'Program Communication Block name' },
      { key: 'segment_name', label: 'Root Segment', type: 'text', default: '', section: 'Database', description: 'Root segment name to start extraction from' },
      { key: 'child_segments', label: 'Child Segments', type: 'tags', default: [], section: 'Database', description: 'Child segment names to include (empty = root only)' },
      { key: 'ssa', label: 'SSA (Segment Search Arguments)', type: 'textarea', default: '', section: 'Query', description: 'Segment Search Arguments for qualified calls (e.g., CUSTOMER(CUSTID  = 00001))' },
      { key: 'copybook_path', label: 'Copybook Path', type: 'text', default: '', section: 'Schema', description: 'Path to COBOL copybook for segment layout' },
      { key: 'encoding', label: 'Encoding', type: 'select', options: [
        { value: 'ebcdic', label: 'EBCDIC' }, { value: 'ascii', label: 'ASCII' },
      ], default: 'ebcdic', section: 'Schema', description: 'Character encoding of the segment data' },
    ],
    defaultConfig: { host: '', port: 9999, datastore: '', psb_name: '', pcb_name: '', segment_name: '', child_segments: [], ssa: '', copybook_path: '', encoding: 'ebcdic' },
  },

  // --- SQL Script Sources (DDL / DML) ---

  'etl-source-ddl': {
    type: 'etl-source-ddl',
    category: 'sources',
    label: 'DDL Source',
    description: 'Upload or author DDL scripts (CREATE TABLE, ALTER, DROP, etc.). Supports DB2, SQL Server, PostgreSQL, Oracle, and MySQL dialects. Upload individual .sql files or a .zip archive.',
    icon: 'FileCode2',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'ddl', name: 'DDL Scripts', type: 'sql' }],
    configSchema: [
      { key: 'dialect', label: 'SQL Dialect', type: 'select', options: [
        { value: 'db2', label: 'IBM DB2' },
        { value: 'sqlserver', label: 'SQL Server (T-SQL)' },
        { value: 'postgresql', label: 'PostgreSQL' },
        { value: 'oracle', label: 'Oracle (PL/SQL)' },
        { value: 'mysql', label: 'MySQL' },
      ], default: 'db2', section: 'Script', description: 'Target SQL dialect for syntax highlighting and validation' },
      { key: 'sql_script', label: 'DDL Script', type: 'sql_script', default: '', section: 'Script', description: 'CREATE TABLE, ALTER TABLE, CREATE INDEX, and other DDL statements' },
      { key: 'create_database', label: 'Create database if not exists', type: 'checkbox', default: false, section: 'Execution', description: 'Execute a CREATE DATABASE IF NOT EXISTS before running DDL statements' },
      { key: 'database_name', label: 'Database Name', type: 'text', default: '', section: 'Execution', description: 'Database name to create (when "Create database" is checked)' },
      { key: 'on_error', label: 'On Error', type: 'select', options: [
        { value: 'stop', label: 'Stop Execution' },
        { value: 'skip', label: 'Skip Statement & Continue' },
        { value: 'log', label: 'Log Error & Continue' },
      ], default: 'stop', section: 'Execution', description: 'How to handle errors when executing DDL statements' },
    ],
    defaultConfig: {
      dialect: 'db2',
      sql_script: '',
      create_database: false,
      database_name: '',
      on_error: 'stop',
    },
  },

  'etl-source-dml': {
    type: 'etl-source-dml',
    category: 'sources',
    label: 'DML Source',
    description: 'Upload or author DML scripts (INSERT, UPDATE, DELETE, stored procedures, etc.). Supports DB2, SQL Server, PostgreSQL, Oracle, and MySQL dialects. Upload individual .sql files or a .zip archive.',
    icon: 'FileEdit',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'dml', name: 'DML Scripts', type: 'sql' }],
    configSchema: [
      { key: 'dialect', label: 'SQL Dialect', type: 'select', options: [
        { value: 'db2', label: 'IBM DB2' },
        { value: 'sqlserver', label: 'SQL Server (T-SQL)' },
        { value: 'postgresql', label: 'PostgreSQL' },
        { value: 'oracle', label: 'Oracle (PL/SQL)' },
        { value: 'mysql', label: 'MySQL' },
      ], default: 'db2', section: 'Script', description: 'Target SQL dialect for syntax highlighting and validation' },
      { key: 'sql_script', label: 'DML Script', type: 'sql_script', default: '', section: 'Script', description: 'INSERT, UPDATE, DELETE, MERGE, stored procedures, and other DML statements' },
      { key: 'statement_delimiter', label: 'Statement Delimiter', type: 'text', default: ';', section: 'Execution', description: 'Character(s) used to separate individual SQL statements (e.g., ; or GO)' },
      { key: 'on_error', label: 'On Error', type: 'select', options: [
        { value: 'stop', label: 'Stop Execution' },
        { value: 'skip', label: 'Skip Statement & Continue' },
        { value: 'rollback', label: 'Rollback Transaction' },
        { value: 'log', label: 'Log Error & Continue' },
      ], default: 'stop', section: 'Execution', description: 'How to handle errors when executing DML statements' },
      { key: 'use_transaction', label: 'Wrap in Transaction', type: 'checkbox', default: true, section: 'Execution', description: 'Execute all DML statements within a single transaction' },
      { key: 'batch_size', label: 'Batch Size', type: 'number', default: 1000, section: 'Performance', description: 'Number of statements to execute per batch (0 = no batching)', min: 0, max: 100000 },
    ],
    defaultConfig: {
      dialect: 'db2',
      sql_script: '',
      statement_delimiter: ';',
      on_error: 'stop',
      use_transaction: true,
      batch_size: 1000,
    },
  },

  // --- Graph / Analytics Sources ---

  'etl-source-neo4j': {
    type: 'etl-source-neo4j',
    category: 'sources',
    label: 'Neo4j',
    description: 'Read nodes and relationships from a Neo4j graph database using the official Neo4j Spark connector. Supports Cypher queries and label-based reads.',
    icon: 'Network',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'url', label: 'Bolt URL', type: 'text', default: 'bolt://localhost:7687', section: 'Connection', description: 'Neo4j Bolt connection URL (e.g., bolt://localhost:7687 or neo4j://host:7687)' },
      { key: 'username', label: 'Username', type: 'text', default: 'neo4j', section: 'Auth', description: 'Neo4j username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'Neo4j password' },
      { key: 'database', label: 'Database', type: 'text', default: 'neo4j', section: 'Connection', description: 'Neo4j database name (Enterprise Edition supports multiple databases)' },
      { key: 'read_mode', label: 'Read Mode', type: 'select', options: [
        { value: 'query', label: 'Cypher Query' },
        { value: 'labels', label: 'Node Labels' },
        { value: 'relationship', label: 'Relationship Type' },
      ], default: 'query', section: 'Data', description: 'How to read data from Neo4j' },
      { key: 'query', label: 'Cypher Query', type: 'textarea', default: '', section: 'Data', description: 'Cypher query to execute (when Read Mode is Cypher Query). Must return tabular data.' },
      { key: 'labels', label: 'Node Labels', type: 'text', default: '', section: 'Data', description: 'Node label to read (when Read Mode is Node Labels, e.g., Person or Person:Employee)' },
      { key: 'relationship', label: 'Relationship Type', type: 'text', default: '', section: 'Data', description: 'Relationship type to read (when Read Mode is Relationship Type, e.g., KNOWS)' },
      { key: 'partitions', label: 'Partitions', type: 'number', default: 1, section: 'Performance', description: 'Number of partitions for parallel reads' },
    ],
    defaultConfig: { url: 'bolt://localhost:7687', username: 'neo4j', password: '', database: 'neo4j', read_mode: 'query', query: '', labels: '', relationship: '', partitions: 1 },
  },

  'etl-source-sqlserver': {
    type: 'etl-source-sqlserver',
    category: 'sources',
    label: 'SQL Server',
    description: 'Read data from Microsoft SQL Server and Azure SQL using the dedicated Apache Spark connector for SQL Server. Optimized bulk reads, Active Directory auth, and column encryption support.',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'host', label: 'Host', type: 'text', default: '', section: 'Connection', description: 'SQL Server hostname or IP address' },
      { key: 'port', label: 'Port', type: 'number', default: 1433, section: 'Connection', description: 'SQL Server port' },
      { key: 'database', label: 'Database', type: 'text', default: '', section: 'Connection', description: 'Database name' },
      { key: 'auth_method', label: 'Authentication', type: 'select', options: [
        { value: 'sql', label: 'SQL Authentication' },
        { value: 'ad_password', label: 'Active Directory Password' },
        { value: 'ad_integrated', label: 'Active Directory Integrated' },
        { value: 'ad_msi', label: 'Managed Identity (Azure)' },
      ], default: 'sql', section: 'Auth', description: 'Authentication method' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Auth', description: 'SQL Server username (SQL or AD Password auth)' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'SQL Server password' },
      { key: 'source_type', label: 'Source Type', type: 'select', options: [
        { value: 'table', label: 'Table / View' },
        { value: 'query', label: 'Custom Query' },
      ], default: 'table', section: 'Data', description: 'Read from a table/view or a custom SQL query' },
      { key: 'schema', label: 'Schema', type: 'text', default: 'dbo', section: 'Data', description: 'Database schema (default: dbo)' },
      { key: 'table_name', label: 'Table / View Name', type: 'text', default: '', section: 'Data', description: 'Table or view name (when Source Type is Table)' },
      { key: 'query', label: 'SQL Query', type: 'textarea', default: '', section: 'Data', description: 'Custom SQL query (when Source Type is Query)' },
      { key: 'encrypt', label: 'Encrypt Connection', type: 'checkbox', default: true, section: 'Security', description: 'Use TLS encryption for the connection' },
      { key: 'trust_server_cert', label: 'Trust Server Certificate', type: 'checkbox', default: false, section: 'Security', description: 'Trust the server certificate without validation (dev only)' },
    ],
    defaultConfig: { host: '', port: 1433, database: '', auth_method: 'sql', username: '', password: '', source_type: 'table', schema: 'dbo', table_name: '', query: '', encrypt: true, trust_server_cert: false },
  },

  'etl-source-starrocks': {
    type: 'etl-source-starrocks',
    category: 'sources',
    label: 'StarRocks',
    description: 'Read data from StarRocks OLAP database using the official StarRocks Spark connector. Supports predicate and projection pushdown for optimized reads.',
    icon: 'Zap',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'fe_nodes', label: 'FE Nodes', type: 'text', default: '', section: 'Connection', description: 'Comma-separated StarRocks FE node addresses (host:http_port, e.g., fe1:8030,fe2:8030)' },
      { key: 'database', label: 'Database', type: 'text', default: '', section: 'Connection', description: 'StarRocks database name' },
      { key: 'table', label: 'Table', type: 'text', default: '', section: 'Connection', description: 'StarRocks table name' },
      { key: 'username', label: 'Username', type: 'text', default: 'root', section: 'Auth', description: 'StarRocks username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'StarRocks password' },
      { key: 'filter', label: 'Filter', type: 'textarea', default: '', section: 'Query', description: 'SQL WHERE clause for predicate pushdown (e.g., dt >= \"2024-01-01\")' },
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Query', description: 'Columns to read (empty = all columns, projection pushdown)' },
      { key: 'batch_rows', label: 'Batch Rows', type: 'number', default: 4096, section: 'Performance', description: 'Number of rows per batch during reads' },
    ],
    defaultConfig: { fe_nodes: '', database: '', table: '', username: 'root', password: '', filter: '', columns: [], batch_rows: 4096 },
  },

  'etl-source-pdf': {
    type: 'etl-source-pdf',
    category: 'sources',
    label: 'PDF',
    description: 'Extract text, tables, and metadata from PDF files using the spark-pdf connector. Each page becomes a row with extracted text content. Useful for document processing pipelines.',
    icon: 'FileText',
    color: CATEGORY_COLORS.sources,
    inputs: [],
    outputs: [{ id: 'output', name: 'Data', type: 'dataframe' }],
    configSchema: [
      { key: 'path', label: 'Path', type: 'text', default: '', section: 'Connection', description: 'Path to PDF file(s) — local, S3, HDFS, or ADLS. Supports glob patterns (e.g., s3://bucket/docs/*.pdf)' },
      { key: 'extract_mode', label: 'Extract Mode', type: 'select', options: [
        { value: 'text', label: 'Text (Full Page Text)' },
        { value: 'table', label: 'Tables (Tabular Data)' },
        { value: 'both', label: 'Both (Text + Tables)' },
      ], default: 'text', section: 'Extraction', description: 'What to extract from the PDF pages' },
      { key: 'pages', label: 'Pages', type: 'text', default: '', section: 'Extraction', description: 'Page range to extract (e.g., 1-5, 10, 15-20). Leave empty for all pages.' },
      { key: 'ocr_enabled', label: 'OCR Enabled', type: 'checkbox', default: false, section: 'Options', description: 'Enable OCR for scanned/image-based PDFs (requires Tesseract)' },
      { key: 'include_metadata', label: 'Include Metadata', type: 'checkbox', default: true, section: 'Options', description: 'Include PDF metadata (author, title, creation date) as columns' },
    ],
    defaultConfig: { path: '', extract_mode: 'text', pages: '', ocr_enabled: false, include_metadata: true },
  },

  // ===========================================================================
  // TRANSFORMS - Data transformation and manipulation
  // ===========================================================================
  'etl-transform-filter': {
    type: 'etl-transform-filter',
    category: 'transforms',
    label: 'Filter Rows',
    description: 'Filter rows based on a SQL WHERE clause condition. Keep only matching rows or remove them.',
    icon: 'Filter',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Filtered', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'filter_config', label: 'Filter Conditions', type: 'condition_rows', default: '', section: 'Filter', description: 'Define conditions to filter rows. Combine with AND/OR logic.' },
      { key: 'mode', label: 'Filter Mode', type: 'select', options: [
        { value: 'keep', label: 'Keep Matching Rows' },
        { value: 'remove', label: 'Remove Matching Rows' },
      ], default: 'keep', section: 'Filter', description: 'Whether to keep rows that match the condition or remove them' },
    ],
    defaultConfig: {
      filter_config: '',
      mode: 'keep',
    },
  },

  'etl-transform-join': {
    type: 'etl-transform-join',
    category: 'transforms',
    label: 'Join',
    description: 'Join two datasets on matching keys. Supports inner, left, right, full outer, cross, and anti join types.',
    icon: 'Merge',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'left', name: 'Left Input', type: 'dataframe' },
      { id: 'right', name: 'Right Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Joined', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'join_type', label: 'Join Type', type: 'select', options: [
        { value: 'inner', label: 'Inner Join' },
        { value: 'left', label: 'Left Outer Join' },
        { value: 'right', label: 'Right Outer Join' },
        { value: 'full', label: 'Full Outer Join' },
        { value: 'cross', label: 'Cross Join' },
        { value: 'anti', label: 'Anti Join (Left Anti)' },
      ], default: 'inner', section: 'Join', description: 'The type of join to perform between left and right inputs' },
      { key: 'left_key', label: 'Left Key Column', type: 'text', default: '', section: 'Keys', description: 'Column name from the left input to join on' },
      { key: 'right_key', label: 'Right Key Column', type: 'text', default: '', section: 'Keys', description: 'Column name from the right input to join on' },
      { key: 'join_keys', label: 'Additional Key Pairs', type: 'key_pairs', default: '[]', section: 'Keys', description: 'Additional key pairs for multi-column joins', leftLabel: 'Left Column', rightLabel: 'Right Column' },
      { key: 'join_condition', label: 'Advanced Join Condition', type: 'textarea', default: '', section: 'Advanced', description: 'SQL expression for complex join conditions (overrides key columns if set). Example: left.id = right.id AND left.date > right.start_date' },
      { key: 'broadcast_hint', label: 'Broadcast Hint', type: 'select', options: [
        { value: 'auto', label: 'Auto (Spark Decides)' },
        { value: 'left', label: 'Broadcast Left Input' },
        { value: 'right', label: 'Broadcast Right Input' },
        { value: 'none', label: 'No Broadcast (Force Shuffle)' },
      ], default: 'auto', section: 'Performance', description: 'Broadcast the smaller dataset to all executors for faster joins. Use when one side is small (< 10MB default). Critical for performance.' },
      { key: 'drop_duplicate_keys', label: 'Drop Duplicate Keys', type: 'checkbox', default: true, section: 'Output', description: 'Drop the duplicate join key column from the right side (avoids ambiguous column names)' },
    ],
    defaultConfig: {
      join_type: 'inner',
      left_key: '',
      right_key: '',
      additional_keys: '[]',
      join_condition: '',
      broadcast_hint: 'auto',
      drop_duplicate_keys: true,
    },
  },

  'etl-transform-aggregate': {
    type: 'etl-transform-aggregate',
    category: 'transforms',
    label: 'Aggregate',
    description: 'Group rows and compute aggregate functions. Supports count, sum, avg, min, max, first, last, collect_list, and stddev.',
    icon: 'BarChart3',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Aggregated', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'aggregation_config', label: 'Aggregation', type: 'aggregations', default: '', section: 'Aggregation', description: 'Define group-by columns and aggregate functions' },
    ],
    defaultConfig: {
      aggregation_config: '',
    },
  },

  'etl-transform-sort': {
    type: 'etl-transform-sort',
    category: 'transforms',
    label: 'Sort',
    description: 'Sort rows by one or more columns in ascending or descending order.',
    icon: 'ArrowUpDown',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Sorted', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'sort_config', label: 'Sort Order', type: 'sort_rows', default: '[]', section: 'Sort', description: 'Define sort columns with per-column direction and null handling' },
    ],
    defaultConfig: {
      sort_config: '[]',
    },
  },

  'etl-transform-column-mapper': {
    type: 'etl-transform-column-mapper',
    category: 'transforms',
    label: 'Column Mapper',
    description: 'Rename, reorder, and cast columns. Map source columns to target columns with optional type casting. Drop unmapped columns if desired.',
    icon: 'Columns',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Mapped', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'mapping_config', label: 'Column Mappings', type: 'column_mapping', default: '[]', section: 'Mappings', description: 'Define column rename, type cast, and drop operations' },
      { key: 'drop_unmapped', label: 'Drop Unmapped Columns', type: 'checkbox', default: false, section: 'Options', description: 'Remove columns not listed in the mappings. If unchecked, unmapped columns pass through unchanged.' },
    ],
    defaultConfig: {
      mapping_config: '[]',
      drop_unmapped: false,
    },
  },

  'etl-transform-expression': {
    type: 'etl-transform-expression',
    category: 'transforms',
    label: 'Expression',
    description: 'Add calculated columns using SQL expressions. Each expression produces a new column or overwrites an existing one.',
    icon: 'Calculator',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Enriched', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'expression_config', label: 'Derived Columns', type: 'expression_list', default: '[]', section: 'Expressions', description: 'Define output columns with SQL expressions' },
    ],
    defaultConfig: {
      expression_config: '[]',
    },
  },

  'etl-transform-lookup': {
    type: 'etl-transform-lookup',
    category: 'transforms',
    label: 'Lookup',
    description: 'Enrich data by looking up values from a reference source. Similar to a left join but optimized for enrichment with caching support.',
    icon: 'Search',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'main', name: 'Main Input', type: 'dataframe' },
      { id: 'lookup', name: 'Lookup Source', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Enriched', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'lookup_source', label: 'Lookup Source Type', type: 'select', options: [
        { value: 'database', label: 'Database Table' },
        { value: 'file', label: 'File' },
        { value: 'cache', label: 'In-Memory Cache' },
      ], default: 'cache', section: 'Source', description: 'Where the lookup reference data comes from' },
      { key: 'lookup_key', label: 'Lookup Key Column', type: 'text', default: '', section: 'Keys', description: 'Column name in the lookup source to match against' },
      { key: 'source_key', label: 'Source Key Column', type: 'text', default: '', section: 'Keys', description: 'Column name in the main input to use for matching' },
      { key: 'return_columns', label: 'Return Columns', type: 'tags', default: [], section: 'Output', description: 'Column names from the lookup source to add to the output' },
      { key: 'cache_enabled', label: 'Enable Cache', type: 'checkbox', default: true, section: 'Performance', description: 'Cache the lookup dataset in memory for faster repeated lookups' },
      { key: 'default_value', label: 'Default Value', type: 'text', default: '', section: 'Output', description: 'Value to use when no match is found in the lookup source (empty = null)' },
    ],
    defaultConfig: {
      lookup_source: 'cache',
      lookup_key: '',
      source_key: '',
      return_columns: [],
      cache_enabled: true,
      default_value: '',
    },
  },

  'etl-transform-union': {
    type: 'etl-transform-union',
    category: 'transforms',
    label: 'Union',
    description: 'Combine two datasets vertically (append rows). Supports UNION (deduplicated) and UNION ALL (keep duplicates) with optional name-based column matching.',
    icon: 'ListPlus',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input1', name: 'Input 1', type: 'dataframe' },
      { id: 'input2', name: 'Input 2', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Combined', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'union_type', label: 'Union Type', type: 'select', options: [
        { value: 'union', label: 'UNION (Deduplicate)' },
        { value: 'union_all', label: 'UNION ALL (Keep Duplicates)' },
      ], default: 'union_all', section: 'Union', description: 'UNION removes duplicate rows; UNION ALL keeps all rows' },
      { key: 'by_name', label: 'Match By Column Name', type: 'checkbox', default: false, section: 'Options', description: 'Match columns by name instead of position. Missing columns are filled with nulls.' },
    ],
    defaultConfig: {
      union_type: 'union_all',
      by_name: false,
    },
  },

  'etl-transform-split': {
    type: 'etl-transform-split',
    category: 'transforms',
    label: 'Conditional Split',
    description: 'Route rows to different outputs based on conditions. Each condition creates a named output. Rows not matching any condition go to the default output.',
    icon: 'GitBranch',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'default', name: 'Default', type: 'dataframe' },
      { id: 'condition_1', name: 'Condition 1', type: 'dataframe' },
      { id: 'condition_2', name: 'Condition 2', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'conditions', label: 'Conditions', type: 'textarea', default: '[]', section: 'Routing', description: 'JSON array of routing conditions: [{\"name\": \"high_value\", \"condition\": \"amount > 10000\"}, {\"name\": \"low_value\", \"condition\": \"amount <= 100\"}]. Each condition creates a named output.' },
      { key: 'default_output_name', label: 'Default Output Name', type: 'text', default: 'unmatched', section: 'Routing', description: 'Name for the output that receives rows not matching any condition' },
    ],
    defaultConfig: {
      conditions: '[]',
      default_output_name: 'unmatched',
    },
  },

  'etl-transform-deduplicate': {
    type: 'etl-transform-deduplicate',
    category: 'transforms',
    label: 'Deduplicate',
    description: 'Remove duplicate rows based on key columns. Choose which duplicate to keep (first, last, or any) with optional ordering.',
    icon: 'Copy',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Deduplicated', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'key_columns', label: 'Key Columns', type: 'tags', default: [], section: 'Dedup', description: 'Columns that define uniqueness. Rows with identical values in these columns are considered duplicates.' },
      { key: 'keep', label: 'Keep', type: 'select', options: [
        { value: 'first', label: 'First Occurrence' },
        { value: 'last', label: 'Last Occurrence' },
        { value: 'any', label: 'Any (Non-deterministic)' },
      ], default: 'first', section: 'Dedup', description: 'Which duplicate row to retain' },
      { key: 'order_by', label: 'Order By', type: 'text', default: '', section: 'Dedup', description: 'Column to order by when determining first/last (e.g., updated_at DESC)' },
    ],
    defaultConfig: {
      key_columns: [],
      keep: 'first',
      order_by: '',
    },
  },

  'etl-transform-pivot': {
    type: 'etl-transform-pivot',
    category: 'transforms',
    label: 'Pivot / Unpivot',
    description: 'Pivot (rows to columns) or unpivot (columns to rows) a dataset. Pivot aggregates values for each distinct value in the pivot column.',
    icon: 'RotateCcw',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Pivoted', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'mode', label: 'Mode', type: 'select', options: [
        { value: 'pivot', label: 'Pivot (Rows to Columns)' },
        { value: 'unpivot', label: 'Unpivot (Columns to Rows)' },
      ], default: 'pivot', section: 'Mode', description: 'Pivot converts row values into columns; Unpivot converts columns into rows' },
      { key: 'pivot_column', label: 'Pivot Column', type: 'text', default: '', section: 'Columns', description: 'Column whose distinct values become new column headers (pivot) or the name column (unpivot)' },
      { key: 'value_column', label: 'Value Column', type: 'text', default: '', section: 'Columns', description: 'Column containing the values to aggregate (pivot) or the value column name (unpivot)' },
      { key: 'agg_function', label: 'Aggregation Function', type: 'select', options: [
        { value: 'sum', label: 'Sum' },
        { value: 'avg', label: 'Average' },
        { value: 'count', label: 'Count' },
        { value: 'min', label: 'Min' },
        { value: 'max', label: 'Max' },
        { value: 'first', label: 'First' },
      ], default: 'sum', section: 'Aggregation', description: 'Aggregation function to apply when pivoting (used when multiple rows map to same cell)' },
      { key: 'pivot_values', label: 'Pivot Values', type: 'tags', default: [], section: 'Columns', description: 'Explicit list of pivot column values to use as new columns (empty = auto-detect all distinct values)' },
    ],
    defaultConfig: {
      mode: 'pivot',
      pivot_column: '',
      value_column: '',
      agg_function: 'sum',
      pivot_values: [],
    },
  },

  'etl-transform-window': {
    type: 'etl-transform-window',
    category: 'transforms',
    label: 'Window Function',
    description: 'Apply window (analytic) functions over partitions of data. Supports ranking, lag/lead, and running aggregates without collapsing rows.',
    icon: 'Rows3',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Windowed', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'window_function', label: 'Window Function', type: 'select', options: [
        { value: 'row_number', label: 'ROW_NUMBER' },
        { value: 'rank', label: 'RANK' },
        { value: 'dense_rank', label: 'DENSE_RANK' },
        { value: 'lag', label: 'LAG' },
        { value: 'lead', label: 'LEAD' },
        { value: 'sum', label: 'SUM (Running)' },
        { value: 'avg', label: 'AVG (Running)' },
        { value: 'min', label: 'MIN (Running)' },
        { value: 'max', label: 'MAX (Running)' },
      ], default: 'row_number', section: 'Function', description: 'The window function to apply' },
      { key: 'partition_by', label: 'Partition By', type: 'tags', default: [], section: 'Window', description: 'Columns to partition the window by (like GROUP BY but without collapsing rows)' },
      { key: 'order_by', label: 'Order By', type: 'text', default: '', section: 'Window', description: 'Column to order rows within each partition (e.g., created_at ASC)' },
      { key: 'frame_type', label: 'Frame Type', type: 'select', options: [
        { value: 'rows', label: 'ROWS' },
        { value: 'range', label: 'RANGE' },
      ], default: 'rows', section: 'Frame', description: 'Window frame type (ROWS = physical rows, RANGE = logical value range)' },
      { key: 'frame_start', label: 'Frame Start', type: 'text', default: 'unbounded preceding', section: 'Frame', description: 'Window frame start boundary (e.g., unbounded preceding, 3 preceding, current row)' },
      { key: 'frame_end', label: 'Frame End', type: 'text', default: 'current row', section: 'Frame', description: 'Window frame end boundary (e.g., current row, 3 following, unbounded following)' },
      { key: 'output_column', label: 'Output Column Name', type: 'text', default: '', section: 'Output', description: 'Name for the new column containing the window function result' },
    ],
    defaultConfig: {
      window_function: 'row_number',
      partition_by: [],
      order_by: '',
      frame_type: 'rows',
      frame_start: 'unbounded preceding',
      frame_end: 'current row',
      output_column: '',
    },
  },

  'etl-transform-custom-sql': {
    type: 'etl-transform-custom-sql',
    category: 'transforms',
    label: 'Custom SQL',
    description: 'Apply an arbitrary SQL query against the input data. The input is registered as a temporary view that can be referenced in the query.',
    icon: 'Code',
    color: CATEGORY_COLORS.transforms,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Result', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'sql_query', label: 'SQL Query', type: 'textarea', default: '', section: 'Query', description: 'SQL SELECT query. Reference the input data using the temp view name (default: input_data). Example: SELECT * FROM input_data WHERE status = \'active\'' },
      { key: 'temp_view_name', label: 'Temp View Name', type: 'text', default: 'input_data', section: 'Query', description: 'Name of the temporary view registered for the input dataframe' },
    ],
    defaultConfig: {
      sql_query: '',
      temp_view_name: 'input_data',
    },
  },

  // --- Additional Core Transforms ---

  'etl-transform-intersect': {
    type: 'etl-transform-intersect',
    category: 'transforms',
    label: 'Intersect',
    description: 'Return rows that exist in both input datasets. Like SQL INTERSECT.',
    icon: 'CircleDot',
    color: CATEGORY_COLORS.transforms,
    inputs: [{ id: 'left', name: 'Left', type: 'dataframe' }, { id: 'right', name: 'Right', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Common Rows', type: 'dataframe' }],
    configSchema: [
      { key: 'distinct', label: 'Distinct Only', type: 'checkbox', default: true, section: 'Options', description: 'Remove duplicate rows from the result (INTERSECT vs INTERSECT ALL)' },
    ],
    defaultConfig: { distinct: true },
  },

  'etl-transform-except': {
    type: 'etl-transform-except',
    category: 'transforms',
    label: 'Except / Subtract',
    description: 'Return rows from the left input that do not exist in the right input. Like SQL EXCEPT.',
    icon: 'MinusCircle',
    color: CATEGORY_COLORS.transforms,
    inputs: [{ id: 'left', name: 'Left', type: 'dataframe' }, { id: 'right', name: 'Right', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Difference', type: 'dataframe' }],
    configSchema: [
      { key: 'distinct', label: 'Distinct Only', type: 'checkbox', default: true, section: 'Options', description: 'Remove duplicate rows from the result (EXCEPT vs EXCEPT ALL)' },
    ],
    defaultConfig: { distinct: true },
  },

  // --- SQL Transform Nodes (operate on SQL text, not dataframes) ---

  'etl-transform-sql-dedup': {
    type: 'etl-transform-sql-dedup',
    category: 'transforms',
    label: 'SQL Deduplicator',
    description: 'Remove duplicate column definitions from CREATE TABLE statements. Commonly needed when COBOL copybook flattening produces duplicate fields from group/child hierarchies. Logs every removal for full transparency.',
    icon: 'CopyMinus',
    color: CATEGORY_COLORS.transforms,
    inputs: [{ id: 'input', name: 'SQL Input', type: 'sql' }],
    outputs: [{ id: 'output', name: 'Deduplicated SQL', type: 'sql' }],
    configSchema: [
      { key: 'keep_strategy', label: 'Keep Strategy', type: 'select', options: [
        { value: 'first', label: 'Keep First Occurrence' },
        { value: 'last', label: 'Keep Last Occurrence' },
      ], default: 'first', section: 'Dedup', description: 'When duplicate columns are found, which occurrence to keep' },
      { key: 'log_removals', label: 'Log Removals', type: 'checkbox', default: true, section: 'Dedup', description: 'Log each removed duplicate column in the execution log' },
      { key: 'dedup_pk', label: 'Deduplicate Primary Keys', type: 'checkbox', default: true, section: 'Dedup', description: 'Also remove duplicate columns from PRIMARY KEY constraints' },
    ],
    defaultConfig: { keep_strategy: 'first', log_removals: true, dedup_pk: true },
  },

  'etl-transform-sql-types': {
    type: 'etl-transform-sql-types',
    category: 'transforms',
    label: 'Type Normalizer',
    description: 'Fix SQL column types using inline COBOL PIC comments. Corrects VARCHAR(255) fallback types to actual lengths derived from PIC clauses (e.g., PIC X(4) becomes VARCHAR(4), PIC XX becomes VARCHAR(2)).',
    icon: 'Type',
    color: CATEGORY_COLORS.transforms,
    inputs: [{ id: 'input', name: 'SQL Input', type: 'sql' }],
    outputs: [{ id: 'output', name: 'Normalized SQL', type: 'sql' }],
    configSchema: [
      { key: 'fix_varchar255', label: 'Fix VARCHAR(255)', type: 'checkbox', default: true, section: 'Type Fixes', description: 'Replace VARCHAR(255) with actual lengths derived from PIC comments' },
      { key: 'use_pic_comments', label: 'Use PIC Comments', type: 'checkbox', default: true, section: 'Type Fixes', description: 'Parse inline "-- PIC X(n)" comments to determine correct column types' },
    ],
    defaultConfig: { fix_varchar255: true, use_pic_comments: true },
  },

  'etl-transform-sql-dialect': {
    type: 'etl-transform-sql-dialect',
    category: 'transforms',
    label: 'Dialect Converter',
    description: 'Convert SQL between database dialects. Handles identifier quoting (brackets vs double-quotes vs backticks), type name differences, and syntax variations between SQL Server, DB2, PostgreSQL, Oracle, and MySQL.',
    icon: 'Languages',
    color: CATEGORY_COLORS.transforms,
    inputs: [{ id: 'input', name: 'SQL Input', type: 'sql' }],
    outputs: [{ id: 'output', name: 'Converted SQL', type: 'sql' }],
    configSchema: [
      { key: 'source_dialect', label: 'Source Dialect', type: 'select', options: [
        { value: 'sqlserver', label: 'SQL Server (T-SQL)' },
        { value: 'db2', label: 'IBM DB2' },
        { value: 'postgresql', label: 'PostgreSQL' },
        { value: 'oracle', label: 'Oracle' },
        { value: 'mysql', label: 'MySQL' },
      ], default: 'sqlserver', section: 'Conversion', description: 'The SQL dialect of the input scripts' },
      { key: 'target_dialect', label: 'Target Dialect', type: 'select', options: [
        { value: 'db2', label: 'IBM DB2' },
        { value: 'sqlserver', label: 'SQL Server (T-SQL)' },
        { value: 'postgresql', label: 'PostgreSQL' },
        { value: 'oracle', label: 'Oracle' },
        { value: 'mysql', label: 'MySQL' },
      ], default: 'db2', section: 'Conversion', description: 'The target SQL dialect to convert to' },
      { key: 'strip_procedures', label: 'Strip Stored Procedures', type: 'checkbox', default: true, section: 'Conversion', description: 'Remove stored procedures that cannot be converted between dialects (e.g., SQL Server BEGIN TRY/CATCH → DB2)' },
    ],
    defaultConfig: { source_dialect: 'sqlserver', target_dialect: 'db2', strip_procedures: true },
  },

  // ===========================================================================
  // SCHEMA - Column and schema manipulation
  // ===========================================================================

  'etl-schema-select': {
    type: 'etl-schema-select',
    category: 'schema',
    label: 'Select / Drop Columns',
    description: 'Select specific columns to keep or drop columns from the dataframe. Equivalent to SQL SELECT or dropping fields.',
    icon: 'Columns3',
    color: CATEGORY_COLORS.schema,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'mode', label: 'Mode', type: 'select', options: [
        { value: 'select', label: 'Select (Keep Listed)' }, { value: 'drop', label: 'Drop (Remove Listed)' },
      ], default: 'select', section: 'Columns', description: 'Keep only the listed columns, or remove the listed columns' },
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Columns', description: 'Column names to select or drop' },
    ],
    defaultConfig: { mode: 'select', columns: [] },
  },

  'etl-schema-rename': {
    type: 'etl-schema-rename',
    category: 'schema',
    label: 'Rename Columns',
    description: 'Rename one or more columns. Provide a JSON mapping of old names to new names.',
    icon: 'PenLine',
    color: CATEGORY_COLORS.schema,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'rename_config', label: 'Rename Mappings', type: 'key_pairs', default: '[]', section: 'Mappings', description: 'Map old column names to new names', leftLabel: 'Current Name', rightLabel: 'New Name' },
    ],
    defaultConfig: { rename_config: '[]' },
  },

  'etl-schema-reorder': {
    type: 'etl-schema-reorder',
    category: 'schema',
    label: 'Reorder Columns',
    description: 'Reorder columns in the dataframe. Specify the desired column order.',
    icon: 'ArrowUpDown',
    color: CATEGORY_COLORS.schema,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'column_order', label: 'Column Order', type: 'tags', default: [], section: 'Order', description: 'Columns in the desired order. Unlisted columns are appended at the end.' },
    ],
    defaultConfig: { column_order: [] },
  },

  'etl-schema-type-cast': {
    type: 'etl-schema-type-cast',
    category: 'schema',
    label: 'Type Cast',
    description: 'Change the data type of one or more columns. Supports all Spark data types.',
    icon: 'Replace',
    color: CATEGORY_COLORS.schema,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'casts', label: 'Type Casts', type: 'textarea', default: '[]', section: 'Casts', description: 'JSON array of casts: [{"column": "age", "type": "integer"}, {"column": "amount", "type": "decimal(10,2)"}]. Types: string, integer, long, float, double, decimal(p,s), boolean, date, timestamp, binary' },
    ],
    defaultConfig: { casts: '[]' },
  },

  'etl-schema-add-constant': {
    type: 'etl-schema-add-constant',
    category: 'schema',
    label: 'Add Constant Column',
    description: 'Add one or more columns with constant (literal) values. Useful for tagging data with source identifiers, load dates, or flags.',
    icon: 'PlusCircle',
    color: CATEGORY_COLORS.schema,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'columns', label: 'Constant Columns', type: 'textarea', default: '[]', section: 'Columns', description: 'JSON array of columns to add: [{"name": "source_system", "value": "SAP", "type": "string"}, {"name": "load_date", "value": "current_timestamp", "type": "timestamp"}]' },
    ],
    defaultConfig: { columns: '[]' },
  },

  'etl-schema-flatten': {
    type: 'etl-schema-flatten',
    category: 'schema',
    label: 'Flatten / Explode',
    description: 'Flatten nested structures (structs) into top-level columns, or explode arrays/maps into multiple rows.',
    icon: 'Unfold',
    color: CATEGORY_COLORS.schema,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'mode', label: 'Mode', type: 'select', options: [
        { value: 'flatten', label: 'Flatten Structs (Columns)' },
        { value: 'explode', label: 'Explode Array/Map (Rows)' },
        { value: 'explode_outer', label: 'Explode Outer (Keep Nulls)' },
      ], default: 'flatten', section: 'Operation', description: 'Flatten nested structs into columns, or explode arrays/maps into rows' },
      { key: 'column', label: 'Column', type: 'text', default: '', section: 'Operation', description: 'Column to flatten or explode (leave empty for flatten-all-structs mode)' },
      { key: 'separator', label: 'Name Separator', type: 'text', default: '_', section: 'Options', description: 'Separator for flattened column names (e.g., address_city)' },
    ],
    defaultConfig: { mode: 'flatten', column: '', separator: '_' },
  },

  // ===========================================================================
  // DATA PREP - Data cleaning, formatting, and preparation
  // ===========================================================================

  'etl-prep-fill-na': {
    type: 'etl-prep-fill-na',
    category: 'data_prep',
    label: 'Fill NA / Replace Null',
    description: 'Fill null/missing values with specified defaults. Can fill all columns or specific columns with different values.',
    icon: 'PaintBucket',
    color: CATEGORY_COLORS.data_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'strategy', label: 'Fill Strategy', type: 'select', options: [
        { value: 'value', label: 'Constant Value' },
        { value: 'mean', label: 'Column Mean (Numeric)' },
        { value: 'median', label: 'Column Median (Numeric)' },
        { value: 'mode', label: 'Column Mode (Most Frequent)' },
        { value: 'forward', label: 'Forward Fill (Previous Row)' },
      ], default: 'value', section: 'Strategy', description: 'How to determine the fill value' },
      { key: 'fill_value', label: 'Fill Value', type: 'text', default: '', section: 'Strategy', description: 'Constant value to fill (when strategy is Constant Value)' },
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Columns to fill (empty = all columns)' },
      { key: 'column_values', label: 'Per-Column Values', type: 'textarea', default: '{}', section: 'Scope', description: 'JSON mapping of column-specific fill values (e.g., {"name": "Unknown", "age": 0, "active": true}). Overrides the general fill value.' },
    ],
    defaultConfig: { strategy: 'value', fill_value: '', columns: [], column_values: '{}' },
  },

  'etl-prep-drop-na': {
    type: 'etl-prep-drop-na',
    category: 'data_prep',
    label: 'Drop Nulls',
    description: 'Remove rows with null/missing values. Supports thresholds and column-specific rules.',
    icon: 'Eraser',
    color: CATEGORY_COLORS.data_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'how', label: 'Drop When', type: 'select', options: [
        { value: 'any', label: 'Any Column is Null' },
        { value: 'all', label: 'All Columns are Null' },
        { value: 'threshold', label: 'Below Threshold (Non-Null Count)' },
      ], default: 'any', section: 'Rules', description: 'When to drop a row' },
      { key: 'threshold', label: 'Threshold', type: 'number', default: 0, section: 'Rules', description: 'Minimum number of non-null values to keep a row (only when Drop When is Threshold)' },
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Only consider these columns for null checks (empty = all columns)' },
    ],
    defaultConfig: { how: 'any', threshold: 0, columns: [] },
  },

  'etl-prep-string-functions': {
    type: 'etl-prep-string-functions',
    category: 'data_prep',
    label: 'String Functions',
    description: 'Apply string transformations to columns: trim, pad, substring, replace, regex_replace, concat, split, upper, lower, and more.',
    icon: 'CaseSensitive',
    color: CATEGORY_COLORS.data_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'operations', label: 'String Operations', type: 'textarea', default: '[]', section: 'Operations', description: 'JSON array of operations: [{"column": "name", "function": "upper"}, {"column": "phone", "function": "regexp_replace", "pattern": "[^0-9]", "replacement": ""}, {"column": "code", "function": "lpad", "length": 10, "pad_char": "0"}]. Functions: trim, ltrim, rtrim, upper, lower, initcap, reverse, length, substring, lpad, rpad, concat, split, replace, regexp_replace, regexp_extract' },
    ],
    defaultConfig: { operations: '[]' },
  },

  'etl-prep-datetime': {
    type: 'etl-prep-datetime',
    category: 'data_prep',
    label: 'Date / Time Functions',
    description: 'Parse, format, and manipulate date and timestamp columns. Convert strings to dates, extract parts, calculate differences.',
    icon: 'Calendar',
    color: CATEGORY_COLORS.data_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'operations', label: 'Date/Time Operations', type: 'textarea', default: '[]', section: 'Operations', description: 'JSON array of operations: [{"column": "date_str", "function": "to_date", "format": "yyyy-MM-dd", "output": "parsed_date"}, {"column": "ts", "function": "extract", "part": "year", "output": "year"}]. Functions: to_date, to_timestamp, date_format, extract (year/month/day/hour/minute/second/dayofweek), date_add, date_sub, datediff, months_between, current_date, current_timestamp' },
    ],
    defaultConfig: { operations: '[]' },
  },

  'etl-prep-sample': {
    type: 'etl-prep-sample',
    category: 'data_prep',
    label: 'Sample Rows',
    description: 'Take a random sample of rows from the dataframe. Supports fraction-based and count-based sampling.',
    icon: 'Dice5',
    color: CATEGORY_COLORS.data_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'mode', label: 'Sample Mode', type: 'select', options: [
        { value: 'fraction', label: 'Fraction (Percentage)' },
        { value: 'count', label: 'Fixed Count' },
      ], default: 'fraction', section: 'Sampling', description: 'Sample by percentage or fixed row count' },
      { key: 'fraction', label: 'Fraction', type: 'number', default: 0.1, section: 'Sampling', description: 'Fraction of rows to sample (0.0 to 1.0, e.g., 0.1 = 10%)' },
      { key: 'count', label: 'Row Count', type: 'number', default: 1000, section: 'Sampling', description: 'Number of rows to sample (when mode is Fixed Count)' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Options', description: 'Random seed for reproducible sampling (set to 0 for non-deterministic)' },
      { key: 'with_replacement', label: 'With Replacement', type: 'checkbox', default: false, section: 'Options', description: 'Allow the same row to be sampled more than once' },
    ],
    defaultConfig: { mode: 'fraction', fraction: 0.1, count: 1000, seed: 42, with_replacement: false },
  },

  'etl-prep-limit': {
    type: 'etl-prep-limit',
    category: 'data_prep',
    label: 'Limit / Top N',
    description: 'Limit the dataframe to the first N rows. Optionally order by a column to get Top N.',
    icon: 'ListEnd',
    color: CATEGORY_COLORS.data_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'limit', label: 'Row Limit', type: 'number', default: 100, section: 'Limit', description: 'Maximum number of rows to return' },
      { key: 'order_by', label: 'Order By', type: 'text', default: '', section: 'Limit', description: 'Column to sort by before limiting (leave empty for arbitrary order)' },
      { key: 'ascending', label: 'Ascending', type: 'checkbox', default: true, section: 'Limit', description: 'Sort ascending (true) or descending (false) when Order By is set' },
    ],
    defaultConfig: { limit: 100, order_by: '', ascending: true },
  },

  'etl-prep-repartition': {
    type: 'etl-prep-repartition',
    category: 'data_prep',
    label: 'Repartition / Coalesce',
    description: 'Change the number of partitions in the dataframe. Repartition shuffles data, coalesce reduces without shuffle.',
    icon: 'LayoutGrid',
    color: CATEGORY_COLORS.data_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'mode', label: 'Mode', type: 'select', options: [
        { value: 'repartition', label: 'Repartition (Full Shuffle)' },
        { value: 'coalesce', label: 'Coalesce (No Shuffle, Reduce Only)' },
      ], default: 'coalesce', section: 'Partitioning', description: 'Repartition does a full shuffle, coalesce only reduces partition count without shuffle' },
      { key: 'num_partitions', label: 'Number of Partitions', type: 'number', default: 1, section: 'Partitioning', description: 'Target number of partitions' },
      { key: 'partition_by', label: 'Partition By Columns', type: 'tags', default: [], section: 'Partitioning', description: 'Columns to hash-partition by (repartition mode only)' },
    ],
    defaultConfig: { mode: 'coalesce', num_partitions: 1, partition_by: [] },
  },

  'etl-prep-hash': {
    type: 'etl-prep-hash',
    category: 'data_prep',
    label: 'Hash / Encode',
    description: 'Generate hash values or encode columns. Supports MD5, SHA-1, SHA-256, xxHash, Base64 encode/decode, and CRC32.',
    icon: 'Hash',
    color: CATEGORY_COLORS.data_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'operations', label: 'Hash/Encode Operations', type: 'textarea', default: '[]', section: 'Operations', description: 'JSON array of operations: [{"columns": ["email"], "function": "sha256", "output": "email_hash"}, {"columns": ["name", "dob"], "function": "md5", "output": "composite_hash"}]. Functions: md5, sha1, sha256, xxhash64, crc32, base64_encode, base64_decode' },
    ],
    defaultConfig: { operations: '[]' },
  },

  'etl-prep-ebcdic': {
    type: 'etl-prep-ebcdic',
    category: 'data_prep',
    label: 'EBCDIC / Encoding',
    description: 'Convert character encoding between EBCDIC and ASCII/UTF-8. Essential for mainframe data migration. Handles packed decimal (COMP-3) conversion.',
    icon: 'FileCode',
    color: CATEGORY_COLORS.data_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'conversion', label: 'Conversion', type: 'select', options: [
        { value: 'ebcdic_to_ascii', label: 'EBCDIC to ASCII/UTF-8' },
        { value: 'ascii_to_ebcdic', label: 'ASCII/UTF-8 to EBCDIC' },
        { value: 'comp3_to_decimal', label: 'COMP-3 (Packed Decimal) to Decimal' },
        { value: 'comp_to_binary', label: 'COMP (Binary) to Integer' },
      ], default: 'ebcdic_to_ascii', section: 'Conversion', description: 'Type of encoding conversion' },
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Columns to convert (empty = all string columns)' },
      { key: 'codepage', label: 'EBCDIC Code Page', type: 'select', options: [
        { value: 'cp037', label: 'CP037 (US/Canada)' },
        { value: 'cp500', label: 'CP500 (International)' },
        { value: 'cp1047', label: 'CP1047 (Latin-1/Open Systems)' },
        { value: 'cp273', label: 'CP273 (Germany)' },
        { value: 'cp277', label: 'CP277 (Denmark/Norway)' },
        { value: 'cp285', label: 'CP285 (UK)' },
      ], default: 'cp037', section: 'Options', description: 'EBCDIC code page for the conversion' },
    ],
    defaultConfig: { conversion: 'ebcdic_to_ascii', columns: [], codepage: 'cp037' },
  },

  // ===========================================================================
  // STATS - Statistics, Data Mining & Data Science (PySpark SQL + MLlib)
  // ===========================================================================

  // ---------------------------------------------------------------------------
  // Descriptive Statistics
  // ---------------------------------------------------------------------------

  'etl-stats-summary': {
    type: 'etl-stats-summary',
    category: 'stats',
    label: 'Summary Statistics',
    description: 'Compute count, mean, stddev, min, max, and percentiles for all numeric columns. Uses df.summary() under the hood.',
    icon: 'BarChart3',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'stats', name: 'Stats Table', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Columns to analyze (empty = all numeric columns)' },
      { key: 'statistics', label: 'Statistics', type: 'multiselect', options: [
        { value: 'count', label: 'Count' },
        { value: 'mean', label: 'Mean' },
        { value: 'stddev', label: 'Std Dev' },
        { value: 'min', label: 'Min' },
        { value: 'max', label: 'Max' },
        { value: 'median', label: 'Median (P50)' },
        { value: 'p25', label: '25th Percentile' },
        { value: 'p75', label: '75th Percentile' },
        { value: 'p90', label: '90th Percentile' },
        { value: 'p95', label: '95th Percentile' },
        { value: 'p99', label: '99th Percentile' },
        { value: 'skewness', label: 'Skewness' },
        { value: 'kurtosis', label: 'Kurtosis' },
        { value: 'variance', label: 'Variance' },
        { value: 'sum', label: 'Sum' },
        { value: 'count_distinct', label: 'Count Distinct' },
      ], default: ['count', 'mean', 'stddev', 'min', 'max'], section: 'Statistics', description: 'Which statistics to compute' },
    ],
    defaultConfig: { columns: [], statistics: ['count', 'mean', 'stddev', 'min', 'max'] },
  },

  'etl-stats-percentiles': {
    type: 'etl-stats-percentiles',
    category: 'stats',
    label: 'Percentiles',
    description: 'Compute approximate percentiles (quantiles) for numeric columns. Uses Spark approxQuantile for performance on large datasets.',
    icon: 'AlignVerticalDistributeCenter',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'percentiles', name: 'Percentiles', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Numeric columns to compute percentiles for' },
      { key: 'percentiles', label: 'Percentile Values', type: 'tags', default: ['0.25', '0.5', '0.75', '0.9', '0.95', '0.99'], section: 'Options', description: 'Percentile values as decimals (0.0 to 1.0)' },
      { key: 'relative_error', label: 'Relative Error', type: 'number', default: 0.01, min: 0, max: 1, step: 0.001, section: 'Options', description: 'Approximation error tolerance (0 = exact, higher = faster). Default 0.01 (1%)' },
    ],
    defaultConfig: { columns: [], percentiles: ['0.25', '0.5', '0.75', '0.9', '0.95', '0.99'], relative_error: 0.01 },
  },

  // ---------------------------------------------------------------------------
  // Data Profiling
  // ---------------------------------------------------------------------------

  'etl-stats-profile': {
    type: 'etl-stats-profile',
    category: 'stats',
    label: 'Data Profiler',
    description: 'Full data profiling: null rates, cardinality, top N values, data types, string length stats, duplicate detection, and pattern analysis per column.',
    icon: 'ScanSearch',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'profile', name: 'Profile Report', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Columns to profile (empty = all columns)' },
      { key: 'checks', label: 'Profile Checks', type: 'multiselect', options: [
        { value: 'null_count', label: 'Null Count / Null %' },
        { value: 'cardinality', label: 'Cardinality (Unique Values)' },
        { value: 'top_n', label: 'Top N Frequent Values' },
        { value: 'empty_strings', label: 'Empty String Count' },
        { value: 'zero_count', label: 'Zero Count (Numerics)' },
        { value: 'length_stats', label: 'String Length (Min/Max/Avg)' },
        { value: 'type_inference', label: 'Actual Type Inference' },
        { value: 'pattern_detection', label: 'Pattern Detection (Email, Phone, etc.)' },
        { value: 'duplicates', label: 'Duplicate Row Detection' },
        { value: 'column_duplicates', label: 'Column-Level Duplicate %' },
      ], default: ['null_count', 'cardinality', 'top_n'], section: 'Checks', description: 'Which profiling checks to run' },
      { key: 'top_n_count', label: 'Top N Count', type: 'number', default: 10, min: 1, max: 100, section: 'Options', description: 'Number of most frequent values to report' },
      { key: 'sample_fraction', label: 'Sample Fraction', type: 'number', default: 1.0, min: 0.01, max: 1.0, step: 0.01, section: 'Performance', description: 'Fraction of data to profile (1.0 = full dataset, lower = faster)' },
    ],
    defaultConfig: { columns: [], checks: ['null_count', 'cardinality', 'top_n'], top_n_count: 10, sample_fraction: 1.0 },
  },

  'etl-stats-frequency': {
    type: 'etl-stats-frequency',
    category: 'stats',
    label: 'Frequency Table',
    description: 'Compute value frequency counts and percentages for one or more columns. Equivalent to a GROUP BY with COUNT sorted by frequency.',
    icon: 'ListOrdered',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'frequencies', name: 'Frequency Table', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Columns to compute frequency tables for' },
      { key: 'top_n', label: 'Top N', type: 'number', default: 50, min: 1, max: 1000, section: 'Options', description: 'Max number of values to include (most frequent first)' },
      { key: 'include_percentage', label: 'Include Percentage', type: 'checkbox', default: true, section: 'Options', description: 'Add percentage column alongside count' },
      { key: 'include_cumulative', label: 'Include Cumulative %', type: 'checkbox', default: false, section: 'Options', description: 'Add cumulative percentage for Pareto analysis' },
    ],
    defaultConfig: { columns: [], top_n: 50, include_percentage: true, include_cumulative: false },
  },

  // ---------------------------------------------------------------------------
  // Correlation & Relationships
  // ---------------------------------------------------------------------------

  'etl-stats-correlation': {
    type: 'etl-stats-correlation',
    category: 'stats',
    label: 'Correlation Matrix',
    description: 'Compute pairwise Pearson or Spearman correlation between numeric columns. Outputs an N x N matrix showing linear relationships.',
    icon: 'Grid3x3',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'matrix', name: 'Correlation Matrix', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Numeric columns to correlate (empty = all numeric)' },
      { key: 'method', label: 'Method', type: 'select', options: [
        { value: 'pearson', label: 'Pearson (Linear)' },
        { value: 'spearman', label: 'Spearman (Rank)' },
      ], default: 'pearson', section: 'Options', description: 'Pearson measures linear correlation, Spearman measures monotonic (rank-based) correlation' },
    ],
    defaultConfig: { columns: [], method: 'pearson' },
  },

  'etl-stats-crosstab': {
    type: 'etl-stats-crosstab',
    category: 'stats',
    label: 'Cross Tabulation',
    description: 'Create a cross-tabulation (contingency table) showing frequency of value pairs between two categorical columns. Uses df.stat.crosstab().',
    icon: 'Table2',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'crosstab', name: 'Cross Tab', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'column1', label: 'Row Column', type: 'text', default: '', section: 'Columns', description: 'Column whose values become rows in the crosstab' },
      { key: 'column2', label: 'Column Column', type: 'text', default: '', section: 'Columns', description: 'Column whose values become columns in the crosstab' },
    ],
    defaultConfig: { column1: '', column2: '' },
  },

  'etl-stats-covariance': {
    type: 'etl-stats-covariance',
    category: 'stats',
    label: 'Covariance',
    description: 'Compute pairwise sample covariance between two numeric columns. Positive = move together, negative = move opposite, zero = independent.',
    icon: 'Diff',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'result', name: 'Result', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'column1', label: 'Column 1', type: 'text', default: '', section: 'Columns', description: 'First numeric column' },
      { key: 'column2', label: 'Column 2', type: 'text', default: '', section: 'Columns', description: 'Second numeric column' },
    ],
    defaultConfig: { column1: '', column2: '' },
  },

  'etl-stats-chi-square': {
    type: 'etl-stats-chi-square',
    category: 'stats',
    label: 'Chi-Square Test',
    description: 'Perform chi-square independence test between categorical features and a label column. Tests whether two categorical variables are statistically independent.',
    icon: 'FlaskConical',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'result', name: 'Test Results', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column containing feature values (use VectorAssembler first)' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Column containing the categorical label to test against' },
      { key: 'significance_level', label: 'Significance Level', type: 'number', default: 0.05, min: 0.001, max: 0.1, step: 0.005, section: 'Options', description: 'P-value threshold for statistical significance (0.05 = 95% confidence)' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', significance_level: 0.05 },
  },

  // ---------------------------------------------------------------------------
  // Distribution Analysis
  // ---------------------------------------------------------------------------

  'etl-stats-histogram': {
    type: 'etl-stats-histogram',
    category: 'stats',
    label: 'Histogram / Binning',
    description: 'Compute frequency distribution by dividing a numeric column into equal-width bins. Outputs bin ranges and counts for distribution analysis.',
    icon: 'BarChart',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'histogram', name: 'Histogram', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'column', label: 'Column', type: 'text', default: '', section: 'Scope', description: 'Numeric column to create histogram for' },
      { key: 'num_bins', label: 'Number of Bins', type: 'number', default: 20, min: 2, max: 1000, section: 'Options', description: 'Number of equal-width bins to divide the range into' },
    ],
    defaultConfig: { column: '', num_bins: 20 },
  },

  'etl-stats-sample': {
    type: 'etl-stats-sample',
    category: 'stats',
    label: 'Statistical Sample',
    description: 'Draw a random or stratified sample from the dataset. Stratified sampling preserves the proportion of categories in a specified column.',
    icon: 'Dices',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Sample', type: 'dataframe' }],
    configSchema: [
      { key: 'method', label: 'Sampling Method', type: 'select', options: [
        { value: 'random', label: 'Random Sample' },
        { value: 'stratified', label: 'Stratified Sample' },
      ], default: 'random', section: 'Method', description: 'Random samples uniformly; Stratified preserves category proportions' },
      { key: 'fraction', label: 'Sample Fraction', type: 'number', default: 0.1, min: 0.001, max: 1.0, step: 0.001, section: 'Options', description: 'Fraction of rows to sample (0.1 = 10%)' },
      { key: 'stratify_column', label: 'Stratify Column', type: 'text', default: '', section: 'Stratified', description: 'Column to stratify by (for stratified sampling)' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Options', description: 'Seed for reproducible sampling (set to same value for same results)' },
      { key: 'with_replacement', label: 'With Replacement', type: 'checkbox', default: false, section: 'Options', description: 'Allow the same row to be sampled more than once' },
    ],
    defaultConfig: { method: 'random', fraction: 0.1, stratify_column: '', seed: 42, with_replacement: false },
  },

  'etl-stats-ks-test': {
    type: 'etl-stats-ks-test',
    category: 'stats',
    label: 'KS Test',
    description: 'Kolmogorov-Smirnov test: test whether a column follows a known distribution (normal, etc.). Returns the KS statistic and p-value.',
    icon: 'FlaskConical',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'result', name: 'Test Results', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'column', label: 'Column', type: 'text', default: '', section: 'Scope', description: 'Numeric column to test' },
      { key: 'distribution', label: 'Distribution', type: 'select', options: [
        { value: 'norm', label: 'Normal (Gaussian)' },
      ], default: 'norm', section: 'Options', description: 'Theoretical distribution to test against' },
      { key: 'significance_level', label: 'Significance Level', type: 'number', default: 0.05, min: 0.001, max: 0.1, step: 0.005, section: 'Options', description: 'P-value threshold (0.05 = 95% confidence)' },
    ],
    defaultConfig: { column: '', distribution: 'norm', significance_level: 0.05 },
  },

  // ---------------------------------------------------------------------------
  // Outlier Detection
  // ---------------------------------------------------------------------------

  'etl-stats-outlier-zscore': {
    type: 'etl-stats-outlier-zscore',
    category: 'stats',
    label: 'Outlier: Z-Score',
    description: 'Detect outliers using Z-score method. Flags rows where a column value is more than N standard deviations from the mean. Adds an outlier flag column.',
    icon: 'AlertTriangle',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'clean', name: 'Clean', type: 'dataframe' },
      { id: 'outliers', name: 'Outliers', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Numeric columns to check for outliers' },
      { key: 'threshold', label: 'Z-Score Threshold', type: 'number', default: 3.0, min: 1.0, max: 10.0, step: 0.1, section: 'Options', description: 'Number of standard deviations to flag as outlier (3.0 = 99.7% of normal data)' },
      { key: 'action', label: 'Action', type: 'select', options: [
        { value: 'flag', label: 'Flag (Add Column)' },
        { value: 'remove', label: 'Remove Outliers' },
        { value: 'cap', label: 'Cap / Winsorize' },
      ], default: 'flag', section: 'Options', description: 'What to do with detected outliers' },
    ],
    defaultConfig: { columns: [], threshold: 3.0, action: 'flag' },
  },

  'etl-stats-outlier-iqr': {
    type: 'etl-stats-outlier-iqr',
    category: 'stats',
    label: 'Outlier: IQR',
    description: 'Detect outliers using Interquartile Range method. Values outside Q1 - 1.5*IQR to Q3 + 1.5*IQR are flagged. More robust than Z-score for skewed data.',
    icon: 'AlertTriangle',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'clean', name: 'Clean', type: 'dataframe' },
      { id: 'outliers', name: 'Outliers', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'columns', label: 'Columns', type: 'tags', default: [], section: 'Scope', description: 'Numeric columns to check for outliers' },
      { key: 'multiplier', label: 'IQR Multiplier', type: 'number', default: 1.5, min: 0.5, max: 5.0, step: 0.1, section: 'Options', description: 'Multiplier for IQR range (1.5 = standard, 3.0 = far outliers only)' },
      { key: 'action', label: 'Action', type: 'select', options: [
        { value: 'flag', label: 'Flag (Add Column)' },
        { value: 'remove', label: 'Remove Outliers' },
        { value: 'cap', label: 'Cap to Boundaries' },
      ], default: 'flag', section: 'Options', description: 'What to do with detected outliers' },
    ],
    defaultConfig: { columns: [], multiplier: 1.5, action: 'flag' },
  },

  // ---------------------------------------------------------------------------
  // Time Series
  // ---------------------------------------------------------------------------

  'etl-stats-moving-avg': {
    type: 'etl-stats-moving-avg',
    category: 'stats',
    label: 'Moving Average',
    description: 'Compute simple or exponential moving average over a window of rows. Uses Spark window functions. Requires data to be ordered by a time/sequence column.',
    icon: 'TrendingUp',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'value_column', label: 'Value Column', type: 'text', default: '', section: 'Columns', description: 'Numeric column to compute moving average on' },
      { key: 'order_column', label: 'Order Column', type: 'text', default: '', section: 'Columns', description: 'Column to order by (date, timestamp, sequence)' },
      { key: 'partition_columns', label: 'Partition By', type: 'tags', default: [], section: 'Columns', description: 'Columns to partition by (compute separate averages per group)' },
      { key: 'window_size', label: 'Window Size', type: 'number', default: 7, min: 2, max: 365, section: 'Window', description: 'Number of rows in the moving window' },
      { key: 'method', label: 'Method', type: 'select', options: [
        { value: 'simple', label: 'Simple Moving Average (SMA)' },
        { value: 'exponential', label: 'Exponential Moving Average (EMA)' },
      ], default: 'simple', section: 'Window', description: 'SMA weights all rows equally; EMA gives more weight to recent rows' },
      { key: 'output_column', label: 'Output Column', type: 'text', default: 'moving_avg', section: 'Output', description: 'Name of the new column containing the moving average' },
    ],
    defaultConfig: { value_column: '', order_column: '', partition_columns: [], window_size: 7, method: 'simple', output_column: 'moving_avg' },
  },

  'etl-stats-lag-lead': {
    type: 'etl-stats-lag-lead',
    category: 'stats',
    label: 'Lag / Lead',
    description: 'Add columns with previous row (lag) or next row (lead) values. Essential for computing period-over-period changes, growth rates, and trend detection.',
    icon: 'ArrowUpDown',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'value_column', label: 'Value Column', type: 'text', default: '', section: 'Columns', description: 'Column to fetch previous/next values from' },
      { key: 'order_column', label: 'Order Column', type: 'text', default: '', section: 'Columns', description: 'Column to order by (date, timestamp, sequence)' },
      { key: 'partition_columns', label: 'Partition By', type: 'tags', default: [], section: 'Columns', description: 'Columns to partition by (compute separate lag/lead per group)' },
      { key: 'direction', label: 'Direction', type: 'select', options: [
        { value: 'lag', label: 'Lag (Previous Rows)' },
        { value: 'lead', label: 'Lead (Next Rows)' },
        { value: 'both', label: 'Both Lag and Lead' },
      ], default: 'lag', section: 'Options', description: 'Whether to look backward (lag), forward (lead), or both' },
      { key: 'offset', label: 'Offset', type: 'number', default: 1, min: 1, max: 365, section: 'Options', description: 'Number of rows to look back/forward' },
      { key: 'compute_change', label: 'Compute Change', type: 'checkbox', default: true, section: 'Options', description: 'Add a column with the difference (current - lag) and percentage change' },
    ],
    defaultConfig: { value_column: '', order_column: '', partition_columns: [], direction: 'lag', offset: 1, compute_change: true },
  },

  'etl-stats-rolling-window': {
    type: 'etl-stats-rolling-window',
    category: 'stats',
    label: 'Rolling Window',
    description: 'Compute rolling (sliding window) aggregations: min, max, sum, avg, count over a configurable window. Uses Spark window functions.',
    icon: 'Rows4',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'value_column', label: 'Value Column', type: 'text', default: '', section: 'Columns', description: 'Numeric column to aggregate over the window' },
      { key: 'order_column', label: 'Order Column', type: 'text', default: '', section: 'Columns', description: 'Column to order by (date, timestamp, sequence)' },
      { key: 'partition_columns', label: 'Partition By', type: 'tags', default: [], section: 'Columns', description: 'Columns to partition by (separate window per group)' },
      { key: 'window_size', label: 'Window Size', type: 'number', default: 7, min: 2, max: 365, section: 'Window', description: 'Number of rows in the rolling window' },
      { key: 'functions', label: 'Window Functions', type: 'multiselect', options: [
        { value: 'sum', label: 'Rolling Sum' },
        { value: 'avg', label: 'Rolling Avg' },
        { value: 'min', label: 'Rolling Min' },
        { value: 'max', label: 'Rolling Max' },
        { value: 'count', label: 'Rolling Count' },
        { value: 'stddev', label: 'Rolling Std Dev' },
      ], default: ['sum', 'avg'], section: 'Functions', description: 'Aggregation functions to apply over the window' },
    ],
    defaultConfig: { value_column: '', order_column: '', partition_columns: [], window_size: 7, functions: ['sum', 'avg'] },
  },

  'etl-stats-cumulative': {
    type: 'etl-stats-cumulative',
    category: 'stats',
    label: 'Cumulative Sum',
    description: 'Compute running cumulative sum, count, min, or max from the first row to the current row. Uses unbounded preceding window.',
    icon: 'TrendingUp',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'value_column', label: 'Value Column', type: 'text', default: '', section: 'Columns', description: 'Numeric column to accumulate' },
      { key: 'order_column', label: 'Order Column', type: 'text', default: '', section: 'Columns', description: 'Column to order by (date, timestamp, sequence)' },
      { key: 'partition_columns', label: 'Partition By', type: 'tags', default: [], section: 'Columns', description: 'Reset cumulative computation per group' },
      { key: 'function', label: 'Function', type: 'select', options: [
        { value: 'sum', label: 'Cumulative Sum' },
        { value: 'count', label: 'Cumulative Count' },
        { value: 'min', label: 'Running Min' },
        { value: 'max', label: 'Running Max' },
        { value: 'avg', label: 'Running Avg' },
      ], default: 'sum', section: 'Options', description: 'Cumulative function to apply' },
      { key: 'output_column', label: 'Output Column', type: 'text', default: 'cumulative', section: 'Output', description: 'Name of the new cumulative column' },
    ],
    defaultConfig: { value_column: '', order_column: '', partition_columns: [], function: 'sum', output_column: 'cumulative' },
  },

  // ---------------------------------------------------------------------------
  // Clustering
  // ---------------------------------------------------------------------------

  'etl-stats-kmeans': {
    type: 'etl-stats-kmeans',
    category: 'stats',
    label: 'K-Means Clustering',
    description: 'Partition data into K clusters using K-Means algorithm. Adds a cluster assignment column. Requires numeric features assembled into a vector column.',
    icon: 'CircleDot',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Clustered', type: 'dataframe' }],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features (use VectorAssembler first)' },
      { key: 'k', label: 'Number of Clusters (K)', type: 'number', default: 3, min: 2, max: 100, section: 'Algorithm', description: 'Number of clusters to create' },
      { key: 'max_iter', label: 'Max Iterations', type: 'number', default: 20, min: 1, max: 100, section: 'Algorithm', description: 'Maximum iterations for convergence' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Algorithm', description: 'Seed for reproducible results' },
      { key: 'prediction_column', label: 'Output Column', type: 'text', default: 'cluster', section: 'Output', description: 'Column name for cluster assignments' },
      { key: 'compute_silhouette', label: 'Compute Silhouette Score', type: 'checkbox', default: true, section: 'Evaluation', description: 'Evaluate cluster quality (-1 to 1, higher = better separation)' },
    ],
    defaultConfig: { features_column: 'features', k: 3, max_iter: 20, seed: 42, prediction_column: 'cluster', compute_silhouette: true },
  },

  'etl-stats-bisecting-kmeans': {
    type: 'etl-stats-bisecting-kmeans',
    category: 'stats',
    label: 'Bisecting K-Means',
    description: 'Hierarchical K-Means that recursively splits the largest cluster. Often faster than K-Means and produces a hierarchical cluster structure.',
    icon: 'GitBranch',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Clustered', type: 'dataframe' }],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'k', label: 'Number of Clusters (K)', type: 'number', default: 4, min: 2, max: 100, section: 'Algorithm', description: 'Number of leaf clusters' },
      { key: 'max_iter', label: 'Max Iterations', type: 'number', default: 20, min: 1, max: 100, section: 'Algorithm', description: 'Max iterations per bisecting step' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Algorithm', description: 'Seed for reproducible results' },
      { key: 'prediction_column', label: 'Output Column', type: 'text', default: 'cluster', section: 'Output', description: 'Column name for cluster assignments' },
    ],
    defaultConfig: { features_column: 'features', k: 4, max_iter: 20, seed: 42, prediction_column: 'cluster' },
  },

  'etl-stats-gmm': {
    type: 'etl-stats-gmm',
    category: 'stats',
    label: 'Gaussian Mixture',
    description: 'Soft clustering using Gaussian Mixture Model. Unlike K-Means, each row gets a probability of belonging to each cluster. Good for overlapping clusters.',
    icon: 'Eclipse',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Clustered', type: 'dataframe' }],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'k', label: 'Number of Components (K)', type: 'number', default: 3, min: 2, max: 50, section: 'Algorithm', description: 'Number of Gaussian components (clusters)' },
      { key: 'max_iter', label: 'Max Iterations', type: 'number', default: 100, min: 1, max: 500, section: 'Algorithm', description: 'Max EM iterations for convergence' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Algorithm', description: 'Seed for reproducible results' },
      { key: 'prediction_column', label: 'Output Column', type: 'text', default: 'cluster', section: 'Output', description: 'Column name for cluster assignments' },
      { key: 'probability_column', label: 'Probability Column', type: 'text', default: 'cluster_probability', section: 'Output', description: 'Column for per-cluster probability vector' },
    ],
    defaultConfig: { features_column: 'features', k: 3, max_iter: 100, seed: 42, prediction_column: 'cluster', probability_column: 'cluster_probability' },
  },

  // ---------------------------------------------------------------------------
  // Classification
  // ---------------------------------------------------------------------------

  'etl-stats-logistic-regression': {
    type: 'etl-stats-logistic-regression',
    category: 'stats',
    label: 'Logistic Regression',
    description: 'Binary or multiclass classification using logistic regression. Adds prediction and probability columns. Requires features vector and label column.',
    icon: 'Binary',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Predictions', type: 'dataframe' },
      { id: 'metrics', name: 'Metrics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Column containing the target class labels' },
      { key: 'max_iter', label: 'Max Iterations', type: 'number', default: 100, min: 1, max: 1000, section: 'Algorithm', description: 'Maximum training iterations' },
      { key: 'reg_param', label: 'Regularization', type: 'number', default: 0.0, min: 0, max: 10, step: 0.01, section: 'Algorithm', description: 'L2 regularization parameter (0 = none, higher = more)' },
      { key: 'train_ratio', label: 'Train Ratio', type: 'number', default: 0.8, min: 0.5, max: 0.95, step: 0.05, section: 'Training', description: 'Fraction of data used for training (rest for evaluation)' },
      { key: 'prediction_column', label: 'Prediction Column', type: 'text', default: 'prediction', section: 'Output', description: 'Column name for predicted labels' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', max_iter: 100, reg_param: 0.0, train_ratio: 0.8, prediction_column: 'prediction' },
  },

  'etl-stats-random-forest-classifier': {
    type: 'etl-stats-random-forest-classifier',
    category: 'stats',
    label: 'Random Forest (Classify)',
    description: 'Ensemble classification using random forest. Trains multiple decision trees and votes for the majority class. Generally higher accuracy than a single tree.',
    icon: 'TreePine',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Predictions', type: 'dataframe' },
      { id: 'metrics', name: 'Metrics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Column containing the target class labels' },
      { key: 'num_trees', label: 'Number of Trees', type: 'number', default: 100, min: 1, max: 500, section: 'Algorithm', description: 'Number of trees in the forest' },
      { key: 'max_depth', label: 'Max Depth', type: 'number', default: 5, min: 1, max: 30, section: 'Algorithm', description: 'Maximum tree depth (deeper = more complex, risk of overfitting)' },
      { key: 'train_ratio', label: 'Train Ratio', type: 'number', default: 0.8, min: 0.5, max: 0.95, step: 0.05, section: 'Training', description: 'Fraction of data used for training' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Algorithm', description: 'Seed for reproducible results' },
      { key: 'prediction_column', label: 'Prediction Column', type: 'text', default: 'prediction', section: 'Output', description: 'Column name for predicted labels' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', num_trees: 100, max_depth: 5, train_ratio: 0.8, seed: 42, prediction_column: 'prediction' },
  },

  'etl-stats-gbt-classifier': {
    type: 'etl-stats-gbt-classifier',
    category: 'stats',
    label: 'Gradient Boosted Trees (Classify)',
    description: 'Gradient-Boosted Tree classifier. Sequentially builds trees that correct errors of previous trees. Best accuracy for tabular data (binary classification).',
    icon: 'TreePine',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Predictions', type: 'dataframe' },
      { id: 'metrics', name: 'Metrics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Column containing the target class labels (binary)' },
      { key: 'max_iter', label: 'Max Iterations', type: 'number', default: 20, min: 1, max: 200, section: 'Algorithm', description: 'Number of boosting rounds (trees)' },
      { key: 'max_depth', label: 'Max Depth', type: 'number', default: 5, min: 1, max: 30, section: 'Algorithm', description: 'Maximum depth per tree' },
      { key: 'step_size', label: 'Learning Rate', type: 'number', default: 0.1, min: 0.01, max: 1.0, step: 0.01, section: 'Algorithm', description: 'Shrinkage per iteration (lower = slower but more robust)' },
      { key: 'train_ratio', label: 'Train Ratio', type: 'number', default: 0.8, min: 0.5, max: 0.95, step: 0.05, section: 'Training', description: 'Fraction of data used for training' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Algorithm', description: 'Seed for reproducible results' },
      { key: 'prediction_column', label: 'Prediction Column', type: 'text', default: 'prediction', section: 'Output', description: 'Column name for predicted labels' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', max_iter: 20, max_depth: 5, step_size: 0.1, train_ratio: 0.8, seed: 42, prediction_column: 'prediction' },
  },

  'etl-stats-decision-tree': {
    type: 'etl-stats-decision-tree',
    category: 'stats',
    label: 'Decision Tree (Classify)',
    description: 'Single decision tree classifier. Produces interpretable if-then rules. Lower accuracy than ensembles but easier to understand and explain.',
    icon: 'GitBranch',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Predictions', type: 'dataframe' },
      { id: 'metrics', name: 'Metrics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Column containing the target class labels' },
      { key: 'max_depth', label: 'Max Depth', type: 'number', default: 5, min: 1, max: 30, section: 'Algorithm', description: 'Maximum tree depth' },
      { key: 'train_ratio', label: 'Train Ratio', type: 'number', default: 0.8, min: 0.5, max: 0.95, step: 0.05, section: 'Training', description: 'Fraction of data used for training' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Algorithm', description: 'Seed for reproducible results' },
      { key: 'prediction_column', label: 'Prediction Column', type: 'text', default: 'prediction', section: 'Output', description: 'Column name for predicted labels' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', max_depth: 5, train_ratio: 0.8, seed: 42, prediction_column: 'prediction' },
  },

  'etl-stats-naive-bayes': {
    type: 'etl-stats-naive-bayes',
    category: 'stats',
    label: 'Naive Bayes',
    description: 'Probabilistic classifier based on Bayes theorem. Fast, works well for text classification and high-dimensional data. Assumes feature independence.',
    icon: 'Scale',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Predictions', type: 'dataframe' },
      { id: 'metrics', name: 'Metrics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Column containing the target class labels' },
      { key: 'model_type', label: 'Model Type', type: 'select', options: [
        { value: 'multinomial', label: 'Multinomial (Count-Based)' },
        { value: 'complement', label: 'Complement (Imbalanced Data)' },
        { value: 'bernoulli', label: 'Bernoulli (Binary Features)' },
        { value: 'gaussian', label: 'Gaussian (Continuous Features)' },
      ], default: 'multinomial', section: 'Algorithm', description: 'Type of Naive Bayes model' },
      { key: 'smoothing', label: 'Smoothing', type: 'number', default: 1.0, min: 0, max: 10, step: 0.1, section: 'Algorithm', description: 'Additive smoothing parameter (Laplace smoothing)' },
      { key: 'train_ratio', label: 'Train Ratio', type: 'number', default: 0.8, min: 0.5, max: 0.95, step: 0.05, section: 'Training', description: 'Fraction of data used for training' },
      { key: 'prediction_column', label: 'Prediction Column', type: 'text', default: 'prediction', section: 'Output', description: 'Column name for predicted labels' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', model_type: 'multinomial', smoothing: 1.0, train_ratio: 0.8, prediction_column: 'prediction' },
  },

  // ---------------------------------------------------------------------------
  // Regression
  // ---------------------------------------------------------------------------

  'etl-stats-linear-regression': {
    type: 'etl-stats-linear-regression',
    category: 'stats',
    label: 'Linear Regression',
    description: 'Predict continuous values from features using ordinary least squares linear regression. Outputs prediction column and evaluation metrics (RMSE, R-squared).',
    icon: 'TrendingUp',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Predictions', type: 'dataframe' },
      { id: 'metrics', name: 'Metrics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Numeric column to predict' },
      { key: 'max_iter', label: 'Max Iterations', type: 'number', default: 100, min: 1, max: 1000, section: 'Algorithm', description: 'Maximum training iterations' },
      { key: 'reg_param', label: 'Regularization', type: 'number', default: 0.0, min: 0, max: 10, step: 0.01, section: 'Algorithm', description: 'Regularization parameter (0 = none)' },
      { key: 'elastic_net', label: 'Elastic Net Mixing', type: 'number', default: 0.0, min: 0, max: 1, step: 0.1, section: 'Algorithm', description: '0 = L2 (Ridge), 1 = L1 (Lasso), between = Elastic Net' },
      { key: 'train_ratio', label: 'Train Ratio', type: 'number', default: 0.8, min: 0.5, max: 0.95, step: 0.05, section: 'Training', description: 'Fraction of data used for training' },
      { key: 'prediction_column', label: 'Prediction Column', type: 'text', default: 'prediction', section: 'Output', description: 'Column name for predicted values' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', max_iter: 100, reg_param: 0.0, elastic_net: 0.0, train_ratio: 0.8, prediction_column: 'prediction' },
  },

  'etl-stats-random-forest-regressor': {
    type: 'etl-stats-random-forest-regressor',
    category: 'stats',
    label: 'Random Forest (Regress)',
    description: 'Ensemble regression using random forest. Trains multiple decision trees and averages their predictions. Handles non-linear relationships well.',
    icon: 'TreePine',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Predictions', type: 'dataframe' },
      { id: 'metrics', name: 'Metrics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Numeric column to predict' },
      { key: 'num_trees', label: 'Number of Trees', type: 'number', default: 100, min: 1, max: 500, section: 'Algorithm', description: 'Number of trees in the forest' },
      { key: 'max_depth', label: 'Max Depth', type: 'number', default: 5, min: 1, max: 30, section: 'Algorithm', description: 'Maximum tree depth' },
      { key: 'train_ratio', label: 'Train Ratio', type: 'number', default: 0.8, min: 0.5, max: 0.95, step: 0.05, section: 'Training', description: 'Fraction of data used for training' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Algorithm', description: 'Seed for reproducible results' },
      { key: 'prediction_column', label: 'Prediction Column', type: 'text', default: 'prediction', section: 'Output', description: 'Column name for predicted values' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', num_trees: 100, max_depth: 5, train_ratio: 0.8, seed: 42, prediction_column: 'prediction' },
  },

  'etl-stats-gbt-regressor': {
    type: 'etl-stats-gbt-regressor',
    category: 'stats',
    label: 'Gradient Boosted Trees (Regress)',
    description: 'Gradient-Boosted Tree regressor. Best accuracy for tabular regression tasks. Sequentially builds trees correcting previous errors.',
    icon: 'TreePine',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Predictions', type: 'dataframe' },
      { id: 'metrics', name: 'Metrics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Numeric column to predict' },
      { key: 'max_iter', label: 'Max Iterations', type: 'number', default: 20, min: 1, max: 200, section: 'Algorithm', description: 'Number of boosting rounds' },
      { key: 'max_depth', label: 'Max Depth', type: 'number', default: 5, min: 1, max: 30, section: 'Algorithm', description: 'Maximum depth per tree' },
      { key: 'step_size', label: 'Learning Rate', type: 'number', default: 0.1, min: 0.01, max: 1.0, step: 0.01, section: 'Algorithm', description: 'Shrinkage per iteration' },
      { key: 'train_ratio', label: 'Train Ratio', type: 'number', default: 0.8, min: 0.5, max: 0.95, step: 0.05, section: 'Training', description: 'Fraction of data used for training' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Algorithm', description: 'Seed for reproducible results' },
      { key: 'prediction_column', label: 'Prediction Column', type: 'text', default: 'prediction', section: 'Output', description: 'Column name for predicted values' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', max_iter: 20, max_depth: 5, step_size: 0.1, train_ratio: 0.8, seed: 42, prediction_column: 'prediction' },
  },

  // ---------------------------------------------------------------------------
  // Association Rules & Frequent Patterns
  // ---------------------------------------------------------------------------

  'etl-stats-fp-growth': {
    type: 'etl-stats-fp-growth',
    category: 'stats',
    label: 'FP-Growth (Association)',
    description: 'Frequent pattern mining and association rules. Finds items that frequently appear together (market basket analysis). Outputs frequent itemsets and if-then rules with confidence/lift.',
    icon: 'Network',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'itemsets', name: 'Frequent Itemsets', type: 'dataframe' },
      { id: 'rules', name: 'Association Rules', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'items_column', label: 'Items Column', type: 'text', default: 'items', section: 'Columns', description: 'Column containing arrays of items (e.g., product IDs per transaction)' },
      { key: 'min_support', label: 'Min Support', type: 'number', default: 0.01, min: 0.001, max: 1.0, step: 0.001, section: 'Algorithm', description: 'Minimum fraction of transactions an itemset must appear in (0.01 = 1%)' },
      { key: 'min_confidence', label: 'Min Confidence', type: 'number', default: 0.5, min: 0.01, max: 1.0, step: 0.01, section: 'Algorithm', description: 'Minimum confidence for association rules (0.5 = rule is correct 50% of the time)' },
    ],
    defaultConfig: { items_column: 'items', min_support: 0.01, min_confidence: 0.5 },
  },

  // ---------------------------------------------------------------------------
  // Dimensionality Reduction
  // ---------------------------------------------------------------------------

  'etl-stats-pca': {
    type: 'etl-stats-pca',
    category: 'stats',
    label: 'PCA (Stats)',
    description: 'Principal Component Analysis for dimensionality reduction. Reduces high-dimensional feature vectors to top K components preserving maximum variance.',
    icon: 'Minimize2',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Reduced', type: 'dataframe' }],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features to reduce' },
      { key: 'k', label: 'Components (K)', type: 'number', default: 3, min: 1, max: 100, section: 'Algorithm', description: 'Number of principal components to keep' },
      { key: 'output_column', label: 'Output Column', type: 'text', default: 'pca_features', section: 'Output', description: 'Column name for the reduced feature vector' },
    ],
    defaultConfig: { features_column: 'features', k: 3, output_column: 'pca_features' },
  },

  'etl-stats-chi-sq-selector': {
    type: 'etl-stats-chi-sq-selector',
    category: 'stats',
    label: 'Feature Selection (Chi-Sq)',
    description: 'Select top K features by chi-squared independence test against a label column. Reduces dimensionality by keeping only the most predictive features.',
    icon: 'Filter',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Selected', type: 'dataframe' }],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of numeric features' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Column containing the target labels' },
      { key: 'num_top_features', label: 'Top Features', type: 'number', default: 10, min: 1, max: 500, section: 'Algorithm', description: 'Number of top features to select' },
      { key: 'output_column', label: 'Output Column', type: 'text', default: 'selected_features', section: 'Output', description: 'Column name for the selected feature vector' },
    ],
    defaultConfig: { features_column: 'features', label_column: 'label', num_top_features: 10, output_column: 'selected_features' },
  },

  // ---------------------------------------------------------------------------
  // Model Evaluation
  // ---------------------------------------------------------------------------

  'etl-stats-evaluator': {
    type: 'etl-stats-evaluator',
    category: 'stats',
    label: 'Model Evaluator',
    description: 'Evaluate model predictions against actual labels. Computes accuracy, precision, recall, F1, AUC-ROC for classification or RMSE, MAE, R-squared for regression.',
    icon: 'ClipboardCheck',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'Pass-Through', type: 'dataframe' },
      { id: 'metrics', name: 'Evaluation Metrics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'task_type', label: 'Task Type', type: 'select', options: [
        { value: 'binary_classification', label: 'Binary Classification' },
        { value: 'multiclass_classification', label: 'Multiclass Classification' },
        { value: 'regression', label: 'Regression' },
      ], default: 'binary_classification', section: 'Task', description: 'Type of ML task to evaluate' },
      { key: 'prediction_column', label: 'Prediction Column', type: 'text', default: 'prediction', section: 'Columns', description: 'Column containing model predictions' },
      { key: 'label_column', label: 'Label Column', type: 'text', default: 'label', section: 'Columns', description: 'Column containing actual (true) labels' },
      { key: 'probability_column', label: 'Probability Column', type: 'text', default: 'probability', section: 'Columns', description: 'Column containing prediction probabilities (for AUC-ROC)' },
    ],
    defaultConfig: { task_type: 'binary_classification', prediction_column: 'prediction', label_column: 'label', probability_column: 'probability' },
  },

  'etl-stats-train-test-split': {
    type: 'etl-stats-train-test-split',
    category: 'stats',
    label: 'Train / Test Split',
    description: 'Split data into training and testing sets using randomSplit. Essential for model training and evaluation to avoid overfitting.',
    icon: 'Split',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'train', name: 'Training Set', type: 'dataframe' },
      { id: 'test', name: 'Test Set', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'train_ratio', label: 'Train Ratio', type: 'number', default: 0.8, min: 0.5, max: 0.95, step: 0.05, section: 'Options', description: 'Fraction for training (0.8 = 80% train, 20% test)' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Options', description: 'Seed for reproducible splits' },
    ],
    defaultConfig: { train_ratio: 0.8, seed: 42 },
  },

  // ---------------------------------------------------------------------------
  // Topic Modeling & Text Analytics
  // ---------------------------------------------------------------------------

  'etl-stats-lda': {
    type: 'etl-stats-lda',
    category: 'stats',
    label: 'LDA (Topic Modeling)',
    description: 'Latent Dirichlet Allocation for topic modeling. Discovers hidden topics in text data. Input should be a vector of word counts (use Tokenizer + CountVectorizer first).',
    icon: 'MessageSquareText',
    color: CATEGORY_COLORS.stats,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [
      { id: 'output', name: 'With Topics', type: 'dataframe' },
      { id: 'topics', name: 'Topics', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'features_column', label: 'Features Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column of word counts or TF-IDF values' },
      { key: 'k', label: 'Number of Topics', type: 'number', default: 10, min: 2, max: 200, section: 'Algorithm', description: 'Number of topics to discover' },
      { key: 'max_iter', label: 'Max Iterations', type: 'number', default: 20, min: 1, max: 200, section: 'Algorithm', description: 'Max EM iterations for convergence' },
      { key: 'seed', label: 'Random Seed', type: 'number', default: 42, section: 'Algorithm', description: 'Seed for reproducible results' },
    ],
    defaultConfig: { features_column: 'features', k: 10, max_iter: 20, seed: 42 },
  },

  // ===========================================================================
  // ML PREP - Machine learning preprocessing (Spark MLlib)
  // ===========================================================================

  'etl-ml-string-indexer': {
    type: 'etl-ml-string-indexer',
    category: 'ml_prep',
    label: 'StringIndexer',
    description: 'Encode categorical string columns into numeric indices. Maps each unique string to a number (most frequent = 0). Part of spark.ml feature engineering.',
    icon: 'ListOrdered',
    color: CATEGORY_COLORS.ml_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'input_columns', label: 'Input Columns', type: 'tags', default: [], section: 'Columns', description: 'Categorical columns to index' },
      { key: 'output_suffix', label: 'Output Suffix', type: 'text', default: '_index', section: 'Columns', description: 'Suffix appended to create output column names (e.g., color → color_index)' },
      { key: 'handle_invalid', label: 'Handle Invalid', type: 'select', options: [
        { value: 'error', label: 'Error (Fail on Unknown)' },
        { value: 'keep', label: 'Keep (Assign Special Index)' },
        { value: 'skip', label: 'Skip (Drop Row)' },
      ], default: 'keep', section: 'Options', description: 'How to handle unseen labels during transform' },
    ],
    defaultConfig: { input_columns: [], output_suffix: '_index', handle_invalid: 'keep' },
  },

  'etl-ml-one-hot': {
    type: 'etl-ml-one-hot',
    category: 'ml_prep',
    label: 'OneHotEncoder',
    description: 'Convert numeric index columns into binary (one-hot) vector columns. Typically used after StringIndexer. Produces sparse vectors.',
    icon: 'ToggleLeft',
    color: CATEGORY_COLORS.ml_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'input_columns', label: 'Input Columns (Indexed)', type: 'tags', default: [], section: 'Columns', description: 'Indexed columns to one-hot encode (output of StringIndexer)' },
      { key: 'output_suffix', label: 'Output Suffix', type: 'text', default: '_onehot', section: 'Columns', description: 'Suffix for output column names' },
      { key: 'drop_last', label: 'Drop Last Category', type: 'checkbox', default: true, section: 'Options', description: 'Drop the last category to avoid multicollinearity (n-1 encoding)' },
    ],
    defaultConfig: { input_columns: [], output_suffix: '_onehot', drop_last: true },
  },

  'etl-ml-vector-assembler': {
    type: 'etl-ml-vector-assembler',
    category: 'ml_prep',
    label: 'VectorAssembler',
    description: 'Combine multiple numeric columns into a single feature vector column. Required input format for most Spark ML algorithms.',
    icon: 'Combine',
    color: CATEGORY_COLORS.ml_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'input_columns', label: 'Feature Columns', type: 'tags', default: [], section: 'Columns', description: 'Numeric columns to combine into a feature vector' },
      { key: 'output_column', label: 'Output Column', type: 'text', default: 'features', section: 'Columns', description: 'Name of the output vector column' },
      { key: 'handle_invalid', label: 'Handle Invalid', type: 'select', options: [
        { value: 'error', label: 'Error' }, { value: 'keep', label: 'Keep (NaN in Vector)' }, { value: 'skip', label: 'Skip Row' },
      ], default: 'error', section: 'Options', description: 'How to handle null or NaN values in input columns' },
    ],
    defaultConfig: { input_columns: [], output_column: 'features', handle_invalid: 'error' },
  },

  'etl-ml-scaler': {
    type: 'etl-ml-scaler',
    category: 'ml_prep',
    label: 'Scaler',
    description: 'Scale numeric feature vectors using StandardScaler (zero mean, unit variance), MinMaxScaler (0-1 range), or MaxAbsScaler (-1 to 1).',
    icon: 'Scaling',
    color: CATEGORY_COLORS.ml_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'scaler_type', label: 'Scaler Type', type: 'select', options: [
        { value: 'standard', label: 'StandardScaler (Z-Score)' },
        { value: 'minmax', label: 'MinMaxScaler (0 to 1)' },
        { value: 'maxabs', label: 'MaxAbsScaler (-1 to 1)' },
      ], default: 'standard', section: 'Scaler', description: 'Scaling algorithm to use' },
      { key: 'input_column', label: 'Input Column', type: 'text', default: 'features', section: 'Columns', description: 'Vector column to scale (typically output of VectorAssembler)' },
      { key: 'output_column', label: 'Output Column', type: 'text', default: 'scaled_features', section: 'Columns', description: 'Name for the scaled vector column' },
      { key: 'with_mean', label: 'Center (Subtract Mean)', type: 'checkbox', default: true, section: 'Options', description: 'Center data by subtracting mean (StandardScaler only)' },
      { key: 'with_std', label: 'Scale (Divide by StdDev)', type: 'checkbox', default: true, section: 'Options', description: 'Scale by standard deviation (StandardScaler only)' },
    ],
    defaultConfig: { scaler_type: 'standard', input_column: 'features', output_column: 'scaled_features', with_mean: true, with_std: true },
  },

  'etl-ml-bucketizer': {
    type: 'etl-ml-bucketizer',
    category: 'ml_prep',
    label: 'Bucketizer',
    description: 'Bin continuous numeric values into discrete buckets (bins). Define split points to create value ranges.',
    icon: 'SplitSquareHorizontal',
    color: CATEGORY_COLORS.ml_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'input_column', label: 'Input Column', type: 'text', default: '', section: 'Columns', description: 'Numeric column to bucketize' },
      { key: 'output_column', label: 'Output Column', type: 'text', default: '', section: 'Columns', description: 'Name for the bucket index column' },
      { key: 'splits', label: 'Split Points', type: 'text', default: '', section: 'Buckets', description: 'Comma-separated split points defining bucket boundaries (e.g., -Infinity,0,18,35,65,Infinity for age groups). Must be sorted and have at least 3 values.' },
    ],
    defaultConfig: { input_column: '', output_column: '', splits: '' },
  },

  'etl-ml-tokenizer': {
    type: 'etl-ml-tokenizer',
    category: 'ml_prep',
    label: 'Tokenizer',
    description: 'Split text into individual tokens (words). Supports simple whitespace tokenization and regex-based tokenization.',
    icon: 'TextCursorInput',
    color: CATEGORY_COLORS.ml_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'input_column', label: 'Input Column', type: 'text', default: '', section: 'Columns', description: 'Text column to tokenize' },
      { key: 'output_column', label: 'Output Column', type: 'text', default: '', section: 'Columns', description: 'Name for the token array column' },
      { key: 'mode', label: 'Tokenizer Mode', type: 'select', options: [
        { value: 'simple', label: 'Simple (Whitespace + Lowercase)' },
        { value: 'regex', label: 'Regex Pattern' },
      ], default: 'simple', section: 'Options', description: 'Simple splits on whitespace and lowercases. Regex uses a custom pattern.' },
      { key: 'pattern', label: 'Regex Pattern', type: 'text', default: '\\s+', section: 'Options', description: 'Regex pattern for splitting (regex mode only)' },
      { key: 'to_lowercase', label: 'To Lowercase', type: 'checkbox', default: true, section: 'Options', description: 'Convert tokens to lowercase' },
    ],
    defaultConfig: { input_column: '', output_column: '', mode: 'simple', pattern: '\\s+', to_lowercase: true },
  },

  'etl-ml-imputer': {
    type: 'etl-ml-imputer',
    category: 'ml_prep',
    label: 'Imputer',
    description: 'Fill missing values using statistical strategies from spark.ml. Computes statistics on the training data and applies them consistently. More robust than simple fillna for ML pipelines.',
    icon: 'Sparkles',
    color: CATEGORY_COLORS.ml_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'input_columns', label: 'Input Columns', type: 'tags', default: [], section: 'Columns', description: 'Numeric columns with missing values to impute' },
      { key: 'output_suffix', label: 'Output Suffix', type: 'text', default: '_imputed', section: 'Columns', description: 'Suffix for output column names (leave empty to overwrite input columns)' },
      { key: 'strategy', label: 'Strategy', type: 'select', options: [
        { value: 'mean', label: 'Mean' },
        { value: 'median', label: 'Median' },
        { value: 'mode', label: 'Mode (Most Frequent)' },
      ], default: 'mean', section: 'Options', description: 'Statistical strategy for computing fill values' },
      { key: 'missing_value', label: 'Missing Value Marker', type: 'text', default: 'NaN', section: 'Options', description: 'Value that represents missing data (default: NaN). Use a number for custom missing markers.' },
    ],
    defaultConfig: { input_columns: [], output_suffix: '_imputed', strategy: 'mean', missing_value: 'NaN' },
  },

  'etl-ml-pca': {
    type: 'etl-ml-pca',
    category: 'ml_prep',
    label: 'PCA',
    description: 'Principal Component Analysis for dimensionality reduction. Reduces a high-dimensional feature vector to fewer components while preserving variance.',
    icon: 'Shrink',
    color: CATEGORY_COLORS.ml_prep,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [{ id: 'output', name: 'Output', type: 'dataframe' }],
    configSchema: [
      { key: 'input_column', label: 'Input Column', type: 'text', default: 'features', section: 'Columns', description: 'Feature vector column (output of VectorAssembler or Scaler)' },
      { key: 'output_column', label: 'Output Column', type: 'text', default: 'pca_features', section: 'Columns', description: 'Name for the reduced feature vector column' },
      { key: 'k', label: 'Components (k)', type: 'number', default: 5, section: 'PCA', description: 'Number of principal components to keep. Must be less than the input vector dimension.' },
    ],
    defaultConfig: { input_column: 'features', output_column: 'pca_features', k: 5 },
  },

  // ===========================================================================
  // TARGETS - Data load destinations
  // ===========================================================================

  // --- SQL Script Executor (executes DDL/DML from upstream source nodes) ---
  'etl-target-sql-executor': {
    type: 'etl-target-sql-executor',
    category: 'targets',
    label: 'SQL Executor',
    description: 'Execute SQL scripts from upstream DDL or DML Source nodes against a connected database. Creates tables, inserts data, runs stored procedures — any SQL your database supports.',
    icon: 'PlayCircle',
    color: CATEGORY_COLORS.targets,
    inputs: [
      { id: 'sql', name: 'SQL Input', type: 'sql' },
    ],
    outputs: [],
    configSchema: [
      { key: 'on_error', label: 'On Error', type: 'select', options: [
        { value: 'stop', label: 'Stop Execution' },
        { value: 'skip', label: 'Skip Statement & Continue' },
        { value: 'rollback', label: 'Rollback Transaction' },
        { value: 'log', label: 'Log Error & Continue' },
      ], default: 'stop', section: 'Execution', description: 'How to handle errors when executing SQL statements' },
      { key: 'use_transaction', label: 'Wrap in Transaction', type: 'checkbox', default: false, section: 'Execution', description: 'Execute all statements within a single database transaction' },
      { key: 'delimiter', label: 'Statement Delimiter', type: 'text', default: ';', section: 'Execution', description: 'Character(s) used to split SQL into individual statements (e.g., ; or GO)' },
      { key: 'create_database', label: 'Create Database First', type: 'checkbox', default: false, section: 'Pre-Flight', description: 'Execute CREATE DATABASE IF NOT EXISTS before running scripts' },
      { key: 'database_name', label: 'Database Name', type: 'text', default: '', section: 'Pre-Flight', description: 'Database name to create (when "Create Database First" is checked)' },
      { key: 'pre_sql', label: 'Pre-Execution SQL', type: 'textarea', default: '', section: 'Pre-Flight', description: 'SQL to execute before the main script (e.g., SET SCHEMA, USE database)' },
      { key: 'post_sql', label: 'Post-Execution SQL', type: 'textarea', default: '', section: 'Post-Flight', description: 'SQL to execute after the main script completes (e.g., GRANT, RUNSTATS)' },
    ],
    defaultConfig: {
      on_error: 'stop',
      use_transaction: false,
      delimiter: ';',
      create_database: false,
      database_name: '',
      pre_sql: '',
      post_sql: '',
    },
  },

  'etl-target-database': {
    type: 'etl-target-database',
    category: 'targets',
    label: 'Database Target',
    description: 'Load data into a relational database via JDBC. Supports append, overwrite, upsert, and ignore write modes with configurable batch size.',
    icon: 'Database',
    color: CATEGORY_COLORS.targets,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [],
    configSchema: [
      { key: 'connection_type', label: 'Database Type', type: 'select', options: [
        { value: 'postgresql', label: 'PostgreSQL' },
        { value: 'mysql', label: 'MySQL' },
        { value: 'sqlserver', label: 'SQL Server' },
        { value: 'oracle', label: 'Oracle' },
        { value: 'db2', label: 'DB2' },
        { value: 'other', label: 'Other (Custom JDBC)' },
      ], default: 'postgresql', section: 'Connection', description: 'The relational database engine to write to' },
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', section: 'Connection', description: 'Database server hostname or IP address' },
      { key: 'port', label: 'Port', type: 'number', default: 5432, section: 'Connection', description: 'Database server port number' },
      { key: 'database', label: 'Database Name', type: 'text', default: '', section: 'Connection', description: 'Name of the database to write to' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Connection', description: 'Database login username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Connection', description: 'Database login password' },
      { key: 'schema', label: 'Schema', type: 'text', default: 'public', section: 'Connection', description: 'Database schema to write to' },
      { key: 'table_name', label: 'Table Name', type: 'text', default: '', section: 'Target', description: 'Name of the target table (will be created if it does not exist)' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append (Add Rows)' },
        { value: 'overwrite', label: 'Overwrite (Replace Table)' },
        { value: 'upsert', label: 'Upsert (Insert or Update)' },
        { value: 'ignore', label: 'Ignore (Skip Existing)' },
      ], default: 'append', section: 'Target', description: 'How to handle existing data in the target table' },
      { key: 'custom_jdbc_url', label: 'Custom JDBC URL', type: 'text', default: '', section: 'Connection', description: 'Full JDBC URL override (overrides host/port/database when set)' },
      { key: 'batch_size', label: 'Batch Size', type: 'number', default: 5000, section: 'Performance', description: 'Number of rows per INSERT batch. Higher values improve throughput but use more memory.' },
      { key: 'num_partitions', label: 'Write Partitions', type: 'number', default: 1, section: 'Performance', description: 'Number of parallel JDBC connections for writing. Higher = faster but more DB load.' },
      { key: 'truncate_before', label: 'Truncate Before Load', type: 'checkbox', default: false, section: 'Target', description: 'TRUNCATE the target table before loading data (faster than overwrite for large tables)' },
      { key: 'create_table_options', label: 'Create Table Options', type: 'text', default: '', section: 'Advanced', description: 'Database-specific table creation options appended to CREATE TABLE (e.g., ENGINE=InnoDB for MySQL)' },
      { key: 'create_table_column_types', label: 'Column Type Overrides', type: 'textarea', default: '', section: 'Advanced', description: 'Override auto-detected column types (e.g., name VARCHAR(500), amount DECIMAL(10,2)). One per line or comma-separated.' },
      { key: 'isolation_level', label: 'Isolation Level', type: 'select', options: [
        { value: 'READ_UNCOMMITTED', label: 'READ UNCOMMITTED' },
        { value: 'READ_COMMITTED', label: 'READ COMMITTED' },
        { value: 'REPEATABLE_READ', label: 'REPEATABLE READ' },
        { value: 'SERIALIZABLE', label: 'SERIALIZABLE' },
        { value: 'NONE', label: 'NONE (Autocommit)' },
      ], default: 'READ_UNCOMMITTED', section: 'Advanced', description: 'JDBC transaction isolation level. READ_UNCOMMITTED is fastest for bulk loads.' },
      { key: 'session_init_statement', label: 'Session Init SQL', type: 'textarea', default: '', section: 'Advanced', description: 'SQL executed on each JDBC connection before writing (e.g., SET search_path, SET lock_timeout)' },
    ],
    defaultConfig: {
      connection_type: 'postgresql',
      host: 'localhost',
      port: 5432,
      database: '',
      username: '',
      password: '',
      schema: 'public',
      table_name: '',
      write_mode: 'append',
      custom_jdbc_url: '',
      batch_size: 5000,
      num_partitions: 1,
      truncate_before: false,
      create_table_options: '',
      create_table_column_types: '',
      isolation_level: 'READ_UNCOMMITTED',
      session_init_statement: '',
    },
  },

  'etl-target-s3': {
    type: 'etl-target-s3',
    category: 'targets',
    label: 'S3 Target',
    description: 'Write data to Amazon S3 in various formats. Supports partitioning, compression, and coalescing output files.',
    icon: 'Cloud',
    color: CATEGORY_COLORS.targets,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [],
    configSchema: [
      { key: 'bucket', label: 'S3 Bucket', type: 'text', default: '', section: 'Location', description: 'S3 bucket name (without s3:// prefix)' },
      { key: 'prefix', label: 'Prefix / Path', type: 'text', default: '', section: 'Location', description: 'S3 key prefix for output files (e.g., data/output/2026/)' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'csv', label: 'CSV' },
        { value: 'json', label: 'JSON' },
        { value: 'parquet', label: 'Parquet' },
        { value: 'avro', label: 'Avro' },
        { value: 'orc', label: 'ORC' },
      ], default: 'parquet', section: 'Format', description: 'Output file format' },
      { key: 'partition_by', label: 'Partition By', type: 'tags', default: [], section: 'Partitioning', description: 'Columns to partition output files by (creates Hive-style partition directories)' },
      { key: 'compression', label: 'Compression', type: 'select', options: [
        { value: 'none', label: 'None' },
        { value: 'gzip', label: 'Gzip' },
        { value: 'snappy', label: 'Snappy' },
        { value: 'lz4', label: 'LZ4' },
        { value: 'zstd', label: 'Zstandard' },
      ], default: 'snappy', section: 'Format', description: 'Compression codec for output files' },
      { key: 'header', label: 'Include Header Row', type: 'checkbox', default: true, section: 'CSV Options', description: 'Write column names as the first row (CSV only)' },
      { key: 'delimiter', label: 'Delimiter', type: 'text', default: ',', section: 'CSV Options', description: 'Column delimiter for CSV output' },
      { key: 'quote', label: 'Quote Character', type: 'text', default: '"', section: 'CSV Options', description: 'Character used to quote fields (CSV)' },
      { key: 'null_value', label: 'Null Value', type: 'text', default: '', section: 'CSV Options', description: 'String written for null values (CSV/JSON). Empty = omit nulls.' },
      { key: 'date_format', label: 'Date Format', type: 'text', default: 'yyyy-MM-dd', section: 'CSV Options', description: 'Java SimpleDateFormat for date columns (CSV/JSON)' },
      { key: 'timestamp_format', label: 'Timestamp Format', type: 'text', default: "yyyy-MM-dd'T'HH:mm:ss", section: 'CSV Options', description: 'Java SimpleDateFormat for timestamp columns (CSV/JSON)' },
      { key: 'coalesce', label: 'Output File Count', type: 'number', default: 1, section: 'Output', description: 'Number of output files (1 = single file, 0 = one per partition)' },
      { key: 'max_records_per_file', label: 'Max Records Per File', type: 'number', default: 0, section: 'Output', description: 'Split output into files with at most N records each (0 = no limit)' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' },
        { value: 'overwrite', label: 'Overwrite' },
        { value: 'ignore', label: 'Ignore (Skip if Exists)' },
        { value: 'error', label: 'Error (Fail if Exists)' },
      ], default: 'overwrite', section: 'Output', description: 'How to handle existing output path' },
      { key: 'partition_overwrite_mode', label: 'Partition Overwrite', type: 'select', options: [
        { value: 'static', label: 'Static (Replace All Partitions)' },
        { value: 'dynamic', label: 'Dynamic (Replace Only Written Partitions)' },
      ], default: 'static', section: 'Partitioning', description: 'When using overwrite + partitionBy, static replaces all partitions; dynamic only replaces partitions present in the data' },
      { key: 'aws_access_key', label: 'AWS Access Key ID', type: 'text', default: '', section: 'AWS Auth', description: 'AWS access key (leave empty for IAM role/instance profile)' },
      { key: 'aws_secret_key', label: 'AWS Secret Access Key', type: 'password', default: '', section: 'AWS Auth', description: 'AWS secret key' },
      { key: 'aws_region', label: 'AWS Region', type: 'text', default: 'us-east-1', section: 'AWS Auth', description: 'AWS region for the S3 bucket' },
    ],
    defaultConfig: {
      bucket: '',
      prefix: '',
      file_format: 'parquet',
      partition_by: [],
      compression: 'snappy',
      header: true,
      delimiter: ',',
      quote: '"',
      null_value: '',
      date_format: 'yyyy-MM-dd',
      timestamp_format: "yyyy-MM-dd'T'HH:mm:ss",
      coalesce: 1,
      max_records_per_file: 0,
      write_mode: 'overwrite',
      partition_overwrite_mode: 'static',
      aws_access_key: '',
      aws_secret_key: '',
      aws_region: 'us-east-1',
    },
  },

  'etl-target-file': {
    type: 'etl-target-file',
    category: 'targets',
    label: 'File Target',
    description: 'Write data to a local filesystem or HDFS path in various formats with configurable output file count.',
    icon: 'FileOutput',
    color: CATEGORY_COLORS.targets,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [],
    configSchema: [
      { key: 'output_path', label: 'Output Path', type: 'text', default: '', section: 'Location', description: 'Absolute path to the output file or directory (local or HDFS)' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'csv', label: 'CSV' },
        { value: 'json', label: 'JSON' },
        { value: 'parquet', label: 'Parquet' },
        { value: 'avro', label: 'Avro' },
        { value: 'orc', label: 'ORC' },
        { value: 'text', label: 'Plain Text' },
      ], default: 'csv', section: 'Format', description: 'Output file format' },
      { key: 'delimiter', label: 'Delimiter', type: 'text', default: ',', section: 'Format', description: 'Column delimiter for CSV output' },
      { key: 'header', label: 'Include Header Row', type: 'checkbox', default: true, section: 'Format', description: 'Write column names as the first row (CSV)' },
      { key: 'quote', label: 'Quote Character', type: 'text', default: '"', section: 'CSV Options', description: 'Character used to quote fields (CSV)' },
      { key: 'null_value', label: 'Null Value', type: 'text', default: '', section: 'CSV Options', description: 'String written for null values (CSV). Empty = omit nulls.' },
      { key: 'date_format', label: 'Date Format', type: 'text', default: 'yyyy-MM-dd', section: 'CSV Options', description: 'Java SimpleDateFormat for date columns' },
      { key: 'timestamp_format', label: 'Timestamp Format', type: 'text', default: "yyyy-MM-dd'T'HH:mm:ss", section: 'CSV Options', description: 'Java SimpleDateFormat for timestamp columns' },
      { key: 'compression', label: 'Compression', type: 'select', options: [
        { value: 'none', label: 'None' }, { value: 'gzip', label: 'Gzip' }, { value: 'snappy', label: 'Snappy' },
        { value: 'lz4', label: 'LZ4' }, { value: 'zstd', label: 'Zstandard' }, { value: 'bzip2', label: 'Bzip2' },
      ], default: 'none', section: 'Output', description: 'Compression codec for output files' },
      { key: 'coalesce', label: 'Output File Count', type: 'number', default: 1, section: 'Output', description: 'Number of output files to write (1 = single file)' },
      { key: 'max_records_per_file', label: 'Max Records Per File', type: 'number', default: 0, section: 'Output', description: 'Split output into multiple files with at most N records each (0 = no limit)' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' },
        { value: 'overwrite', label: 'Overwrite' },
        { value: 'ignore', label: 'Ignore (Skip if Exists)' },
        { value: 'error', label: 'Error (Fail if Exists)' },
      ], default: 'overwrite', section: 'Output', description: 'How to handle existing output path' },
      { key: 'partition_by', label: 'Partition By', type: 'tags', default: [], section: 'Partitioning', description: 'Columns to partition output by (creates Hive-style directories)' },
    ],
    defaultConfig: {
      output_path: '',
      file_format: 'csv',
      delimiter: ',',
      header: true,
      quote: '"',
      null_value: '',
      date_format: 'yyyy-MM-dd',
      timestamp_format: "yyyy-MM-dd'T'HH:mm:ss",
      compression: 'none',
      coalesce: 1,
      max_records_per_file: 0,
      write_mode: 'overwrite',
      partition_by: [],
    },
  },

  'etl-target-kafka': {
    type: 'etl-target-kafka',
    category: 'targets',
    label: 'Kafka Target',
    description: 'Publish data as messages to an Apache Kafka topic. Supports JSON, Avro, and plain string serialization with optional key column.',
    icon: 'Radio',
    color: CATEGORY_COLORS.targets,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [],
    configSchema: [
      { key: 'bootstrap_servers', label: 'Bootstrap Servers', type: 'text', default: 'localhost:9092', section: 'Connection', description: 'Comma-separated list of Kafka broker addresses (host:port)' },
      { key: 'topic', label: 'Topic', type: 'text', default: '', section: 'Target', description: 'Kafka topic name to publish messages to' },
      { key: 'key_column', label: 'Key Column', type: 'text', default: '', section: 'Target', description: 'Column to use as the Kafka message key (empty = null key)' },
      { key: 'value_format', label: 'Value Format', type: 'select', options: [
        { value: 'json', label: 'JSON' },
        { value: 'avro', label: 'Avro' },
        { value: 'string', label: 'Plain String' },
      ], default: 'json', section: 'Format', description: 'Serialization format for message values' },
      { key: 'schema_registry_url', label: 'Schema Registry URL', type: 'text', default: '', section: 'Format', description: 'Confluent Schema Registry URL (required for Avro)' },
      { key: 'compression', label: 'Compression', type: 'select', options: [
        { value: 'none', label: 'None' }, { value: 'gzip', label: 'Gzip' },
        { value: 'snappy', label: 'Snappy' }, { value: 'lz4', label: 'LZ4' }, { value: 'zstd', label: 'Zstandard' },
      ], default: 'none', section: 'Performance', description: 'Compression codec for produced messages' },
      { key: 'acks', label: 'Acknowledgements', type: 'select', options: [
        { value: '0', label: '0 (Fire and Forget)' },
        { value: '1', label: '1 (Leader Only)' },
        { value: 'all', label: 'All (Full ISR)' },
      ], default: '1', section: 'Performance', description: 'Number of broker acknowledgements required before considering a write complete' },
      { key: 'batch_size', label: 'Batch Size (bytes)', type: 'number', default: 16384, section: 'Performance', description: 'Producer batch size in bytes. Larger batches improve throughput.' },
      { key: 'linger_ms', label: 'Linger (ms)', type: 'number', default: 0, section: 'Performance', description: 'Delay in ms before sending a batch. Higher values batch more records for better throughput.' },
      { key: 'security_protocol', label: 'Security Protocol', type: 'select', options: [
        { value: 'PLAINTEXT', label: 'PLAINTEXT' }, { value: 'SSL', label: 'SSL' },
        { value: 'SASL_PLAINTEXT', label: 'SASL_PLAINTEXT' }, { value: 'SASL_SSL', label: 'SASL_SSL' },
      ], default: 'PLAINTEXT', section: 'Security', description: 'Kafka security protocol' },
      { key: 'sasl_mechanism', label: 'SASL Mechanism', type: 'select', options: [
        { value: 'PLAIN', label: 'PLAIN' }, { value: 'SCRAM-SHA-256', label: 'SCRAM-SHA-256' },
        { value: 'SCRAM-SHA-512', label: 'SCRAM-SHA-512' },
      ], default: 'PLAIN', section: 'Security', description: 'SASL authentication mechanism' },
      { key: 'sasl_username', label: 'SASL Username', type: 'text', default: '', section: 'Security', description: 'SASL username' },
      { key: 'sasl_password', label: 'SASL Password', type: 'password', default: '', section: 'Security', description: 'SASL password' },
    ],
    defaultConfig: {
      bootstrap_servers: 'localhost:9092',
      topic: '',
      key_column: '',
      value_format: 'json',
      schema_registry_url: '',
      compression: 'none',
      acks: '1',
      batch_size: 16384,
      linger_ms: 0,
      security_protocol: 'PLAINTEXT',
      sasl_mechanism: 'PLAIN',
      sasl_username: '',
      sasl_password: '',
    },
  },

  // --- Cloud Storage Targets ---

  'etl-target-azure-blob': {
    type: 'etl-target-azure-blob',
    category: 'targets',
    label: 'Azure Blob / ADLS',
    description: 'Write data to Azure Blob Storage or Azure Data Lake Storage Gen2. Supports Parquet, CSV, JSON, Avro, ORC, and Delta Lake formats.',
    icon: 'Cloud',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'storage_account', label: 'Storage Account', type: 'text', default: '', section: 'Connection', description: 'Azure storage account name' },
      { key: 'container', label: 'Container', type: 'text', default: '', section: 'Connection', description: 'Blob container or ADLS filesystem name' },
      { key: 'path', label: 'Path / Prefix', type: 'text', default: '', section: 'Connection', description: 'Output path within container' },
      { key: 'auth_method', label: 'Authentication', type: 'select', options: [
        { value: 'access_key', label: 'Access Key' }, { value: 'sas_token', label: 'SAS Token' },
        { value: 'service_principal', label: 'Service Principal' }, { value: 'managed_identity', label: 'Managed Identity' },
      ], default: 'access_key', section: 'Connection', description: 'Authentication method' },
      { key: 'access_key', label: 'Access Key / SAS Token', type: 'password', default: '', section: 'Connection', description: 'Storage access key or SAS token' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'parquet', label: 'Parquet' }, { value: 'csv', label: 'CSV' }, { value: 'json', label: 'JSON' },
        { value: 'avro', label: 'Avro' }, { value: 'orc', label: 'ORC' }, { value: 'delta', label: 'Delta Lake' },
      ], default: 'parquet', section: 'Format', description: 'Output file format' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite' },
      ], default: 'overwrite', section: 'Options', description: 'How to handle existing data' },
      { key: 'partition_by', label: 'Partition By', type: 'tags', default: [], section: 'Options', description: 'Columns to partition the output by' },
    ],
    defaultConfig: { storage_account: '', container: '', path: '', auth_method: 'access_key', access_key: '', file_format: 'parquet', write_mode: 'overwrite', partition_by: [] },
  },

  'etl-target-gcs': {
    type: 'etl-target-gcs',
    category: 'targets',
    label: 'Google Cloud Storage',
    description: 'Write data to Google Cloud Storage buckets. Supports Parquet, CSV, JSON, Avro, and ORC formats.',
    icon: 'Cloud',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'bucket', label: 'Bucket', type: 'text', default: '', section: 'Connection', description: 'GCS bucket name' },
      { key: 'path', label: 'Path / Prefix', type: 'text', default: '', section: 'Connection', description: 'Output path within the bucket' },
      { key: 'project_id', label: 'Project ID', type: 'text', default: '', section: 'Connection', description: 'Google Cloud project ID' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'parquet', label: 'Parquet' }, { value: 'csv', label: 'CSV' }, { value: 'json', label: 'JSON' },
        { value: 'avro', label: 'Avro' }, { value: 'orc', label: 'ORC' },
      ], default: 'parquet', section: 'Format', description: 'Output file format' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite' },
      ], default: 'overwrite', section: 'Options', description: 'How to handle existing data' },
      { key: 'partition_by', label: 'Partition By', type: 'tags', default: [], section: 'Options', description: 'Columns to partition the output by' },
    ],
    defaultConfig: { bucket: '', path: '', project_id: '', file_format: 'parquet', write_mode: 'overwrite', partition_by: [] },
  },

  'etl-target-hdfs': {
    type: 'etl-target-hdfs',
    category: 'targets',
    label: 'HDFS',
    description: 'Write data to Hadoop Distributed File System. Supports all Spark output formats.',
    icon: 'HardDrive',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'namenode_url', label: 'NameNode URL', type: 'text', default: 'hdfs://localhost:9000', section: 'Connection', description: 'HDFS NameNode URL' },
      { key: 'path', label: 'Path', type: 'text', default: '', section: 'Connection', description: 'HDFS output path' },
      { key: 'file_format', label: 'File Format', type: 'select', options: [
        { value: 'parquet', label: 'Parquet' }, { value: 'csv', label: 'CSV' }, { value: 'json', label: 'JSON' },
        { value: 'avro', label: 'Avro' }, { value: 'orc', label: 'ORC' },
      ], default: 'parquet', section: 'Format', description: 'Output file format' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite' },
      ], default: 'overwrite', section: 'Options', description: 'How to handle existing data' },
      { key: 'compression', label: 'Compression', type: 'select', options: [
        { value: 'none', label: 'None' }, { value: 'snappy', label: 'Snappy' }, { value: 'gzip', label: 'Gzip' }, { value: 'lz4', label: 'LZ4' },
      ], default: 'snappy', section: 'Options', description: 'Compression codec' },
      { key: 'coalesce', label: 'Output Files', type: 'number', default: 0, section: 'Options', description: 'Number of output files (0 = auto, based on partitions)' },
    ],
    defaultConfig: { namenode_url: 'hdfs://localhost:9000', path: '', file_format: 'parquet', write_mode: 'overwrite', compression: 'snappy', coalesce: 0 },
  },

  // --- NoSQL / Document Targets ---

  'etl-target-mongodb': {
    type: 'etl-target-mongodb',
    category: 'targets',
    label: 'MongoDB',
    description: 'Write data to MongoDB collections. Supports append, overwrite, and upsert write modes.',
    icon: 'Database',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'connection_uri', label: 'Connection URI', type: 'text', default: 'mongodb://localhost:27017', section: 'Connection', description: 'MongoDB connection string' },
      { key: 'database', label: 'Database', type: 'text', default: '', section: 'Connection', description: 'MongoDB database name' },
      { key: 'collection', label: 'Collection', type: 'text', default: '', section: 'Connection', description: 'MongoDB collection name' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite' },
      ], default: 'append', section: 'Options', description: 'How to handle existing documents' },
      { key: 'upsert', label: 'Upsert', type: 'checkbox', default: false, section: 'Options', description: 'Enable upsert mode (requires id field mapping)' },
      { key: 'id_field', label: 'ID Field', type: 'text', default: '_id', section: 'Options', description: 'Field to use as document ID for upsert operations' },
    ],
    defaultConfig: { connection_uri: 'mongodb://localhost:27017', database: '', collection: '', write_mode: 'append', upsert: false, id_field: '_id' },
  },

  'etl-target-cassandra': {
    type: 'etl-target-cassandra',
    category: 'targets',
    label: 'Cassandra',
    description: 'Write data to Apache Cassandra tables. Supports batch writes with configurable consistency levels.',
    icon: 'Database',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'host', label: 'Contact Points', type: 'text', default: 'localhost', section: 'Connection', description: 'Comma-separated Cassandra contact points' },
      { key: 'port', label: 'Port', type: 'number', default: 9042, section: 'Connection', description: 'Cassandra native transport port' },
      { key: 'keyspace', label: 'Keyspace', type: 'text', default: '', section: 'Connection', description: 'Cassandra keyspace name' },
      { key: 'table', label: 'Table', type: 'text', default: '', section: 'Connection', description: 'Cassandra table name' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Auth', description: 'Cassandra username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'Cassandra password' },
      { key: 'consistency_level', label: 'Consistency Level', type: 'select', options: [
        { value: 'LOCAL_ONE', label: 'LOCAL_ONE' }, { value: 'LOCAL_QUORUM', label: 'LOCAL_QUORUM' },
        { value: 'ONE', label: 'ONE' }, { value: 'QUORUM', label: 'QUORUM' },
      ], default: 'LOCAL_QUORUM', section: 'Options', description: 'Write consistency level' },
      { key: 'batch_size_bytes', label: 'Batch Size (bytes)', type: 'number', default: 65536, section: 'Performance', description: 'Maximum batch size in bytes for Cassandra writes' },
    ],
    defaultConfig: { host: 'localhost', port: 9042, keyspace: '', table: '', username: '', password: '', consistency_level: 'LOCAL_QUORUM', batch_size_bytes: 65536 },
  },

  'etl-target-dynamodb': {
    type: 'etl-target-dynamodb',
    category: 'targets',
    label: 'DynamoDB',
    description: 'Write data to Amazon DynamoDB tables. Supports batch writes with configurable throughput.',
    icon: 'Database',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'table_name', label: 'Table Name', type: 'text', default: '', section: 'Connection', description: 'DynamoDB table name' },
      { key: 'region', label: 'AWS Region', type: 'text', default: 'us-east-1', section: 'Connection', description: 'AWS region' },
      { key: 'write_throughput', label: 'Write Throughput', type: 'text', default: '1', section: 'Performance', description: 'Target fraction of provisioned write throughput to consume (0.1 to 1.0)' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'put', label: 'Put (Insert/Replace)' }, { value: 'update', label: 'Update (Merge)' },
      ], default: 'put', section: 'Options', description: 'DynamoDB write operation type' },
    ],
    defaultConfig: { table_name: '', region: 'us-east-1', write_throughput: '1', write_mode: 'put' },
  },

  'etl-target-elasticsearch': {
    type: 'etl-target-elasticsearch',
    category: 'targets',
    label: 'Elasticsearch',
    description: 'Write data to Elasticsearch indices. Supports index creation, document upsert, and bulk operations.',
    icon: 'Search',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'nodes', label: 'Nodes', type: 'text', default: 'localhost', section: 'Connection', description: 'Comma-separated Elasticsearch node addresses' },
      { key: 'port', label: 'Port', type: 'number', default: 9200, section: 'Connection', description: 'Elasticsearch HTTP port' },
      { key: 'index', label: 'Index', type: 'text', default: '', section: 'Connection', description: 'Target Elasticsearch index name' },
      { key: 'id_field', label: 'Document ID Field', type: 'text', default: '', section: 'Options', description: 'Column to use as document ID (empty = auto-generated)' },
      { key: 'write_mode', label: 'Write Operation', type: 'select', options: [
        { value: 'index', label: 'Index (Insert/Replace)' }, { value: 'create', label: 'Create (Insert Only)' },
        { value: 'update', label: 'Update (Partial)' }, { value: 'upsert', label: 'Upsert' },
      ], default: 'index', section: 'Options', description: 'Elasticsearch write operation' },
      { key: 'ssl_enabled', label: 'SSL Enabled', type: 'checkbox', default: false, section: 'Security', description: 'Enable SSL/TLS' },
    ],
    defaultConfig: { nodes: 'localhost', port: 9200, index: '', id_field: '', write_mode: 'index', ssl_enabled: false },
  },

  'etl-target-redis': {
    type: 'etl-target-redis',
    category: 'targets',
    label: 'Redis',
    description: 'Write data to Redis. Supports hashes, strings, lists, and sorted sets.',
    icon: 'Database',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'host', label: 'Host', type: 'text', default: 'localhost', section: 'Connection', description: 'Redis server host' },
      { key: 'port', label: 'Port', type: 'number', default: 6379, section: 'Connection', description: 'Redis server port' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Connection', description: 'Redis password' },
      { key: 'db_number', label: 'Database Number', type: 'number', default: 0, section: 'Connection', description: 'Redis database number (0-15)' },
      { key: 'data_type', label: 'Data Type', type: 'select', options: [
        { value: 'hash', label: 'Hash' }, { value: 'string', label: 'String' }, { value: 'list', label: 'List' }, { value: 'zset', label: 'Sorted Set' },
      ], default: 'hash', section: 'Data', description: 'Redis data structure to write to' },
      { key: 'key_column', label: 'Key Column', type: 'text', default: '', section: 'Data', description: 'DataFrame column to use as Redis key' },
      { key: 'ttl', label: 'TTL (seconds)', type: 'number', default: 0, section: 'Options', description: 'Time-to-live in seconds (0 = no expiry)' },
    ],
    defaultConfig: { host: 'localhost', port: 6379, password: '', db_number: 0, data_type: 'hash', key_column: '', ttl: 0 },
  },

  // --- Data Warehouse Targets ---

  'etl-target-snowflake': {
    type: 'etl-target-snowflake',
    category: 'targets',
    label: 'Snowflake',
    description: 'Write data to Snowflake tables. Uses internal staging for optimized bulk loads.',
    icon: 'Snowflake',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'account', label: 'Account', type: 'text', default: '', section: 'Connection', description: 'Snowflake account identifier' },
      { key: 'warehouse', label: 'Warehouse', type: 'text', default: '', section: 'Connection', description: 'Virtual warehouse name' },
      { key: 'database', label: 'Database', type: 'text', default: '', section: 'Connection', description: 'Database name' },
      { key: 'schema', label: 'Schema', type: 'text', default: 'PUBLIC', section: 'Connection', description: 'Schema name' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Auth', description: 'Snowflake username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'Snowflake password' },
      { key: 'table_name', label: 'Table Name', type: 'text', default: '', section: 'Data', description: 'Target table name' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite' },
      ], default: 'append', section: 'Options', description: 'How to handle existing data' },
      { key: 'auto_create', label: 'Auto Create Table', type: 'checkbox', default: true, section: 'Options', description: 'Automatically create the table if it does not exist' },
    ],
    defaultConfig: { account: '', warehouse: '', database: '', schema: 'PUBLIC', username: '', password: '', table_name: '', write_mode: 'append', auto_create: true },
  },

  'etl-target-redshift': {
    type: 'etl-target-redshift',
    category: 'targets',
    label: 'Redshift',
    description: 'Write data to Amazon Redshift. Uses S3 staging with COPY command for optimized bulk loads.',
    icon: 'Database',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'jdbc_url', label: 'JDBC URL', type: 'text', default: '', section: 'Connection', description: 'Redshift JDBC connection URL' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Auth', description: 'Redshift username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'Redshift password' },
      { key: 'temp_dir', label: 'S3 Temp Directory', type: 'text', default: '', section: 'Connection', description: 'S3 path for temporary staging (e.g., s3://bucket/temp/)' },
      { key: 'table_name', label: 'Table Name', type: 'text', default: '', section: 'Data', description: 'Target table name' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite' },
      ], default: 'append', section: 'Options', description: 'How to handle existing data' },
    ],
    defaultConfig: { jdbc_url: '', username: '', password: '', temp_dir: '', table_name: '', write_mode: 'append' },
  },

  'etl-target-bigquery': {
    type: 'etl-target-bigquery',
    category: 'targets',
    label: 'BigQuery',
    description: 'Write data to Google BigQuery tables. Supports direct and indirect (GCS staging) write methods.',
    icon: 'Database',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'project', label: 'GCP Project', type: 'text', default: '', section: 'Connection', description: 'Google Cloud project ID' },
      { key: 'dataset', label: 'Dataset', type: 'text', default: '', section: 'Connection', description: 'BigQuery dataset name' },
      { key: 'table', label: 'Table', type: 'text', default: '', section: 'Connection', description: 'BigQuery table name' },
      { key: 'temp_gcs_bucket', label: 'Temp GCS Bucket', type: 'text', default: '', section: 'Connection', description: 'GCS bucket for temporary staging during writes' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite' },
      ], default: 'append', section: 'Options', description: 'How to handle existing data' },
      { key: 'create_disposition', label: 'Table Creation', type: 'select', options: [
        { value: 'CREATE_IF_NEEDED', label: 'Create If Needed' }, { value: 'CREATE_NEVER', label: 'Never Create' },
      ], default: 'CREATE_IF_NEEDED', section: 'Options', description: 'Whether to create the table if it does not exist' },
      { key: 'partition_field', label: 'Partition Field', type: 'text', default: '', section: 'Options', description: 'Column for time-based partitioning (leave empty for no partitioning)' },
    ],
    defaultConfig: { project: '', dataset: '', table: '', temp_gcs_bucket: '', write_mode: 'append', create_disposition: 'CREATE_IF_NEEDED', partition_field: '' },
  },

  // --- Lakehouse Format Targets ---

  'etl-target-delta': {
    type: 'etl-target-delta',
    category: 'targets',
    label: 'Delta Lake',
    description: 'Write data to Delta Lake tables with ACID transactions, schema enforcement, and merge (upsert) support.',
    icon: 'Triangle',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'path', label: 'Delta Table Path', type: 'text', default: '', section: 'Connection', description: 'Path to Delta table (local, S3, HDFS, or ADLS)' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite' }, { value: 'merge', label: 'Merge (Upsert)' },
      ], default: 'append', section: 'Options', description: 'How to handle existing data. Merge requires a merge key.' },
      { key: 'merge_key', label: 'Merge Key Columns', type: 'tags', default: [], section: 'Options', description: 'Columns to match on during merge/upsert (required for Merge mode)' },
      { key: 'partition_by', label: 'Partition By', type: 'tags', default: [], section: 'Options', description: 'Columns to partition the Delta table by' },
      { key: 'schema_evolution', label: 'Schema Evolution', type: 'checkbox', default: false, section: 'Options', description: 'Allow automatic schema evolution (mergeSchema)' },
    ],
    defaultConfig: { path: '', write_mode: 'append', merge_key: [], partition_by: [], schema_evolution: false },
  },

  'etl-target-iceberg': {
    type: 'etl-target-iceberg',
    category: 'targets',
    label: 'Apache Iceberg',
    description: 'Write data to Apache Iceberg tables with snapshot isolation and schema evolution.',
    icon: 'Hexagon',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'catalog', label: 'Catalog Name', type: 'text', default: '', section: 'Connection', description: 'Iceberg catalog name' },
      { key: 'database', label: 'Database / Namespace', type: 'text', default: 'default', section: 'Connection', description: 'Iceberg database or namespace' },
      { key: 'table', label: 'Table', type: 'text', default: '', section: 'Connection', description: 'Iceberg table name' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite Dynamic' },
      ], default: 'append', section: 'Options', description: 'How to handle existing data' },
      { key: 'partition_by', label: 'Partition By', type: 'tags', default: [], section: 'Options', description: 'Partition columns (uses Iceberg hidden partitioning transforms)' },
    ],
    defaultConfig: { catalog: '', database: 'default', table: '', write_mode: 'append', partition_by: [] },
  },

  // --- Streaming Targets ---

  'etl-target-kinesis': {
    type: 'etl-target-kinesis',
    category: 'targets',
    label: 'AWS Kinesis',
    description: 'Write streaming data to Amazon Kinesis Data Streams.',
    icon: 'Waves',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'stream_name', label: 'Stream Name', type: 'text', default: '', section: 'Connection', description: 'Kinesis stream name' },
      { key: 'region', label: 'AWS Region', type: 'text', default: 'us-east-1', section: 'Connection', description: 'AWS region' },
      { key: 'partition_key', label: 'Partition Key Column', type: 'text', default: '', section: 'Options', description: 'Column to use as Kinesis partition key' },
      { key: 'format', label: 'Record Format', type: 'select', options: [
        { value: 'json', label: 'JSON' }, { value: 'csv', label: 'CSV' }, { value: 'string', label: 'Raw String' },
      ], default: 'json', section: 'Format', description: 'Format of the output records' },
    ],
    defaultConfig: { stream_name: '', region: 'us-east-1', partition_key: '', format: 'json' },
  },

  // --- Graph / Analytics Targets ---

  'etl-target-neo4j': {
    type: 'etl-target-neo4j',
    category: 'targets',
    label: 'Neo4j',
    description: 'Write data to a Neo4j graph database as nodes or relationships using the official Neo4j Spark connector. Supports MERGE (upsert) and CREATE operations.',
    icon: 'Network',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'url', label: 'Bolt URL', type: 'text', default: 'bolt://localhost:7687', section: 'Connection', description: 'Neo4j Bolt connection URL' },
      { key: 'username', label: 'Username', type: 'text', default: 'neo4j', section: 'Auth', description: 'Neo4j username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'Neo4j password' },
      { key: 'database', label: 'Database', type: 'text', default: 'neo4j', section: 'Connection', description: 'Neo4j database name' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'node', label: 'Create/Merge Nodes' },
        { value: 'relationship', label: 'Create/Merge Relationships' },
        { value: 'query', label: 'Custom Cypher Query' },
      ], default: 'node', section: 'Data', description: 'How to write data to Neo4j' },
      { key: 'save_mode', label: 'Save Mode', type: 'select', options: [
        { value: 'Overwrite', label: 'Overwrite (MERGE)' },
        { value: 'Append', label: 'Append (CREATE)' },
        { value: 'ErrorIfExists', label: 'Error If Exists' },
      ], default: 'Overwrite', section: 'Data', description: 'How to handle existing nodes/relationships' },
      { key: 'labels', label: 'Node Labels', type: 'text', default: '', section: 'Data', description: 'Node label(s) when writing nodes (e.g., Person or Person:Employee)' },
      { key: 'relationship_type', label: 'Relationship Type', type: 'text', default: '', section: 'Data', description: 'Relationship type when writing relationships (e.g., KNOWS)' },
      { key: 'node_keys', label: 'Node Keys (MERGE)', type: 'tags', default: [], section: 'Data', description: 'DataFrame columns to use as node keys for MERGE matching' },
      { key: 'query', label: 'Cypher Query', type: 'textarea', default: '', section: 'Data', description: 'Custom Cypher query for writing. Use `event` to reference each row (e.g., MERGE (p:Person {id: event.id}) SET p.name = event.name)' },
      { key: 'batch_size', label: 'Batch Size', type: 'number', default: 5000, section: 'Performance', description: 'Number of rows per transaction batch' },
    ],
    defaultConfig: { url: 'bolt://localhost:7687', username: 'neo4j', password: '', database: 'neo4j', write_mode: 'node', save_mode: 'Overwrite', labels: '', relationship_type: '', node_keys: [], query: '', batch_size: 5000 },
  },

  'etl-target-sqlserver': {
    type: 'etl-target-sqlserver',
    category: 'targets',
    label: 'SQL Server',
    description: 'Write data to Microsoft SQL Server or Azure SQL using the dedicated Apache Spark connector. Optimized bulk inserts with table lock and batch control.',
    icon: 'Database',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'host', label: 'Host', type: 'text', default: '', section: 'Connection', description: 'SQL Server hostname or IP address' },
      { key: 'port', label: 'Port', type: 'number', default: 1433, section: 'Connection', description: 'SQL Server port' },
      { key: 'database', label: 'Database', type: 'text', default: '', section: 'Connection', description: 'Database name' },
      { key: 'auth_method', label: 'Authentication', type: 'select', options: [
        { value: 'sql', label: 'SQL Authentication' },
        { value: 'ad_password', label: 'Active Directory Password' },
        { value: 'ad_integrated', label: 'Active Directory Integrated' },
        { value: 'ad_msi', label: 'Managed Identity (Azure)' },
      ], default: 'sql', section: 'Auth', description: 'Authentication method' },
      { key: 'username', label: 'Username', type: 'text', default: '', section: 'Auth', description: 'SQL Server username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'SQL Server password' },
      { key: 'schema', label: 'Schema', type: 'text', default: 'dbo', section: 'Data', description: 'Database schema' },
      { key: 'table_name', label: 'Table Name', type: 'text', default: '', section: 'Data', description: 'Target table name' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'overwrite', label: 'Overwrite' },
      ], default: 'append', section: 'Options', description: 'How to handle existing data' },
      { key: 'batch_size', label: 'Batch Size', type: 'number', default: 10000, section: 'Performance', description: 'Number of rows per insert batch' },
      { key: 'table_lock', label: 'Table Lock', type: 'checkbox', default: true, section: 'Performance', description: 'Use TABLOCK hint for faster bulk inserts' },
      { key: 'encrypt', label: 'Encrypt Connection', type: 'checkbox', default: true, section: 'Security', description: 'Use TLS encryption' },
    ],
    defaultConfig: { host: '', port: 1433, database: '', auth_method: 'sql', username: '', password: '', schema: 'dbo', table_name: '', write_mode: 'append', batch_size: 10000, table_lock: true, encrypt: true },
  },

  'etl-target-starrocks': {
    type: 'etl-target-starrocks',
    category: 'targets',
    label: 'StarRocks',
    description: 'Write data to StarRocks OLAP database using the official StarRocks Spark connector. Uses Stream Load for high-throughput ingestion.',
    icon: 'Zap',
    color: CATEGORY_COLORS.targets,
    inputs: [{ id: 'input', name: 'Input', type: 'dataframe' }],
    outputs: [],
    configSchema: [
      { key: 'fe_nodes', label: 'FE Nodes', type: 'text', default: '', section: 'Connection', description: 'Comma-separated StarRocks FE node addresses (host:http_port)' },
      { key: 'be_nodes', label: 'BE Nodes', type: 'text', default: '', section: 'Connection', description: 'Comma-separated BE node addresses for Stream Load (host:http_port)' },
      { key: 'database', label: 'Database', type: 'text', default: '', section: 'Connection', description: 'StarRocks database name' },
      { key: 'table', label: 'Table', type: 'text', default: '', section: 'Connection', description: 'StarRocks table name' },
      { key: 'username', label: 'Username', type: 'text', default: 'root', section: 'Auth', description: 'StarRocks username' },
      { key: 'password', label: 'Password', type: 'password', default: '', section: 'Auth', description: 'StarRocks password' },
      { key: 'write_mode', label: 'Write Mode', type: 'select', options: [
        { value: 'append', label: 'Append' }, { value: 'upsert', label: 'Upsert (Primary Key Tables)' },
      ], default: 'append', section: 'Options', description: 'Append or upsert (requires Primary Key table model)' },
      { key: 'batch_rows', label: 'Batch Rows', type: 'number', default: 65536, section: 'Performance', description: 'Number of rows per Stream Load batch' },
    ],
    defaultConfig: { fe_nodes: '', be_nodes: '', database: '', table: '', username: 'root', password: '', write_mode: 'append', batch_rows: 65536 },
  },

  // ===========================================================================
  // QUALITY - Data quality and validation
  // ===========================================================================
  'etl-quality-check': {
    type: 'etl-quality-check',
    category: 'quality',
    label: 'Data Quality Check',
    description: 'Apply data quality rules to validate column values. Rules include not_null, unique, range, regex, referential integrity, and custom expressions. Failed rows can trigger warnings, failures, or quarantine.',
    icon: 'ShieldCheck',
    color: CATEGORY_COLORS.quality,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'passed', name: 'Passed', type: 'dataframe' },
      { id: 'failed', name: 'Failed / Quarantined', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'checks', label: 'Quality Checks', type: 'textarea', default: '[]', section: 'Rules', description: 'JSON array of quality checks: [{\"column\": \"email\", \"rule\": \"not_null\", \"threshold\": 0.99, \"description\": \"Email must not be null\"}]. Rules: not_null, unique, range, regex, referential, custom' },
      { key: 'fail_action', label: 'Failure Action', type: 'select', options: [
        { value: 'warn', label: 'Warn (Log and Continue)' },
        { value: 'fail', label: 'Fail (Stop Pipeline)' },
        { value: 'quarantine', label: 'Quarantine (Route to Failed Output)' },
      ], default: 'warn', section: 'Behavior', description: 'What to do when a quality check fails' },
    ],
    defaultConfig: {
      checks: '[]',
      fail_action: 'warn',
    },
  },

  'etl-quality-schema': {
    type: 'etl-quality-schema',
    category: 'quality',
    label: 'Schema Validator',
    description: 'Validate that data conforms to an expected schema. Check column names, data types, and nullability. Optionally coerce types to match the expected schema.',
    icon: 'FileCheck',
    color: CATEGORY_COLORS.quality,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Validated', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'expected_schema', label: 'Expected Schema', type: 'textarea', default: '{}', section: 'Schema', description: 'JSON schema definition: {\"columns\": [{\"name\": \"id\", \"type\": \"integer\", \"nullable\": false}, {\"name\": \"email\", \"type\": \"string\", \"nullable\": false}]}' },
      { key: 'strict_mode', label: 'Strict Mode', type: 'checkbox', default: false, section: 'Validation', description: 'Fail if any column does not exactly match the expected type (no implicit coercion)' },
      { key: 'allow_extra_columns', label: 'Allow Extra Columns', type: 'checkbox', default: true, section: 'Validation', description: 'Allow columns not defined in the expected schema to pass through' },
      { key: 'type_coercion', label: 'Type Coercion', type: 'checkbox', default: true, section: 'Validation', description: 'Attempt to cast columns to match expected types (e.g., string to integer)' },
    ],
    defaultConfig: {
      expected_schema: '{}',
      strict_mode: false,
      allow_extra_columns: true,
      type_coercion: true,
    },
  },

  'etl-quality-profiler': {
    type: 'etl-quality-profiler',
    category: 'quality',
    label: 'Data Profiler',
    description: 'Generate statistical profiles of your data. Computes min, max, mean, null counts, distinct counts, and optionally histograms and pattern detection per column.',
    icon: 'BarChart2',
    color: CATEGORY_COLORS.quality,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Data (Pass-through)', type: 'dataframe' },
      { id: 'profile', name: 'Profile Report', type: 'json' },
    ],
    configSchema: [
      { key: 'columns', label: 'Columns to Profile', type: 'tags', default: [], section: 'Scope', description: 'Specific columns to profile (empty = all columns)' },
      { key: 'sample_size', label: 'Sample Size', type: 'number', default: 0, section: 'Performance', description: 'Number of rows to sample for profiling (0 = use all rows). Sampling speeds up profiling on large datasets.' },
      { key: 'compute_histograms', label: 'Compute Histograms', type: 'checkbox', default: true, section: 'Analysis', description: 'Generate value distribution histograms for numeric columns' },
      { key: 'detect_patterns', label: 'Detect Patterns', type: 'checkbox', default: true, section: 'Analysis', description: 'Detect common data patterns (email, phone, date formats, etc.) in string columns' },
    ],
    defaultConfig: {
      columns: [],
      sample_size: 0,
      compute_histograms: true,
      detect_patterns: true,
    },
  },

  // ===========================================================================
  // UTILITY - Pipeline control and helper nodes
  // ===========================================================================
  'etl-util-custom-pyspark': {
    type: 'etl-util-custom-pyspark',
    category: 'utility',
    label: 'Custom PySpark',
    description: 'Execute arbitrary PySpark code. The input dataframe is available as a variable (default: df). Your code must produce a result dataframe (default: result).',
    icon: 'Terminal',
    color: CATEGORY_COLORS.utility,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Result', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'code', label: 'PySpark Code', type: 'textarea', default: '# Input dataframe is available as \'df\'\n# Assign your output to \'result\'\nresult = df', section: 'Code', description: 'PySpark code to execute. The input dataframe is bound to the variable named in Input Variable Name. Assign the output to the variable named in Output Variable Name.' },
      { key: 'input_var_name', label: 'Input Variable Name', type: 'text', default: 'df', section: 'Variables', description: 'Variable name for the input dataframe in your code' },
      { key: 'output_var_name', label: 'Output Variable Name', type: 'text', default: 'result', section: 'Variables', description: 'Variable name your code assigns the output dataframe to' },
    ],
    defaultConfig: {
      code: '# Input dataframe is available as \'df\'\n# Assign your output to \'result\'\nresult = df',
      input_var_name: 'df',
      output_var_name: 'result',
    },
  },

  'etl-util-notification': {
    type: 'etl-util-notification',
    category: 'utility',
    label: 'Notification',
    description: 'Send notifications via email, Slack, or webhook at pipeline milestones. Trigger on success, failure, or both.',
    icon: 'Bell',
    color: CATEGORY_COLORS.utility,
    inputs: [
      { id: 'input', name: 'Input (Pass-through)', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Output (Pass-through)', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'channel', label: 'Notification Channel', type: 'select', options: [
        { value: 'email', label: 'Email' },
        { value: 'slack', label: 'Slack' },
        { value: 'webhook', label: 'Webhook (HTTP POST)' },
      ], default: 'email', section: 'Channel', description: 'How to deliver the notification' },
      { key: 'recipient', label: 'Recipient', type: 'text', default: '', section: 'Channel', description: 'Email address, Slack channel (e.g., #data-ops), or webhook URL' },
      { key: 'message_template', label: 'Message Template', type: 'textarea', default: 'Pipeline step completed. Rows processed: {{row_count}}', section: 'Message', description: 'Message body with optional template variables: {{row_count}}, {{timestamp}}, {{status}}, {{error}}' },
      { key: 'on_success', label: 'Notify on Success', type: 'checkbox', default: true, section: 'Triggers', description: 'Send notification when this step completes successfully' },
      { key: 'on_failure', label: 'Notify on Failure', type: 'checkbox', default: true, section: 'Triggers', description: 'Send notification when this step fails' },
    ],
    defaultConfig: {
      channel: 'email',
      recipient: '',
      message_template: 'Pipeline step completed. Rows processed: {{row_count}}',
      on_success: true,
      on_failure: true,
    },
  },

  'etl-util-checkpoint': {
    type: 'etl-util-checkpoint',
    category: 'utility',
    label: 'Checkpoint',
    description: 'Persist the current dataframe to disk as a checkpoint. Breaks the Spark lineage to improve reliability and performance on long pipelines.',
    icon: 'Save',
    color: CATEGORY_COLORS.utility,
    inputs: [
      { id: 'input', name: 'Input', type: 'dataframe' },
    ],
    outputs: [
      { id: 'output', name: 'Checkpointed', type: 'dataframe' },
    ],
    configSchema: [
      { key: 'checkpoint_path', label: 'Checkpoint Path', type: 'text', default: '', section: 'Storage', description: 'Directory path to store checkpoint data (local or HDFS). Must be accessible by all Spark executors.' },
      { key: 'eager', label: 'Eager Checkpoint', type: 'checkbox', default: true, section: 'Options', description: 'If enabled, immediately materializes the dataframe to disk. If disabled, checkpointing is lazy and triggered by the next action.' },
    ],
    defaultConfig: {
      checkpoint_path: '',
      eager: true,
    },
  },
}

// =============================================================================
// CATEGORY DEFINITIONS
// =============================================================================

export const ETL_NODE_CATEGORIES = {
  templates: {
    label: 'Templates',
    icon: 'LayoutTemplate',
    color: CATEGORY_COLORS.templates,
    nodes: ['etl-template-db-to-db', 'etl-template-cobol-to-db', 'etl-template-idms-to-db2', 'etl-template-s3-etl'],
  },
  sources: {
    label: 'Sources',
    icon: 'Database',
    color: CATEGORY_COLORS.sources,
    nodes: [
      'etl-source-database', 'etl-source-s3', 'etl-source-azure-blob', 'etl-source-gcs', 'etl-source-hdfs', 'etl-source-file',
      'etl-source-snowflake', 'etl-source-redshift', 'etl-source-bigquery',
      'etl-source-mongodb', 'etl-source-cassandra', 'etl-source-dynamodb', 'etl-source-elasticsearch', 'etl-source-redis', 'etl-source-hbase',
      'etl-source-delta', 'etl-source-iceberg',
      'etl-source-kafka', 'etl-source-kinesis',
      'etl-source-rest-api', 'etl-source-ftp', 'etl-source-pdf',
      'etl-source-neo4j', 'etl-source-sqlserver', 'etl-source-starrocks',
      'etl-source-cobol-copybook', 'etl-source-idms', 'etl-source-vsam', 'etl-source-ims',
      'etl-source-ddl', 'etl-source-dml',
    ],
  },
  transforms: {
    label: 'Transforms',
    icon: 'Shuffle',
    color: CATEGORY_COLORS.transforms,
    nodes: [
      'etl-transform-filter', 'etl-transform-join', 'etl-transform-aggregate', 'etl-transform-sort',
      'etl-transform-column-mapper', 'etl-transform-expression', 'etl-transform-lookup', 'etl-transform-union',
      'etl-transform-split', 'etl-transform-deduplicate', 'etl-transform-pivot', 'etl-transform-window',
      'etl-transform-custom-sql', 'etl-transform-intersect', 'etl-transform-except',
      'etl-transform-sql-dedup', 'etl-transform-sql-types', 'etl-transform-sql-dialect',
    ],
  },
  schema: {
    label: 'Schema',
    icon: 'Columns3',
    color: CATEGORY_COLORS.schema,
    nodes: [
      'etl-schema-select', 'etl-schema-rename', 'etl-schema-reorder',
      'etl-schema-type-cast', 'etl-schema-add-constant', 'etl-schema-flatten',
    ],
  },
  data_prep: {
    label: 'Data Prep',
    icon: 'Sparkles',
    color: CATEGORY_COLORS.data_prep,
    nodes: [
      'etl-prep-fill-na', 'etl-prep-drop-na', 'etl-prep-string-functions', 'etl-prep-datetime',
      'etl-prep-sample', 'etl-prep-limit', 'etl-prep-repartition', 'etl-prep-hash', 'etl-prep-ebcdic',
    ],
  },
  stats: {
    label: 'Stats',
    icon: 'BarChart3',
    color: CATEGORY_COLORS.stats,
    nodes: [
      // Descriptive Statistics
      'etl-stats-summary', 'etl-stats-percentiles',
      // Data Profiling
      'etl-stats-profile', 'etl-stats-frequency',
      // Correlation & Relationships
      'etl-stats-correlation', 'etl-stats-crosstab', 'etl-stats-covariance', 'etl-stats-chi-square',
      // Distribution Analysis
      'etl-stats-histogram', 'etl-stats-sample', 'etl-stats-ks-test',
      // Outlier Detection
      'etl-stats-outlier-zscore', 'etl-stats-outlier-iqr',
      // Time Series
      'etl-stats-moving-avg', 'etl-stats-lag-lead', 'etl-stats-rolling-window', 'etl-stats-cumulative',
      // Clustering
      'etl-stats-kmeans', 'etl-stats-bisecting-kmeans', 'etl-stats-gmm',
      // Classification
      'etl-stats-logistic-regression', 'etl-stats-random-forest-classifier', 'etl-stats-gbt-classifier',
      'etl-stats-decision-tree', 'etl-stats-naive-bayes',
      // Regression
      'etl-stats-linear-regression', 'etl-stats-random-forest-regressor', 'etl-stats-gbt-regressor',
      // Association Rules
      'etl-stats-fp-growth',
      // Dimensionality Reduction
      'etl-stats-pca', 'etl-stats-chi-sq-selector',
      // Model Evaluation
      'etl-stats-evaluator', 'etl-stats-train-test-split',
      // Topic Modeling
      'etl-stats-lda',
    ],
  },
  ml_prep: {
    label: 'ML Prep',
    icon: 'Brain',
    color: CATEGORY_COLORS.ml_prep,
    nodes: [
      'etl-ml-string-indexer', 'etl-ml-one-hot', 'etl-ml-vector-assembler', 'etl-ml-scaler',
      'etl-ml-bucketizer', 'etl-ml-tokenizer', 'etl-ml-imputer', 'etl-ml-pca',
    ],
  },
  targets: {
    label: 'Targets',
    icon: 'HardDriveDownload',
    color: CATEGORY_COLORS.targets,
    nodes: [
      'etl-target-database', 'etl-target-s3', 'etl-target-azure-blob', 'etl-target-gcs', 'etl-target-hdfs', 'etl-target-file',
      'etl-target-snowflake', 'etl-target-redshift', 'etl-target-bigquery',
      'etl-target-mongodb', 'etl-target-cassandra', 'etl-target-dynamodb', 'etl-target-elasticsearch', 'etl-target-redis',
      'etl-target-delta', 'etl-target-iceberg',
      'etl-target-kafka', 'etl-target-kinesis',
      'etl-target-neo4j', 'etl-target-sqlserver', 'etl-target-starrocks',
      'etl-target-sql-executor',
    ],
  },
  quality: {
    label: 'Quality',
    icon: 'ShieldCheck',
    color: CATEGORY_COLORS.quality,
    nodes: ['etl-quality-check', 'etl-quality-schema', 'etl-quality-profiler'],
  },
  utility: {
    label: 'Utility',
    icon: 'Wrench',
    color: CATEGORY_COLORS.utility,
    nodes: ['etl-util-custom-pyspark', 'etl-util-notification', 'etl-util-checkpoint'],
  },
}

// =============================================================================
// TEMPLATE GENERATORS
// =============================================================================

/**
 * Check if a node type is a template
 * @param {string} nodeType - The node type to check
 * @returns {boolean} True if the node type is a template
 */
export function isTemplateNode(nodeType) {
  const node = ETL_NODE_REGISTRY[nodeType]
  return node && node.isTemplate === true
}

/**
 * Generate nodes and edges for a template
 * @param {string} templateType - The template type (e.g., 'etl-template-db-to-db')
 * @param {number} startX - Starting X position on canvas
 * @param {number} startY - Starting Y position on canvas
 * @returns {{ nodes: Array, edges: Array }} The generated nodes and edges
 */
export function generateTemplateNodes(templateType, startX = 100, startY = 100) {
  const template = ETL_NODE_REGISTRY[templateType]
  if (!template || !template.isTemplate || !template.templateNodes) {
    return { nodes: [], edges: [] }
  }

  const timestamp = Date.now()
  const nodeSpacingX = 280  // Horizontal spacing between nodes
  const nodes = []
  const edges = []

  // Generate nodes with positions (horizontal layout left to right)
  template.templateNodes.forEach((nodeType, index) => {
    const nodeDef = ETL_NODE_REGISTRY[nodeType]
    if (!nodeDef) return

    const nodeId = `${nodeType}-${timestamp}-${index}`

    nodes.push({
      id: nodeId,
      type: 'etlNode',
      position: {
        x: startX + (index * nodeSpacingX),
        y: startY,
      },
      data: {
        nodeType: nodeType,
        label: nodeDef.label,
        config: { ...nodeDef.defaultConfig },
        enabled: true,
        status: null,
        error: null,
        executionResult: null,
      },
    })

    // Create edge to previous node (if not first)
    if (index > 0) {
      const prevNodeId = `${template.templateNodes[index - 1]}-${timestamp}-${index - 1}`
      edges.push({
        id: `e-${prevNodeId}-${nodeId}`,
        type: 'smoothstep',
        source: prevNodeId,
        target: nodeId,
        sourceHandle: null,
        targetHandle: null,
        style: {
          stroke: 'var(--border-color)',
          strokeWidth: 2,
        },
        animated: false,
      })
    }
  })

  return { nodes, edges }
}

export default ETL_NODE_REGISTRY
